import React, { useState, useMemo, useRef, useEffect, lazy, Suspense } from 'react';
import { Camera, Upload, X, ChevronRight, CheckCircle, ArrowLeft, Copy, Receipt, Loader2, AlertTriangle, ArrowRight, Circle, Users, RotateCcw, PlusCircle, Trash2, Calendar, Clock, Star, ThumbsUp, ThumbsDown, MessageSquare, Home, Coffee, Check, Share2, Edit3, Save, Activity, User as UserIcon, Heart, MessageCircle, Bookmark, HeartCrack, Search } from 'lucide-react';
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getFirestore, doc, setDoc, onSnapshot, updateDoc, arrayUnion, arrayRemove, enableNetwork, collection, query, where, getDocs, getDoc } from "firebase/firestore";
import { getAuth, signInWithCustomToken, signOut, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BillHistory } from './utils/billHistory';
import { UserProfile } from './utils/userProfile';
import { UserRegistry } from './utils/userRegistry';
import { FriendsManager } from './utils/friendsManager';
import { firebaseConfig, appId } from './config/firebase';
import { StatusBar, Style } from '@capacitor/status-bar';

// Configure iOS status bar
StatusBar.setStyle({ style: Style.Light }).catch(() => {});
StatusBar.setBackgroundColor({ color: '#fdfaef' }).catch(() => {});
StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});

// Lazy load HistoryScreen for better initial performance
const HistoryScreen = lazy(() => import('./components/HistoryScreen'));

// --- Firebase Configuration ---
console.log("Using appId:", appId);

const initialAuthToken = null;

// Initialize Firebase
console.log("Using Firebase config:", firebaseConfig);
console.log("Using appId:", appId);
const app = initializeApp(firebaseConfig);
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY;

if (typeof window !== 'undefined' && appCheckSiteKey) {
 try {
  initializeAppCheck(app, {
   provider: new ReCaptchaV3Provider(appCheckSiteKey),
   isTokenAutoRefreshEnabled: true,
  });
  console.log("Firebase App Check enabled");
 } catch (error) {
  console.error("Failed to initialize Firebase App Check:", error);
 }
}

// Connect to the correct Firestore database
const db = getFirestore(app);

const auth = getAuth(app);
const functions = getFunctions(app);

// Set up persistent authentication
setPersistence(auth, browserLocalPersistence).then(() => {
 console.log("Firebase Auth persistence set to local");
}).catch((error) => {
 console.error("Failed to set Firebase Auth persistence:", error);
});

// Test Firestore connectivity on app load
console.log("Testing Firestore connectivity...");
enableNetwork(db).then(() => {
 console.log("Firestore network enabled successfully");
}).catch(error => {
 console.error("Firestore network enable failed:", error);
});

// --- Loading Component for Lazy Loaded Screens ---
const LazyScreenLoader = () => (
 <div className="flex flex-col h-full bg-[#fdfaef] items-center justify-center">
 <div className="animate-spin mb-4">
  <Loader2 size={32} className="text-indigo-600" />
 </div>
 <p className="text-lg text-gray-600">Loading...</p>
 </div>
);

// --- Haptic Feedback Helper ---
const triggerHaptic = (style = 'light') => {
 // For iOS devices with haptic feedback
 if (window.navigator && window.navigator.vibrate) {
 const patterns = { 
  light: [10], 
  medium: [20], 
  heavy: [30], 
  success: [10, 10, 10] 
 };
 window.navigator.vibrate(patterns[style] || patterns.light);
 }
 
 // For debugging - add a console log
 console.log(`Haptic triggered: ${style}`);
};

// Phone number formatting helper
const formatPhoneInput = (value) => {
 // Remove all non-digits
 const digitsOnly = value.replace(/\D/g, '');
 
 // Don't format if empty
 if (!digitsOnly) return '';
 
 // Format based on length
 if (digitsOnly.length <= 3) {
 return digitsOnly;
 } else if (digitsOnly.length <= 6) {
 return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
 } else {
 return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
 }
};

// Toast notification system
const ToastContext = React.createContext();

const ToastProvider = ({ children }) => {
 const [toasts, setToasts] = useState([]);

 const showToast = (message, type = 'success', duration = 3000) => {
 const id = Date.now();
 setToasts(prev => [...prev, { id, message, type }]);
 setTimeout(() => {
  setToasts(prev => prev.filter(t => t.id !== id));
 }, duration);
 };

 return (
 <ToastContext.Provider value={showToast}>
  {children}
  <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] flex flex-col gap-2 pointer-events-none">
  {toasts.map(toast => (
   <div
   key={toast.id}
   className={`px-4 py-3 shadow-brutal border-2 border-black flex items-center gap-2 pointer-events-auto animate-slide-in-top ${
    toast.type === 'error' ? 'bg-red-600 text-white' :
    toast.type === 'warning' ? 'bg-amber-500 text-white' :
    'bg-green-600 text-white'
   }`}
   >
   {toast.type === 'error' ? <AlertTriangle size={18} /> : <Check size={18} />}
   <span className="font-medium text-sm">{toast.message}</span>
   </div>
  ))}
  </div>
 </ToastContext.Provider>
 );
};

// Bill flow progress indicator — neo-brutalist
const BillProgressStepper = ({ currentStep }) => {
 const steps = [
 { key: 'scan', label: 'Scan' },
 { key: 'verify', label: 'Verify' },
 { key: 'people', label: 'People' },
 { key: 'assign', label: 'Assign' },
 { key: 'split', label: 'Split!' },
 ];

 const currentIndex = steps.findIndex(s => s.key === currentStep);

 return (
 <div className="flex items-center justify-center gap-0 py-2 px-4 bg-[#fdfaef] border-b-2 border-black">
  {steps.map((step, i) => (
  <React.Fragment key={step.key}>
   <div className="flex flex-col items-center gap-0.5">
   <div className={`w-7 h-7 flex items-center justify-center text-xs font-bold border-2 border-black transition-all duration-100 ${
    i < currentIndex ? 'bg-black text-white' :
    i === currentIndex ? 'bg-[#FF6B35] text-white shadow-brutal-sm' :
    'bg-white text-gray-400'
   }`}>
    {i < currentIndex ? <Check size={14} strokeWidth={3} /> : i + 1}
   </div>
   <span className={`text-[9px] font-bold uppercase tracking-wide ${
    i === currentIndex ? 'text-[#FF6B35]' :
    i < currentIndex ? 'text-black' : 'text-gray-400'
   }`}>{step.label}</span>
   </div>
   {i < steps.length - 1 && (
   <div className={`w-5 h-0.5 mb-3 transition-all duration-100 ${
    i < currentIndex ? 'bg-black' : 'bg-gray-300'
   }`} />
   )}
  </React.Fragment>
  ))}
 </div>
 );
};

// Phone Authentication Modal Component
const PhoneAuthModal = ({
 isVisible,
 step,
 phoneNumber,
 setPhoneNumber,
 verificationCode,
 setVerificationCode,
 onSendCode,
 onVerifyCode,
 onCancel,
 errorMessage,
 onResendCode,
 isSendingCode,
 isVerifyingCode,
 onTryWhatsApp,
 deliveryChannel
}) => {
 if (!isVisible) return null;

 const channelLabel = deliveryChannel === 'whatsapp' ? 'via WhatsApp' : 'via SMS';

 return (
 <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
  <div className="bg-white p-6 shadow-brutal-lg text-center max-w-sm mx-auto border-2 border-black">
  {step === 'phone' ? (
   <>
   <h3 className="text-2xl font-medium text-black mt-4 font-clash">Sign in with phone</h3>
   <p className="text-sm text-gray-600 mt-2">We'll text you a verification code</p>

   {errorMessage && (
    <div className="bg-red-100 border-2 border-red-600 text-red-700 px-3 py-2 mt-3 text-sm">
    {errorMessage}
    </div>
   )}

   <div className="mt-4">
    <input
    type="tel"
    value={formatPhoneInput(phoneNumber)}
    onChange={(e) => {
     let value = e.target.value.replace(/\D/g, '');
     if (value.length <= 10) {
     setPhoneNumber(value);
     }
    }}
    onKeyDown={(e) => { if (e.key === 'Enter' && phoneNumber.length === 10 && !isSendingCode) onSendCode(); }}
    placeholder="(555) 123-4567"
    className="w-full p-3 text-lg bg-gray-100 border-2 border-black outline-none text-center"
    autoFocus
    maxLength={18}
    />
    <p className="text-xs text-gray-500 mt-1 text-center">
    US phone number
    </p>
   </div>

   <div className="mt-4 flex gap-3">
    <button
    onClick={onCancel}
    className="flex-1 btn-pill btn-pill-secondary btn-pill-lg font-bold"
    >
    Cancel
    </button>
    <button
    onClick={onSendCode}
    disabled={phoneNumber.length !== 10 || isSendingCode}
    className="flex-1 btn-pill btn-pill-primary btn-pill-lg font-bold disabled:bg-gray-400 disabled:opacity-50"
    >
    {isSendingCode ? (
     <span className="flex items-center justify-center gap-2">
     <Loader2 size={18} className="animate-spin" /> Sending...
     </span>
    ) : 'Send code'}
    </button>
   </div>

   {/* Fallback channel options when SMS fails */}
   {errorMessage && (
    <div className="mt-3 space-y-2">
    {onTryWhatsApp && (
     <button
     onClick={onTryWhatsApp}
     disabled={isSendingCode}
     className="w-full text-sm bg-green-100 text-green-800 py-2 px-3 hover:bg-green-200 border-2 border-black disabled:opacity-50"
     >
     Try via WhatsApp instead
     </button>
    )}
    </div>
   )}
   </>
  ) : (
   <>
   <h3 className="text-2xl font-medium text-black mt-4 font-clash">Enter verification code</h3>
   <p className="text-sm text-gray-600 mt-2">
    Code sent {channelLabel} to +1 {formatPhoneInput(phoneNumber)}
   </p>

   {errorMessage && (
    <div className="bg-red-100 border-2 border-red-600 text-red-700 px-3 py-2 mt-3 text-sm">
    {errorMessage}
    </div>
   )}

   <div className="mt-4">
    <input
    type="text"
    inputMode="numeric"
    pattern="[0-9]*"
    value={verificationCode}
    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
    onKeyDown={(e) => { if (e.key === 'Enter' && verificationCode.length === 6 && !isVerifyingCode) onVerifyCode(); }}
    placeholder="123456"
    className="w-full p-3 text-lg bg-gray-100 border-2 border-black outline-none text-center tracking-widest"
    maxLength="6"
    autoFocus
    />
   </div>

   <div className="mt-4 flex gap-3">
    <button
    onClick={onCancel}
    className="flex-1 btn-pill btn-pill-secondary btn-pill-lg font-bold"
    >
    Cancel
    </button>
    <button
    onClick={onVerifyCode}
    disabled={verificationCode.length !== 6 || isVerifyingCode}
    className="flex-1 btn-pill btn-pill-success btn-pill-lg font-bold disabled:bg-gray-400 disabled:opacity-50"
    >
    {isVerifyingCode ? (
     <span className="flex items-center justify-center gap-2">
     <Loader2 size={18} className="animate-spin" /> Verifying...
     </span>
    ) : 'Verify'}
    </button>
   </div>

   {/* Resend / fallback options */}
   <div className="mt-3 space-y-2">
    {errorMessage && errorMessage.includes("didn't work") && onResendCode && (
    <button
     onClick={onResendCode}
     className="w-full text-sm text-blue-600 hover:text-blue-800 underline"
    >
     Yes, send another code
    </button>
    )}
    {errorMessage && errorMessage.includes("expired") && onResendCode && (
    <button
     onClick={onResendCode}
     className="w-full text-sm text-blue-600 hover:text-blue-800 underline"
    >
     Request a new code
    </button>
    )}
    {!errorMessage && (
    <button
     onClick={onResendCode}
     className="w-full text-sm text-gray-500 hover:text-gray-700"
    >
     Didn't receive the code? Try again
    </button>
    )}
   </div>
   </>
  )}
  </div>
 </div>
 );
};

// Helper function to format currency
const formatCurrency = (amount) => {
 const options = { style: 'currency', currency: 'USD' };
 return new Intl.NumberFormat('en-US', options).format(amount < 0 ? -amount : amount);
};

// Helper function to generate image summary
const generateImageSummary = (results) => {
 // Create a canvas element
 const canvas = document.createElement('canvas');
 const ctx = canvas.getContext('2d');
 
 // Set canvas size - wider for better text layout
 canvas.width = 1000;
 canvas.height = 1400;
 
 // Background
 ctx.fillStyle = '#fdfaef';
 ctx.fillRect(0, 0, canvas.width, canvas.height);
 
 // Set font - ensure fallback fonts
 ctx.font = 'bold 32px "JetBrains Mono", "Courier New", monospace';
 ctx.textAlign = 'center';
 ctx.fillStyle = '#000';
 
 let y = 60;
 
 // Header
 ctx.fillText('SPLIT SUMMARY', canvas.width / 2, y);
 y += 50;
 
 // Total
 ctx.font = 'bold 48px "JetBrains Mono", "Courier New", monospace';
 ctx.fillText(`TOTAL: ${formatCurrency(results.grandTotal)}`, canvas.width / 2, y);
 y += 80;
 
 // Line separator
 ctx.beginPath();
 ctx.moveTo(50, y);
 ctx.lineTo(canvas.width - 50, y);
 ctx.strokeStyle = '#000';
 ctx.lineWidth = 2;
 ctx.stroke();
 y += 40;
 
 // Transactions section
 ctx.font = 'bold 28px "JetBrains Mono", "Courier New", monospace';
 ctx.fillText('WHO OWES WHO', canvas.width / 2, y);
 y += 40;
 
 ctx.font = '24px "JetBrains Mono", "Courier New", monospace';
 ctx.textAlign = 'left';
 
 if (results.transactions.length > 0) {
 results.transactions.forEach(transaction => {
  const text = `${transaction.from.toUpperCase()} → ${transaction.to.toUpperCase()}`;
  const amount = formatCurrency(transaction.amount);
  
  ctx.fillText(text, 50, y);
  ctx.textAlign = 'right';
  ctx.fillText(amount, canvas.width - 50, y);
  ctx.textAlign = 'left';
  y += 35;
 });
 } else {
 ctx.textAlign = 'center';
 ctx.fillText('EVERYONE IS SETTLED UP!', canvas.width / 2, y);
 ctx.textAlign = 'left';
 y += 35;
 }
 
 y += 20;
 
 // Line separator
 ctx.beginPath();
 ctx.moveTo(50, y);
 ctx.lineTo(canvas.width - 50, y);
 ctx.stroke();
 y += 40;
 
 // Individual breakdown
 ctx.font = 'bold 28px "JetBrains Mono", "Courier New", monospace';
 ctx.textAlign = 'center';
 ctx.fillText('INDIVIDUAL BREAKDOWN', canvas.width / 2, y);
 y += 40;
 
 ctx.font = '22px "JetBrains Mono", "Courier New", monospace';
 ctx.textAlign = 'left';
 
 // Helper function to wrap text
 const wrapText = (text, maxWidth) => {
 const words = text.split(' ');
 const lines = [];
 let currentLine = words[0];
 
 for (let i = 1; i < words.length; i++) {
  const word = words[i];
  const width = ctx.measureText(currentLine + ' ' + word).width;
  if (width < maxWidth) {
  currentLine += ' ' + word;
  } else {
  lines.push(currentLine);
  currentLine = word;
  }
 }
 lines.push(currentLine);
 return lines;
 };
 
 results.breakdown.forEach(person => {
 // Person name
 ctx.font = 'bold 24px "JetBrains Mono", "Courier New", monospace';
 ctx.fillText(person.name.toUpperCase(), 50, y);
 y += 35;
 
 ctx.font = '20px "JetBrains Mono", "Courier New", monospace';
 
 // Items with text wrapping
 person.items.forEach(item => {
  const itemName = item.name.toUpperCase();
  const maxTextWidth = canvas.width - 200; // Leave space for price
  const lines = wrapText(itemName, maxTextWidth);
  
  lines.forEach((line, index) => {
  ctx.fillText(` ${line}`, 70, y);
  if (index === lines.length - 1) {
   // Show price on the last line
   ctx.textAlign = 'right';
   ctx.fillText(formatCurrency(item.price), canvas.width - 50, y);
   ctx.textAlign = 'left';
  }
  y += 25;
  });
 });
 
 // Tax & tip
 ctx.fillText(' TAX & TIP SHARE', 70, y);
 ctx.textAlign = 'right';
 ctx.fillText(formatCurrency(person.taxAndTipShare), canvas.width - 50, y);
 ctx.textAlign = 'left';
 y += 25;
 
 // Total share
 ctx.font = 'bold 22px "JetBrains Mono", "Courier New", monospace';
 ctx.fillText(' TOTAL SHARE', 70, y);
 ctx.textAlign = 'right';
 ctx.fillText(formatCurrency(person.finalShare), canvas.width - 50, y);
 ctx.textAlign = 'left';
 y += 25;
 
 // Amount paid
 ctx.font = '20px "JetBrains Mono", "Courier New", monospace';
 ctx.fillText(' AMOUNT PAID', 70, y);
 ctx.textAlign = 'right';
 ctx.fillText(formatCurrency(person.paid), canvas.width - 50, y);
 ctx.textAlign = 'left';
 y += 25;
 
 // Balance
 ctx.font = 'bold 22px "JetBrains Mono", "Courier New", monospace';
 ctx.fillStyle = person.balance >= 0 ? '#16a34a' : '#dc2626';
 ctx.fillText(' BALANCE', 70, y);
 ctx.textAlign = 'right';
 ctx.fillText(formatCurrency(person.balance), canvas.width - 50, y);
 ctx.textAlign = 'left';
 ctx.fillStyle = '#000';
 y += 45;
 });
 
 // Convert to blob and handle clipboard/download
 canvas.toBlob(async (blob) => {
 // Always try clipboard first (works on newer mobile browsers)
 if (navigator.clipboard && window.ClipboardItem) {
  try {
  const item = new ClipboardItem({ 'image/png': blob });
  await navigator.clipboard.write([item]);
  showImageSuccessMessage('📸 IMAGE COPIED TO CLIPBOARD!', 'Ready to paste in Messages, Instagram, etc.');
  return;
  } catch (err) {
  console.log('Clipboard failed, trying alternative...', err);
  }
 }
 
 // Fallback: try to trigger native share on mobile
 if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
  try {
  // Convert blob to file for sharing
  const file = new File([blob], 'split-summary.png', { type: 'image/png' });
  await navigator.share({
   title: 'Split Summary',
   text: 'Check out our bill split!',
   files: [file]
  });
  showImageSuccessMessage('📤 SHARED!', 'Image shared successfully');
  return;
  } catch (err) {
  console.log('Share failed, falling back to download...', err);
  }
 }
 
 // Final fallback: download
 downloadImage(canvas);
 const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
 if (isMobile) {
  showImageSuccessMessage('📸 IMAGE SAVED!', 'Check your Photos app or Downloads');
 } else {
  showImageSuccessMessage('📸 IMAGE DOWNLOADED!', 'Check your Downloads folder');
 }
 }, 'image/png');
};

// Helper function to download image
const downloadImage = (canvas) => {
 const link = document.createElement('a');
 link.download = 'split-summary.png';
 link.href = canvas.toDataURL();
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
};

// Helper function to show success message
const showImageSuccessMessage = (text, subtitle = '') => {
 const message = document.createElement('div');
 message.innerHTML = `
 <div style="font-size: 18px; margin-bottom: 4px;">${text}</div>
 ${subtitle ? `<div style="font-size: 14px; opacity: 0.9;">${subtitle}</div>` : ''}
 `;
 message.style.cssText = `
 position: fixed;
 top: 50%;
 left: 50%;
 transform: translate(-50%, -50%) scale(0);
 background: #7c3aed;
 color: white;
 padding: 16px 24px;
 border-radius: 0;
 font-weight: bold;
 font-family: 'JetBrains Mono', monospace;
 z-index: 10000;
 box-shadow: 4px 4px 0px #000;
 transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
 pointer-events: none;
 text-align: center;
 max-width: 280px;
 line-height: 1.3;
 `;
 
 document.body.appendChild(message);
 
 setTimeout(() => {
 message.style.transform = 'translate(-50%, -50%) scale(1)';
 }, 50);
 
 setTimeout(() => {
 message.style.transform = 'translate(-50%, -50%) scale(0)';
 setTimeout(() => {
  if (message.parentNode) {
  message.remove();
  }
 }, 300);
 }, 3000); // Longer display time for mobile users
};

const compressImage = (base64ImageData, maxWidth = 800, quality = 0.7) => {
 return new Promise((resolve) => {
 const canvas = document.createElement('canvas');
 const ctx = canvas.getContext('2d');
 const image = new Image();

 image.onload = () => {
  // Calculate new dimensions
  const ratio = Math.min(maxWidth / image.width, maxWidth / image.height);
  canvas.width = image.width * ratio;
  canvas.height = image.height * ratio;

  // Draw and compress
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const compressedBase64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
  resolve(compressedBase64);
 };
 
 // FIX: Check if base64ImageData already has the prefix
 if (base64ImageData.includes('data:')) {
  image.src = base64ImageData;
 } else {
  image.src = `data:image/png;base64,${base64ImageData}`;
 }
 });
};

// --- Recent Bills Helpers ---
const RECENT_BILLS_KEY = 'split_recent_bills';
const CURRENT_BILL_KEY = 'split_current_bill';
const MAX_RECENT_BILLS = 3;

const getRecentBills = () => {
 try {
  const bills = localStorage.getItem(RECENT_BILLS_KEY);
  return bills ? JSON.parse(bills) : [];
 } catch (error) {
  console.error("Could not get recent bills:", error);
  return [];
 }
};

const addRecentBill = (billId) => {
 try {
  let recent = getRecentBills();
  recent = recent.filter(id => id !== billId);
  recent.unshift(billId);
  const updatedRecent = recent.slice(0, MAX_RECENT_BILLS);
  localStorage.setItem(RECENT_BILLS_KEY, JSON.stringify(updatedRecent));
  return updatedRecent;
 } catch (error) {
  console.error("Could not add recent bill:", error);
  return getRecentBills();
 }
};

const saveCurrentBill = (billId) => {
 try {
  if (billId) {
   localStorage.setItem(CURRENT_BILL_KEY, billId);
   console.log('💾 Saved current bill to localStorage:', billId);
  } else {
   localStorage.removeItem(CURRENT_BILL_KEY);
   console.log('🗑️ Cleared current bill from localStorage');
  }
 } catch (error) {
  console.error("Could not save current bill:", error);
 }
};

const getCurrentBill = () => {
 try {
  const billId = localStorage.getItem(CURRENT_BILL_KEY);
  if (billId) {
   console.log('📂 Found saved bill in localStorage:', billId);
  }
  return billId;
 } catch (error) {
  console.error("Could not get current bill:", error);
  return null;
 }
};

// --- Simple Mobile Selection Handler ---
const MobileSelectionHandler = {
 selectedItemId: null,
 
 clearSelection() {
 this.selectedItemId = null;
 // Clear all visual selection states
 document.querySelectorAll('[data-item-id]').forEach(el => {
  el.classList.remove('mobile-selected', 'mobile-merge-ready');
  el.style.background = '';
  el.style.borderColor = '';
  el.style.borderWidth = '';
  el.style.boxShadow = '';
  el.style.transform = '';
 });
 },
 
 selectItem(itemId) {
 this.clearSelection();
 this.selectedItemId = itemId;
 
 // Highlight selected item
 const element = document.querySelector(`[data-item-id="${itemId}"]`);
 if (element) {
  element.classList.add('mobile-selected');
  element.style.background = 'linear-gradient(45deg, #dbeafe, #bfdbfe)';
  element.style.borderColor = '#3b82f6';
  element.style.borderWidth = '3px';
  element.style.borderStyle = 'solid';
  element.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.3)';
  element.style.transform = 'scale(1.02)';
  element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
  
  triggerHaptic('medium');
 }
 
 // Highlight potential merge targets
 document.querySelectorAll('[data-item-id]').forEach(el => {
  const targetId = el.dataset.itemId;
  if (targetId !== itemId.toString()) {
  el.classList.add('mobile-merge-ready');
  el.style.background = 'rgba(34, 197, 94, 0.1)';
  el.style.borderColor = '#22c55e';
  el.style.borderWidth = '2px';
  el.style.borderStyle = 'dashed';
  el.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
  }
 });
 
 // Show instruction toast
 this.showInstructionToast();
 },
 
 attemptMerge(targetItemId, onMergeItems) {
 if (this.selectedItemId && this.selectedItemId !== targetItemId) {
  // Success - trigger merge
  triggerHaptic('success');
  
  // Visual feedback for successful merge
  const targetElement = document.querySelector(`[data-item-id="${targetItemId}"]`);
  if (targetElement) {
  targetElement.style.background = 'linear-gradient(45deg, #bbf7d0, #86efac)';
  targetElement.style.transform = 'scale(1.1)';
  targetElement.style.boxShadow = '0 0 30px rgba(34, 197, 94, 0.6)';
  }
  
  // Show success message
  this.showSuccessMessage();
  
  // Execute merge after brief animation
  // Pass both the selected item and target item IDs
  setTimeout(() => {
  onMergeItems(this.selectedItemId, targetItemId);
  this.clearSelection();
  }, 300);
  
  return true;
 }
 return false;
 },
 
 showInstructionToast() {
 // Remove any existing toast
 const existingToast = document.querySelector('.instruction-toast');
 if (existingToast) {
  existingToast.remove();
 }
 
 const toast = document.createElement('div');
 toast.className = 'instruction-toast';
 toast.innerHTML = '👆 DOUBLE TAP ANOTHER ITEM TO MERGE';
 toast.style.cssText = `
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
  background: #3b82f6;
  color: white;
  padding: 12px 20px;
  border-radius: 0;
  font-weight: bold;
  font-size: 16px;
  font-family: 'JetBrains Mono', monospace;
  z-index: 10000;
  box-shadow: 4px 4px 0px #000;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  text-align: center;
  pointer-events: none;
 `;
 
 document.body.appendChild(toast);
 
 // Animate in
 setTimeout(() => {
  toast.style.transform = 'translateX(-50%) translateY(0)';
 }, 100);
 
 // Auto remove after 3 seconds
 setTimeout(() => {
  if (toast.parentNode) {
  toast.style.transform = 'translateX(-50%) translateY(-100%)';
  setTimeout(() => toast.remove(), 300);
  }
 }, 3000);
 },
 
 showSuccessMessage() {
 const message = document.createElement('div');
 message.innerHTML = '✨ MERGED!';
 message.style.cssText = `
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  background: #22c55e;
  color: white;
  padding: 16px 24px;
  border-radius: 0;
  font-weight: bold;
  font-size: 18px;
  font-family: 'JetBrains Mono', monospace;
  z-index: 10000;
  box-shadow: 4px 4px 0px #000;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  pointer-events: none;
 `;
 
 document.body.appendChild(message);
 
 setTimeout(() => {
  message.style.transform = 'translate(-50%, -50%) scale(1)';
 }, 50);
 
 setTimeout(() => {
  message.style.transform = 'translate(-50%, -50%) scale(0)';
  setTimeout(() => message.remove(), 300);
 }, 1500);
 }
};

// Main App Component
const TaxInput = ({ tax, onTaxChange }) => {
 const [displayValue, setDisplayValue] = useState(tax.toFixed(2));
 const [isFocused, setIsFocused] = useState(false);

 // Update displayValue when tax prop changes
 useEffect(() => {
 if (!isFocused) {
  setDisplayValue(tax.toFixed(2));
 }
 }, [tax, isFocused]);

 const handleFocus = (e) => {
 setIsFocused(true);
 e.target.select();
 };

 const handleBlur = () => {
 setIsFocused(false);
 const numValue = parseFloat(displayValue) || 0;
 setDisplayValue(numValue.toFixed(2));
 onTaxChange(numValue);
 };

 const handleChange = (e) => {
 const value = e.target.value;
 setDisplayValue(value);
 
 const numValue = parseFloat(value);
 if (!isNaN(numValue)) {
  onTaxChange(numValue);
 }
 };

 return (
 <input
  type="text"
  inputMode="decimal"
  value={displayValue}
  onChange={handleChange}
  onFocus={handleFocus}
  onBlur={handleBlur}
  className="w-24 p-2 rounded-md bg-white border-2 border-black focus:ring-2 focus:ring-indigo-500 outline-none text-right"
  placeholder="0.00"
 />
 );
};
const TipInput = ({ tip, onTipChange }) => {
 const [displayValue, setDisplayValue] = useState(tip.toFixed(2));
 const [isFocused, setIsFocused] = useState(false);

 // Update displayValue when tip prop changes (like from percentage buttons)
 useEffect(() => {
 if (!isFocused) {
  setDisplayValue(tip.toFixed(2));
 }
 }, [tip, isFocused]);

 const handleFocus = (e) => {
 setIsFocused(true);
 e.target.select();
 };

 const handleBlur = () => {
 setIsFocused(false);
 const numValue = parseFloat(displayValue) || 0;
 setDisplayValue(numValue.toFixed(2));
 onTipChange(numValue);
 };

 const handleChange = (e) => {
 const value = e.target.value;
 setDisplayValue(value);
 
 const numValue = parseFloat(value);
 if (!isNaN(numValue)) {
  onTipChange(numValue);
 }
 };

 return (
 <input
  type="text"
  inputMode="decimal"
  value={displayValue}
  onChange={handleChange}
  onFocus={handleFocus}
  onBlur={handleBlur}
  className="w-24 p-2 rounded-md bg-white border-2 border-black focus:ring-2 focus:ring-indigo-500 outline-none text-right"
  placeholder="0.00"
 />
 );
};


// Mobile-friendly prompt modals
const NamePromptModal = ({ isVisible, onConfirm, onCancel, initialValue = '', initialVenmo = '' }) => {
 const [name, setName] = useState(initialValue);
 const [venmo, setVenmo] = useState(initialVenmo);

 if (!isVisible) return null;

 const handleConfirm = () => {
 if (name.trim()) {
  onConfirm(name.trim(), venmo.replace('@', '').trim());
 }
 };

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
  <div className="bg-white border-2 border-black p-6 w-full max-w-sm shadow-brutal-lg">
  <h3 className="text-xl font-bold mb-4 text-center">ENTER YOUR NAME</h3>
  <input
   type="text"
   value={name}
   onChange={(e) => setName(e.target.value)}
   placeholder="Your name"
   className="w-full p-3 border-2 border-black mb-3 text-lg"
   autoFocus
   onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
  />
  <div className="flex items-center w-full p-3 border-2 border-gray-300 mb-4 text-base bg-gray-50">
   <span className="text-gray-400 mr-1">@</span>
   <input
   type="text"
   value={venmo}
   onChange={(e) => setVenmo(e.target.value)}
   placeholder="venmo username (optional)"
   className="flex-1 bg-transparent outline-none text-base"
   onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
   />
  </div>
  <p className="text-xs text-gray-400 text-center mb-4">Add Venmo to get paid back instantly</p>
  <div className="flex gap-3">
   <button
   onClick={onCancel}
   className="flex-1 py-3 px-4 btn-pill btn-pill-secondary text-lg font-bold"
   >
   CANCEL
   </button>
   <button
   onClick={handleConfirm}
   disabled={!name.trim()}
   className="btn-pill btn-pill-primary btn-pill-lg flex-1 disabled:opacity-50"
   >
   CONTINUE
   </button>
  </div>
  </div>
 </div>
 );
};

const VenuePromptModal = ({ isVisible, onConfirm, onCancel, initialValue = '' }) => {
 const [venue, setVenue] = useState(initialValue);

 useEffect(() => {
 if (isVisible) {
  setVenue(initialValue);
 }
 }, [isVisible, initialValue]);

 if (!isVisible) return null;

 const handleConfirm = () => {
 if (venue.trim()) {
  onConfirm(venue.trim());
 }
 };

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
  <div className="bg-white border-2 border-black p-6 w-full max-w-sm shadow-brutal-lg">
  <h3 className="text-xl font-bold mb-2 text-center">CONFIRM RESTAURANT</h3>
  <p className="text-gray-600 mb-4 text-center">We detected this name from your receipt</p>
  <input
   type="text"
   value={venue}
   onChange={(e) => setVenue(e.target.value)}
   placeholder="Restaurant name"
   className="w-full p-3 border-2 border-black mb-4 text-lg"
   autoFocus
   onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
  />
  <div className="flex gap-3">
   <button
   onClick={onCancel}
   className="flex-1 py-3 px-4 btn-pill btn-pill-secondary text-lg font-bold"
   >
   CANCEL
   </button>
   <button
   onClick={handleConfirm}
   disabled={!venue.trim()}
   className="flex-1 py-3 px-4 btn-pill btn-pill-primary text-lg font-bold disabled:opacity-50"
   >
   CREATE BILL
   </button>
  </div>
  </div>
 </div>
 );
};

const ProfileScreen = ({ currentUser, onBack, onSignOut, onHome, setScreen, userProfile, setUserProfile, onStartBill }) => {
 const [userHistory, setUserHistory] = useState([]);
 const [stats, setStats] = useState({ totalBills: 0, archivedBills: 0, totalSpent: 0, averageBill: 0, mostFrequentParticipants: [], recentActivity: [] });
 
 // Load history with cloud sync
 useEffect(() => {
 const loadHistoryData = async () => {
  if (currentUser?.id) {
  const history = await BillHistory.getHistoryWithSync(currentUser.id, db);
  setUserHistory(history);
  setStats(BillHistory.getStats()); // Stats can still use local method as it processes the returned data
  }
 };
 loadHistoryData();
 }, [currentUser?.id]);
 const [editingVenmo, setEditingVenmo] = useState(false);
 const [editingName, setEditingName] = useState(false);
 const [editingUsername, setEditingUsername] = useState(false);
 const [tempVenmo, setTempVenmo] = useState('');
 const [tempName, setTempName] = useState('');
 const [tempUsername, setTempUsername] = useState('');
 const [uploadingPhoto, setUploadingPhoto] = useState(false);
 const [usernameStatus, setUsernameStatus] = useState(''); // 'checking', 'available', 'taken', 'error'
 const fileInputRef = useRef(null);


 // Function to load profile data
 const loadProfile = async () => {
 console.log('loadProfile called, currentUser:', currentUser);
 const userId = currentUser?.uid || currentUser?.id;
 if (userId) {
  console.log('Loading profile for user ID:', userId);
  const profile = await UserProfile.getProfile(userId, db);
  console.log('Profile loaded with cloud sync:', profile);

  // Check if username is missing from profile
  if (!profile?.username && db) {
  console.log('⚠️ Username missing from profile, checking Firestore...');
  const existingUsername = await UserRegistry.getCurrentUserUsername(userId, db);

  if (existingUsername) {
   console.log('✅ Found existing username in Firestore:', existingUsername);
   // Save it back to the local profile
   profile.username = existingUsername;
   await UserProfile.saveProfile(userId, { username: existingUsername }, db);
  }
  }

  setUserProfile(profile);
  setTempVenmo(profile.venmoUsername || '');
  setTempName(profile.displayName || '');
  // Auto-suggest Venmo username if no username is set, but allow editing
  setTempUsername(profile.username || profile.venmoUsername || '');

 } else {
  console.log('No currentUser.uid or currentUser.id available');
 }
 };

 useEffect(() => {
 loadProfile();
 }, [currentUser]);

 const formatPhoneNumber = (phoneNumber) => {
 if (!phoneNumber) return 'Not provided';
 // Format as (XXX) XXX-XXXX
 const cleaned = phoneNumber.replace(/\D/g, '');
 const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
 if (match) {
  return `(${match[1]}) ${match[2]}-${match[3]}`;
 }
 return phoneNumber;
 };

 const getMemberSince = () => {
 if (currentUser?.metadata?.creationTime) {
  return new Date(currentUser.metadata.creationTime).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long'
  });
 }
 return 'Recently';
 };

 const handlePhotoUpload = async (event) => {
 console.log('handlePhotoUpload called, event:', event);
 const file = event.target.files[0];
 console.log('Selected file:', file);
 const userId = currentUser?.uid || currentUser?.id;
 console.log('User ID:', userId);
 if (!file || !userId) {
  console.log('No file or userId, returning');
  return;
 }

 setUploadingPhoto(true);
 try {
  console.log('Starting upload with UserProfile.uploadProfilePicture');
  const profilePictureData = await UserProfile.uploadProfilePicture(userId, file, db);
  console.log('Upload successful, profilePictureData:', profilePictureData);
  // Reload profile to ensure UI is in sync
  loadProfile();
 } catch (error) {
  console.error('Error uploading photo:', error.message);
  console.error('Full error:', error);
 } finally {
  setUploadingPhoto(false);
 }
 };

 const handleRemovePhoto = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 if (!userId) return;
 try {
  const success = await UserProfile.removeProfilePicture(userId, db);
  if (success) {
  // Reload profile to ensure UI is in sync
  loadProfile();
  }
 } catch (error) {
  console.error('Error removing photo:', error);
 }
 };

 const saveVenmoUsername = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 if (!userId) {
  console.error('No user ID available for saving');
  setEditingVenmo(false);
  return;
 }

 const cleanUsername = tempVenmo.replace('@', '').trim();
 const success = await UserProfile.saveProfile(userId, { venmoUsername: cleanUsername }, db);
 
 if (success) {
  // Reload profile to ensure UI is in sync
  loadProfile();
 } else {
  console.error('Failed to save Venmo username');
  // Reset to previous value
  setTempVenmo(userProfile?.venmoUsername || '');
 }
 setEditingVenmo(false);
 };

 const saveDisplayName = async () => {
 console.log('saveDisplayName called!');
 console.log('Current user:', currentUser);
 console.log('Temp name:', tempName);
 
 const userId = currentUser?.uid || currentUser?.id;
 if (!userId) {
  console.error('No user ID available for saving');
  setEditingName(false);
  return;
 }

 const cleanName = tempName.trim();
 console.log('Saving display name:', cleanName);
 const success = await UserProfile.saveProfile(userId, { displayName: cleanName }, db);
 console.log('Save success:', success);
 
 if (success) {
  // Reload profile to ensure UI is in sync
  loadProfile();
 } else {
  console.error('Failed to save display name');
  // Reset to previous value
  setTempName(userProfile?.displayName || '');
 }
 setEditingName(false);
 };

 const saveUsername = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 if (!userId) {
  console.error('No user ID available for saving');
  setEditingUsername(false);
  return;
 }

 const cleanUsername = tempUsername.trim().toLowerCase();
 if (!cleanUsername) {
  setEditingUsername(false);
  return;
 }

 try {
  setUsernameStatus('checking');
  
  // Check if username is available (or if user can reclaim it)
  console.log('Checking username availability for:', cleanUsername);
  const isAvailable = await UserRegistry.checkUsernameAvailable(cleanUsername, db, userId, currentUser?.phoneNumber);
  console.log('Username available:', isAvailable);
  
  if (!isAvailable) {
  setUsernameStatus('taken');
  return;
  }

  // Reserve the username in Firestore
  console.log('Reserving username in Firestore...');
  await UserRegistry.reserveUsername(cleanUsername, userId, userProfile, db);
  console.log('Username reserved successfully');
  
  // Save to local profile
  console.log('Saving to local profile...');
  const success = await UserProfile.saveProfile(userId, { username: cleanUsername }, db);
  console.log('Local save success:', success);
  
  if (success) {
  setUsernameStatus('available');
  loadProfile();
  setEditingUsername(false);
  } else {
  setUsernameStatus('error');
  }
 } catch (error) {
  console.error('Error saving username:', error);
  console.error('Error details:', error.message);
  setUsernameStatus('error');
  // Show user-friendly error
  alert(`Error: ${error.message}`);
 }
 };

 const checkUsernameAvailability = async (username) => {
 if (!username || username.length < 3) {
  setUsernameStatus('');
  return;
 }

 try {
  setUsernameStatus('checking');
  const userId = currentUser?.uid || currentUser?.id;
  const isAvailable = await UserRegistry.checkUsernameAvailable(username, db, userId, currentUser?.phoneNumber);
  setUsernameStatus(isAvailable ? 'available' : 'taken');
 } catch (error) {
  setUsernameStatus('error');
 }
 };

 return (
 <div className="flex flex-col min-h-screen bg-[#fdfaef]" style={{minHeight: '100vh'}}>
  {/* Header - matches History page style */}
  <div className="relative p-6 border-b-2 border-dashed border-gray-400 text-center bg-[#fdfaef]">
  <div className="absolute left-4 top-1/2 -translate-y-1/2">
   <button onClick={onBack} className="text-gray-600 hover:text-black p-2">
   <ArrowLeft size={24} />
   </button>
  </div>
  <h2 className="text-black font-bold text-xl px-16">ACCOUNT</h2>
  <p className="text-base text-gray-600 px-16">
   {userProfile?.username ? `@${userProfile.username}` : 'Manage your profile'}
  </p>
  </div>

  <div className="flex-1 overflow-y-auto pb-24" style={{WebkitOverflowScrolling: 'touch'}}>
  {/* Profile Avatar */}
  <div className="flex flex-col items-center pt-6 pb-5">
   <div className="relative mb-3">
   {userProfile?.profilePicture ? (
    <img
    src={userProfile.profilePicture}
    alt="Profile"
    className="w-24 h-24 rounded-full object-cover border-2 border-black"
    />
   ) : (
    <div className="w-24 h-24 bg-black rounded-full flex items-center justify-center text-white text-3xl font-bold border-2 border-black">
    {userProfile?.displayName ? userProfile.displayName.charAt(0).toUpperCase() :
     currentUser?.phoneNumber ? currentUser.phoneNumber.slice(-4) : 'U'}
    </div>
   )}
   <button
    onClick={() => {
    console.log('Camera button clicked, fileInputRef:', fileInputRef.current);
    fileInputRef.current?.click();
    }}
    disabled={uploadingPhoto}
    className="absolute -bottom-1 -right-1 bg-white hover:bg-gray-50 text-black rounded-full p-1.5 border-2 border-black"
   >
    {uploadingPhoto ? (
    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
    ) : (
    <Camera size={14} />
    )}
   </button>
   {userProfile?.profilePicture && (
    <button
    onClick={handleRemovePhoto}
    className="absolute -top-1 -left-1 bg-white hover:bg-gray-50 text-black rounded-full p-1 border-2 border-black"
    >
    <X size={12} />
    </button>
   )}
   <input
    ref={fileInputRef}
    type="file"
    accept="image/*"
    onChange={handlePhotoUpload}
    className="hidden"
   />
   </div>
   <h3 className="text-xl font-bold text-black">
   {userProfile?.displayName || 'Your Account'}
   </h3>
  </div>

  {/* Account Info Card */}
  <div className="mx-4 mb-4 border-2 border-black bg-white overflow-hidden shadow-brutal-sm">
   {/* Display Name */}
   {editingName ? (
   <div className="flex items-center justify-between px-4 py-3 border-b-2 border-dashed border-gray-300">
    <div className="flex flex-col flex-1 gap-1">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Display Name</span>
    <input
     type="text"
     value={tempName}
     onChange={(e) => setTempName(e.target.value)}
     placeholder="Enter display name"
     className="text-sm font-bold text-black bg-transparent border-none outline-none p-0"
     autoFocus
    />
    </div>
    <div className="flex items-center gap-1 ml-2">
    <button onClick={() => { saveDisplayName(); }} className="p-2 text-green-600 hover:bg-green-50">
     <Check size={16} />
    </button>
    <button onClick={() => { setEditingName(false); setTempName(userProfile?.displayName || ''); }} className="p-2 text-gray-400 hover:bg-gray-50">
     <X size={16} />
    </button>
    </div>
   </div>
   ) : (
   <div
    onClick={() => setEditingName(true)}
    className="flex items-center justify-between px-4 py-3 border-b-2 border-dashed border-gray-300 active:bg-gray-50 cursor-pointer"
    style={{ WebkitTapHighlightColor: 'transparent' }}
   >
    <div className="flex flex-col gap-0.5">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Display Name</span>
    <span className="text-sm font-bold text-black">{userProfile?.displayName || 'Not set'}</span>
    </div>
    <ChevronRight size={16} className="text-gray-400" />
   </div>
   )}

   {/* Username */}
   {editingUsername ? (
   <div className="px-4 py-3 border-b-2 border-dashed border-gray-300">
    <div className="flex items-start justify-between">
    <div className="flex flex-col flex-1 gap-1">
     <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Username</span>
     <div className="flex items-center">
     <span className="text-sm font-bold text-gray-400 mr-0.5">@</span>
     <input
      type="text"
      value={tempUsername}
      onChange={(e) => {
      const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
      setTempUsername(value);
      if (value.length >= 3) {
       checkUsernameAvailability(value);
      } else {
       setUsernameStatus('');
      }
      }}
      placeholder={userProfile?.venmoUsername ? `${userProfile.venmoUsername}` : "your_username"}
      className="text-sm font-bold text-black bg-transparent border-none outline-none p-0 flex-1"
      autoFocus
     />
     </div>
     <div className="mt-0.5">
     {usernameStatus === 'checking' && <p className="text-xs font-bold text-blue-500">Checking...</p>}
     {usernameStatus === 'available' && <p className="text-xs font-bold text-green-600">✓ Available</p>}
     {usernameStatus === 'taken' && <p className="text-xs font-bold text-red-500">✗ Taken</p>}
     {usernameStatus === 'error' && <p className="text-xs font-bold text-red-500">Error checking</p>}
     </div>
    </div>
    <div className="flex items-center gap-1 ml-2">
     <button
     onClick={saveUsername}
     disabled={usernameStatus !== 'available'}
     className="p-2 text-green-600 hover:bg-green-50 disabled:opacity-30"
     >
     <Check size={16} />
     </button>
     <button
     onClick={() => { setEditingUsername(false); setTempUsername(userProfile?.username || ''); setUsernameStatus(''); }}
     className="p-2 text-gray-400 hover:bg-gray-50"
     >
     <X size={16} />
     </button>
    </div>
    </div>
   </div>
   ) : (
   <div
    onClick={() => { setEditingUsername(true); setUsernameStatus(''); }}
    className="flex items-center justify-between px-4 py-3 border-b-2 border-dashed border-gray-300 active:bg-gray-50 cursor-pointer"
    style={{ WebkitTapHighlightColor: 'transparent' }}
   >
    <div className="flex flex-col gap-0.5">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Username</span>
    <span className="text-sm font-bold text-black">@{userProfile?.username || 'not set'}</span>
    </div>
    <ChevronRight size={16} className="text-gray-400" />
   </div>
   )}

   {/* Phone Number */}
   <div className="flex items-center justify-between px-4 py-3 border-b-2 border-dashed border-gray-300">
   <div className="flex flex-col gap-0.5">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Phone</span>
    <span className="text-sm font-bold text-black">{formatPhoneNumber(currentUser?.phoneNumber)}</span>
   </div>
   </div>

   {/* Venmo */}
   {editingVenmo ? (
   <div className="flex items-center justify-between px-4 py-3">
    <div className="flex flex-col flex-1 gap-1">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Venmo</span>
    <div className="flex items-center">
     <span className="text-sm font-bold text-gray-400 mr-0.5">@</span>
     <input
     type="text"
     value={tempVenmo}
     onChange={(e) => setTempVenmo(e.target.value)}
     placeholder="username"
     className="text-sm font-bold text-black bg-transparent border-none outline-none p-0 flex-1"
     autoFocus
     />
    </div>
    </div>
    <div className="flex items-center gap-1 ml-2">
    <button onClick={saveVenmoUsername} className="p-2 text-green-600 hover:bg-green-50">
     <Check size={16} />
    </button>
    <button onClick={() => {setEditingVenmo(false); setTempVenmo(userProfile?.venmoUsername || '');}} className="p-2 text-gray-400 hover:bg-gray-50">
     <X size={16} />
    </button>
    </div>
   </div>
   ) : (
   <div
    onClick={() => setEditingVenmo(true)}
    className="flex items-center justify-between px-4 py-3 active:bg-gray-50 cursor-pointer"
    style={{ WebkitTapHighlightColor: 'transparent' }}
   >
    <div className="flex flex-col gap-0.5">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Venmo</span>
    <span className="text-sm font-bold text-black">
     {userProfile?.venmoUsername ? `@${userProfile.venmoUsername}` : 'Not set'}
    </span>
    </div>
    <ChevronRight size={16} className="text-gray-400" />
   </div>
   )}
  </div>

  {/* Stats */}
  {stats && stats.totalBills > 0 && (
   <div className="mx-4 mb-4">
   <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">STATS</h3>
   <div className="grid grid-cols-2 gap-2">
    <div className="text-center p-3 bg-white border-2 border-black shadow-brutal-sm">
    <div className="text-xl font-bold text-black">{stats.totalBills}</div>
    <div className="text-xs font-bold text-gray-500 uppercase">Bills Split</div>
    </div>
    <div className="text-center p-3 bg-white border-2 border-black shadow-brutal-sm">
    <div className="text-xl font-bold text-black">
     {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(stats.totalSpent)}
    </div>
    <div className="text-xs font-bold text-gray-500 uppercase">Total Spent</div>
    </div>
    <div className="text-center p-3 bg-white border-2 border-black shadow-brutal-sm">
    <div className="text-xl font-bold text-black">
     {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(stats.averageBill)}
    </div>
    <div className="text-xs font-bold text-gray-500 uppercase">Avg Bill</div>
    </div>
    <div className="text-center p-3 bg-white border-2 border-black shadow-brutal-sm">
    <div className="text-xl font-bold text-black">{stats.archivedBills}</div>
    <div className="text-xs font-bold text-gray-500 uppercase">Archived</div>
    </div>
   </div>
   </div>
  )}

  {/* Recent Activity */}
  {userHistory.length > 0 && (
   <div className="mx-4 mb-4">
   <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">RECENT</h3>
   <div className="border-2 border-black bg-white overflow-hidden shadow-brutal-sm">
    {userHistory.slice(0, 3).map((bill, index) => (
    <button
     key={bill.id}
     onClick={() => onStartBill(bill)}
     className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-50 transition-colors text-left ${index < Math.min(userHistory.length, 3) - 1 ? 'border-b-2 border-dashed border-gray-300' : ''}`}
    >
     <Receipt size={16} className="text-gray-500 flex-shrink-0" />
     <div className="flex-grow min-w-0">
     <div className="text-sm font-bold text-black truncate">{bill.venueName}</div>
     <div className="text-xs font-bold text-gray-500">{bill.date} · {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(bill.total)}</div>
     </div>
     <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
    </button>
    ))}
    {userHistory.length > 3 && (
    <button
     onClick={() => setScreen('history')}
     className="w-full text-center text-sm font-bold text-black py-3 hover:bg-gray-50 active:bg-gray-50 border-t-2 border-dashed border-gray-300"
    >
     View all {userHistory.length} bills →
    </button>
    )}
   </div>
   </div>
  )}

  {/* Quick Actions */}
  <div className="mx-4 mb-4">
   <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">QUICK ACTIONS</h3>
   <div className="border-2 border-black bg-white overflow-hidden shadow-brutal-sm">
   <button
    onClick={() => setScreen('history')}
    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-50 border-b-2 border-dashed border-gray-300 text-left"
   >
    <Calendar size={18} className="text-black" />
    <span className="text-sm font-bold text-black flex-1">Bill History</span>
    <ChevronRight size={16} className="text-gray-400" />
   </button>
   <button
    onClick={() => setScreen('saved')}
    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-50 border-b-2 border-dashed border-gray-300 text-left"
   >
    <Bookmark size={18} className="text-black" />
    <span className="text-sm font-bold text-black flex-1">Saved Places</span>
    <ChevronRight size={16} className="text-gray-400" />
   </button>
   <button
    onClick={() => onHome()}
    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-50 text-left"
   >
    <Receipt size={18} className="text-black" />
    <span className="text-sm font-bold text-black flex-1">Create New Bill</span>
    <ChevronRight size={16} className="text-gray-400" />
   </button>
   </div>
  </div>

  {/* Sign Out */}
  <div className="mx-4 mb-8">
   <button
   onClick={onSignOut}
   className="w-full py-3 text-sm font-bold text-red-600 border-2 border-black bg-white hover:bg-red-50 active:bg-red-100 shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
   >
   SIGN OUT
   </button>
  </div>
  </div>
 </div>
 );
};


const SavedScreen = ({ currentUser, onBack, onHome, onStartBill, savedPosts, userActivityData, loadSavedPosts }) => {
 const [savedBills, setSavedBills] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const loadSavedBills = async () => {
  setLoading(true);
  console.log('🔍 SavedScreen - Loading saved bills:', {
  savedPostsSize: savedPosts.size,
  savedPostsArray: Array.from(savedPosts),
  userActivityDataLength: userActivityData.length,
  userActivityIds: userActivityData.map(activity => activity.id)
  });
  
  // Filter user activity to only show saved posts
  const savedBillsList = userActivityData.filter(activity => 
  savedPosts.has(activity.id)
  );
  
  console.log('🔍 SavedScreen - Filtered saved bills:', {
  savedBillsListLength: savedBillsList.length,
  savedBillsIds: savedBillsList.map(bill => bill.id)
  });
  
  setSavedBills(savedBillsList);
  setLoading(false);
 };

 loadSavedBills();
 }, [savedPosts, userActivityData]);

 if (loading) {
 return (
  <div className="flex flex-col min-h-screen bg-[#fdfaef]">
  <div className="flex items-center justify-center h-64">
   <Loader2 className="animate-spin text-[#FF6B35]" size={32} />
  </div>
  </div>
 );
 }

 return (
 <div className="flex flex-col min-h-screen bg-[#fdfaef] pb-20">
  {/* Header */}
  <div className="sticky top-0 z-10 bg-white -xl border-b-2 border-black/50 px-4 py-4">
  <div className="flex items-center gap-3">
   <button 
   onClick={onBack}
   className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
   >
   <ArrowLeft size={18} className="text-gray-600" />
   </button>
   <h1 className="text-xl font-semibold text-gray-900">Saved Places</h1>
   <div className="ml-auto">
   <span className="text-sm text-gray-500">{savedBills.length} saved</span>
   </div>
  </div>
  </div>

  <div className="flex-1 px-4 py-6">
  {savedBills.length === 0 ? (
   <div className="text-center py-12">
   <Bookmark size={48} className="mx-auto text-gray-300 mb-4" />
   <h3 className="text-lg font-medium text-gray-900 mb-2">No saved places yet</h3>
   <p className="text-gray-500 mb-6">Tap the bookmark icon on places in your activity feed to save them here.</p>
   <button
    onClick={onHome}
    className="bg-[#FF6B35] text-white px-6 py-2 font-medium hover:bg-[#FF5722] transition-colors"
   >
    Browse Activity
   </button>
   </div>
  ) : (
   <div className="space-y-4">
   {savedBills.map((activity, index) => (
    <div key={activity.id} className="bg-white shadow-brutal-sm border-2 border-black p-4">
    <div className="flex items-start gap-3">
     <img 
     src={activity.user?.avatar || '/default-avatar.png'} 
     alt={activity.user?.name || 'User'}
     className="w-10 h-10 rounded-full object-cover"
     />
     <div className="flex-1 min-w-0">
     <div className="flex items-center gap-2">
      <span className="font-medium text-gray-900 text-sm">
      {activity.user?.name || 'Unknown User'}
      </span>
      <span className="text-gray-500 text-sm">•</span>
      <span className="text-gray-500 text-sm">{activity.timestamp}</span>
     </div>
     <p className="text-gray-800 mt-1">
      Split a <span className="font-medium">${activity.total?.toFixed(2) || '0.00'}</span> bill at{' '}
      <span className="font-medium text-[#FF6B35]">{activity.venue}</span>
      {activity.participants && activity.participants.length > 1 && (
      <span className="text-gray-500"> with {activity.participants.length - 1} others</span>
      )}
     </p>
     </div>
     <button
     onClick={() => onStartBill(activity.billData || activity)}
     className="text-[#FF6B35] hover:text-[#FF5722] transition-colors p-1"
     >
     <ArrowRight size={16} />
     </button>
    </div>
    </div>
   ))}
   </div>
  )}
  </div>
 </div>
 );
};

const ActivityScreen = ({ currentUser, userProfile, onBack, onHome, onStartBill, onSignIn, setScreen, setIsStartBillModalVisible, setIsAddFriendModalVisible, friends, setFriends, db, setIsSearchModalVisible, joinBill }) => {
 const [expandedReviews, setExpandedReviews] = useState(new Set());
 const [filterRating, setFilterRating] = useState(null);
 const [filterType, setFilterType] = useState(null); // 'review', 'split', or null for all
 const [likedPosts, setLikedPosts] = useState(new Set());
 const [tftiPosts, setTftiPosts] = useState(new Set());
 const [commentingPosts, setCommentingPosts] = useState(new Set());
 const [savedPosts, setSavedPosts] = useState(new Set());
 const [comments, setComments] = useState({}); // Store comments by activity ID
 const [commentTexts, setCommentTexts] = useState({}); // Store draft comment texts by activity ID

 // Get real activity data from friends' activity + current user's activity from Firestore
 // Load own bills from history (fast), then async-merge friends' activities
 const getUserActivityData = async () => {
 if (!currentUser || !db) return [];
 const userId = currentUser?.uid || currentUser?.id;
 try {
  const history = await BillHistory.getHistoryWithSync(userId, db);
  return history.slice(0, 20).map(bill => ({
  id: `user-${bill.id}`,
  type: 'split',
  user: {
   name: userProfile?.displayName || userProfile?.username || 'You',
   avatar: userProfile?.profilePicture || '/default-avatar.png',
   username: userProfile?.username || ''
  },
  venue: bill.venueName || 'Unknown Restaurant',
  total: bill.total || 0,
  participants: bill.participants || ['You'],
  timestamp: bill.createdAt ? new Date(bill.createdAt).toLocaleDateString() : bill.timestamp ? new Date(bill.timestamp).toLocaleDateString() : new Date().toLocaleDateString(),
  rawTimestamp: bill.timestamp || (bill.createdAt ? new Date(bill.createdAt).getTime() : 0),
  rating: bill.experience?.venueRating || null,
  wouldReturn: bill.experience?.wouldReturn || null,
  isCurrentUser: true,
  billData: bill
  }));
 } catch (error) {
  console.error('Error loading activity data:', error);
  return [];
 }
 };

 // Load friends' activities from their bill history (direct Firestore read — guaranteed data)
 const loadFriendsActivities = async () => {
 if (!friends.length || !db) return;
 const friendActivities = [];
 for (const friend of friends) {
  const fId = friend.userId;
  if (!fId) continue;
  try {
  const historyRef = collection(db, 'users', fId, 'billHistory');
  const snapshot = await getDocs(historyRef);
  snapshot.forEach(d => {
   const bill = d.data();
   friendActivities.push({
   id: `friend-${d.id}`,
   type: 'split',
   user: {
    name: friend.displayName || friend.username || 'Friend',
    avatar: friend.profilePicture || null,
    initials: (friend.displayName || friend.username || '?')[0].toUpperCase(),
   },
   venue: bill.venueName || 'Unknown Restaurant',
   total: bill.total || 0,
   billTotal: bill.total || 0,
   participants: bill.participants || [],
   participantCount: bill.participantCount || 1,
   timestamp: bill.timestamp ? new Date(bill.timestamp).toLocaleDateString() : 'Recently',
   rawTimestamp: bill.timestamp || 0,
   date: bill.timestamp ? new Date(bill.timestamp).toLocaleDateString() : new Date().toLocaleDateString(),
   rating: bill.experience?.venueRating || null,
   review: bill.experience?.venueNotes || null,
   wouldReturn: bill.experience?.wouldReturn || null,
   tags: bill.experience?.tags || [],
   isCurrentUser: false
   });
  });
  } catch (e) {
  console.warn('Could not load bills for friend:', friend.displayName, e.message);
  }
 }
 if (friendActivities.length > 0) {
  setActivityData(prev => {
  const existingIds = new Set(prev.map(a => a.id));
  const newOnes = friendActivities.filter(a => !existingIds.has(a.id));
  const merged = [...prev, ...newOnes];
  merged.sort((a, b) => (b.rawTimestamp || 0) - (a.rawTimestamp || 0));
  return merged.slice(0, 30);
  });
 }
 };

 const [activityData, setActivityData] = useState([]);

 // Load own activity immediately, then friends async
 useEffect(() => {
 const load = async () => {
  const data = await getUserActivityData();
  setActivityData(data);

  // One-time backfill: write own bills to activities collection so friends can see them
  const userId = currentUser?.uid || currentUser?.id;
  if (userId && data.length > 0) {
  const backfillKey = `backfilled_${userId}`;
  if (!sessionStorage.getItem(backfillKey)) {
   sessionStorage.setItem(backfillKey, 'true');
   data.slice(0, 10).forEach(act => {
   const billId = act.billData?.id || act.id?.replace('user-', '');
   if (billId) {
    setDoc(doc(db, `artifacts/${appId}/public/data/activities`, billId), {
    userId,
    userName: userProfile?.displayName || userProfile?.username || 'Unknown',
    userAvatar: userProfile?.profilePicture || null,
    type: 'split',
    venue: act.venue || 'Unknown Restaurant',
    total: act.total || 0,
    participants: act.participants || [],
    participantCount: Array.isArray(act.participants) ? act.participants.length : 1,
    timestamp: act.billData?.timestamp || Date.now(),
    rating: act.rating || null,
    billId,
    }).catch(() => {});
   }
   });
  }
  }

  // Then load friends' activities in the background
  loadFriendsActivities();
 };
 load();
 }, [currentUser, userProfile, friends]);

 // Check for join URL parameters when component mounts
 useEffect(() => {
 const urlParams = new URLSearchParams(window.location.search);
 const joinParam = urlParams.get('join');
 if (joinParam && joinBill && currentUser) {
  console.log('Join parameter detected:', joinParam, 'for user:', currentUser?.id);
  // Clear the join parameter from URL to prevent re-triggering
  urlParams.delete('join');
  const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
  window.history.replaceState({}, '', newUrl);
  
  // Auto-trigger join with the bill ID from URL
  setTimeout(() => {
  console.log('Triggering joinBill for:', joinParam.toUpperCase());
  joinBill(joinParam.toUpperCase());
  }, 1500); // Longer delay to ensure everything is loaded
 }
 }, [joinBill, currentUser]);

 const StarDisplay = ({ rating, size = 14 }) => {
 return (
  <div className="flex items-center gap-0.5">
  {[1,2,3,4,5].map(star => (
   <Star 
   key={star} 
   size={size} 
   fill={star <= rating ? '#fbbf24' : 'none'} 
   className={star <= rating ? 'text-yellow-400' : 'text-gray-300'} 
   />
  ))}
  </div>
 );
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
 };

 // Filter activities based on current filters
 const filteredActivities = activityData.filter(activity => {
 if (filterType && activity.type !== filterType) return false;
 if (filterRating && activity.rating < filterRating) return false;
 return true;
 });

 const toggleExpandedReview = (activityId) => {
 const newExpanded = new Set(expandedReviews);
 if (newExpanded.has(activityId)) {
  newExpanded.delete(activityId);
 } else {
  newExpanded.add(activityId);
 }
 setExpandedReviews(newExpanded);
 };

 const getReviewPreview = (review, isExpanded) => {
 if (!review) return '';
 if (isExpanded || review.length <= 80) return review;
 return review.substring(0, 80) + '...';
 };

 const handleLike = async (activityId) => {
 const newLiked = new Set(likedPosts);
 const wasLiked = newLiked.has(activityId);
 
 if (wasLiked) {
  newLiked.delete(activityId);
 } else {
  newLiked.add(activityId);
 }
 
 setLikedPosts(newLiked);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userLikesRef = doc(db, `users/${userId}/profile/likedPlaces`);
  console.log('❤️ Saving likes to Firestore:', {
   userId,
   activityId,
   action: wasLiked ? 'unliked' : 'liked'
  });
  await setDoc(userLikesRef, {
   likedPlaceIds: Array.from(newLiked),
   updatedAt: new Date().toISOString()
  });
  console.log(`✅ ${wasLiked ? 'Removed like from' : 'Added like to'} activity:`, activityId);
  } catch (error) {
  console.error('Error saving like to Firestore:', error);
  // Revert the local state if Firestore save failed
  setLikedPosts(likedPosts);
  }
 }
 };

 const handleTfti = async (activityId) => {
 const newTfti = new Set(tftiPosts);
 const wasTfti = newTfti.has(activityId);
 
 if (wasTfti) {
  newTfti.delete(activityId);
 } else {
  newTfti.add(activityId);
 }
 
 setTftiPosts(newTfti);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userTftiRef = doc(db, `users/${userId}/profile/tftiPlaces`);
  console.log('💔 Saving TFTI to Firestore:', {
   userId,
   activityId,
   action: wasTfti ? 'removed TFTI' : 'added TFTI'
  });
  await setDoc(userTftiRef, {
   tftiPlaceIds: Array.from(newTfti),
   updatedAt: new Date().toISOString()
  });
  console.log(`✅ ${wasTfti ? 'Removed TFTI from' : 'Added TFTI to'} activity:`, activityId);
  } catch (error) {
  console.error('Error saving TFTI to Firestore:', error);
  // Revert the local state if Firestore save failed
  setTftiPosts(tftiPosts);
  }
 }
 };

 const handleComment = (activityId) => {
 const newCommenting = new Set(commentingPosts);
 if (newCommenting.has(activityId)) {
  newCommenting.delete(activityId);
 } else {
  newCommenting.add(activityId);
 }
 setCommentingPosts(newCommenting);
 };

 const handleShare = (activityId) => {
 // Placeholder for share functionality
 console.log('Share activity:', activityId);
 };

 const handleSave = async (activityId) => {
 const newSaved = new Set(savedPosts);
 const wasSaved = newSaved.has(activityId);
 
 if (wasSaved) {
  newSaved.delete(activityId);
 } else {
  newSaved.add(activityId);
 }
 
 setSavedPosts(newSaved);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userSavedRef = doc(db, `users/${userId}/profile/savedPlaces`);
  console.log('💾 Saving to Firestore:', {
   userId,
   path: `users/${userId}/profile/savedPlaces`,
   activityId,
   action: wasSaved ? 'removed' : 'added'
  });
  await setDoc(userSavedRef, {
   savedPlaceIds: Array.from(newSaved),
   updatedAt: new Date().toISOString()
  });
  console.log(`✅ ${wasSaved ? 'Removed from' : 'Added to'} saved list:`, activityId);
  } catch (error) {
  console.error('Error saving to Firestore:', error);
  // Revert the local state if Firestore save failed
  setSavedPosts(savedPosts);
  }
 }
 };

 const handlePostComment = async (activityId, commentText) => {
 if (!commentText.trim()) return;
 
 const newComments = { ...comments };
 if (!newComments[activityId]) {
  newComments[activityId] = [];
 }
 
 const comment = {
  id: Date.now(),
  text: commentText.trim(),
  timestamp: new Date().toISOString(),
  userId: currentUser?.uid || currentUser?.id,
  userName: currentUser?.displayName || userProfile?.displayName || currentUser?.phoneNumber || 'Anonymous',
  userAvatar: userProfile?.profilePicture || null
 };
 
 newComments[activityId].push(comment);
 setComments(newComments);
 
 // Close the comment input
 const newCommenting = new Set(commentingPosts);
 newCommenting.delete(activityId);
 setCommentingPosts(newCommenting);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userCommentsRef = doc(db, `users/${userId}/profile/comments`);
  console.log('💬 Saving comment to Firestore:', {
   userId,
   activityId,
   comment: commentText.trim()
  });
  await setDoc(userCommentsRef, {
   comments: newComments,
   updatedAt: new Date().toISOString()
  });
  console.log('✅ Comment posted successfully');
  } catch (error) {
  console.error('Error saving comment to Firestore:', error);
  }
 }
 };

 // Load social interaction data on component mount
 useEffect(() => {
 const loadSocialData = async () => {
  const userId = currentUser?.uid || currentUser?.id;
  if (!userId || !db) return;

  try {
  // Load likes
  const userLikesRef = doc(db, `users/${userId}/profile/likedPlaces`);
  const likesDoc = await getDoc(userLikesRef);
  if (likesDoc.exists()) {
   const likesData = likesDoc.data();
   setLikedPosts(new Set(likesData.likedPlaceIds || []));
  }

  // Load TFTI
  const userTftiRef = doc(db, `users/${userId}/profile/tftiPlaces`);
  const tftiDoc = await getDoc(userTftiRef);
  if (tftiDoc.exists()) {
   const tftiData = tftiDoc.data();
   setTftiPosts(new Set(tftiData.tftiPlaceIds || []));
  }

  // Load saved posts
  const userSavedRef = doc(db, `users/${userId}/profile/savedPlaces`);
  const savedDoc = await getDoc(userSavedRef);
  if (savedDoc.exists()) {
   const savedData = savedDoc.data();
   setSavedPosts(new Set(savedData.savedPlaceIds || []));
  }

  // Load comments
  const userCommentsRef = doc(db, `users/${userId}/profile/comments`);
  const commentsDoc = await getDoc(userCommentsRef);
  if (commentsDoc.exists()) {
   const commentsData = commentsDoc.data();
   setComments(commentsData.comments || {});
  }
  } catch (error) {
  console.error('Error loading social data:', error);
  }
 };

 if (currentUser && db) {
  loadSocialData();
 }
 }, [currentUser, db]);

 // Show sign-in screen if not authenticated
 if (!currentUser) {
 return (
  <div className="fixed inset-0 flex flex-col bg-[#fdfaef] text-[#2D5A5A] p-6 md:p-12 justify-between overflow-y-auto z-40">
  <div className="text-center flex-1 flex flex-col items-center justify-center">
   <div className="flex items-center justify-center mb-6">
   <img
    src="/banana-8bit.png"
    alt="8-bit Banana"
    className="w-20 h-20 md:w-16 md:h-16"
   />
   </div>
   <h1 className="text-8xl md:text-7xl font-bold text-[#2D5A5A]">SPLIT</h1>

   <div className="w-full max-w-lg mx-auto mt-12 px-4">
   <button
    onClick={onSignIn}
    className="w-full btn-pill btn-pill-warning btn-pill-xl font-bold"
   >
    Sign in with phone
   </button>
   </div>
  </div>

  {/* Footer with Twitter/X icon */}
  <div className="absolute bottom-6 left-0 right-0 flex justify-center">
   <a
   href="https://x.com/idsplitthat"
   target="_blank"
   rel="noopener noreferrer"
   className="text-[#2D5A5A] hover:text-[#FF6B35] transition-colors p-2 opacity-50 hover:opacity-100"
   aria-label="Follow us on X/Twitter"
   >
   <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-4 h-4"
   >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
   </svg>
   </a>
  </div>
  </div>
 );
 }

 return (
 <div className="flex flex-col h-full bg-[#fdfaef]">
  {/* Scrollable Content Area */}
  <div className="flex-grow overflow-y-auto" style={{ overscrollBehavior: 'none' }}>
  {/* Header with Friend Avatars */}
  <div className="px-4 pt-2 pb-2 border-b-2 border-black">
   <div className="flex items-center justify-between mb-2">
   <div className="flex items-center gap-2 md:hidden">
    <img
    src="/banana-8bit.png"
    alt="8-bit Banana"
    className="w-8 h-8"
    />
    <h1 className="text-2xl font-bold text-black">SPLIT</h1>
   </div>
   <button
    onClick={() => setIsSearchModalVisible(true)}
    className="text-[#2D5A5A] hover:text-[#FF6B35] transition-colors md:hidden"
   >
    <Search size={22} />
   </button>
   </div>

   {/* Friend Avatars Row */}
   <div className="flex items-center gap-3 overflow-x-auto pb-1">
   {/* Friends */}
   {friends.slice(0, 4).map((friend) => (
    <div key={friend.id} className="flex flex-col items-center gap-1 min-w-[60px]">
    <button 
     onClick={() => {
     // TODO: Quick-add friend to new bill
     console.log('Quick-add friend to bill:', friend);
     }}
     className="w-12 h-12 rounded-full bg-[#4ECDC4] flex items-center justify-center text-white font-bold text-sm relative hover:bg-[#45B7AF] transition-colors active:scale-95 overflow-hidden"
    >
     {friend.profilePicture ? (
     <img 
      src={friend.profilePicture} 
      alt={friend.displayName || friend.username}
      className="w-full h-full object-cover rounded-full"
     />
     ) : (
     (friend.displayName || friend.username)[0].toUpperCase()
     )}
     {/* Friend indicator - solid ring */}
     <div className="absolute inset-0 rounded-full border-2 border-[#2D5A5A] -m-0.5"></div>
    </button>
    <span className="text-xs text-[#2D5A5A] font-medium capitalize truncate max-w-[60px]">
     {(friend.displayName || friend.username).split(' ')[0]}
    </span>
    </div>
   ))}
   
   {/* Add Friends Button */}
   <div className="flex flex-col items-center gap-1 min-w-[60px]">
    <button
    onClick={() => setIsAddFriendModalVisible(true)}
    className="w-12 h-12 rounded-full border-2 border-dashed border-[#2D5A5A] flex items-center justify-center text-[#2D5A5A] hover:bg-[#2D5A5A] hover:text-white transition-all duration-200 active:scale-95 relative"
    >
    <Users size={16} />
    <div className="absolute -bottom-1 -right-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white rounded-full p-1 border-2 border-white">
     <PlusCircle size={12} />
    </div>
    </button>
    <span className="text-xs text-[#2D5A5A] font-medium ">Add</span>
   </div>
   </div>
  </div>

  {/* Filter Controls */}
  <div className="px-4 py-3 border-b border-gray-300 flex items-center gap-2">
    <button onClick={() => setFilterType(null)} className={`px-4 py-2 text-sm font-bold border-2 border-black transition-all duration-100 ${filterType === null ? 'bg-[#FF6B35] text-white shadow-brutal-sm' : 'bg-white text-gray-600 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[1px] active:translate-y-[1px] active:shadow-none'}`}>All</button>
    <button onClick={() => setFilterType('review')} className={`px-4 py-2 text-sm font-bold border-2 border-black transition-all duration-100 ${filterType === 'review' ? 'bg-[#FF6B35] text-white shadow-brutal-sm' : 'bg-white text-gray-600 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[1px] active:translate-y-[1px] active:shadow-none'}`}>Reviews</button>
    <button onClick={() => setFilterType('split')} className={`px-4 py-2 text-sm font-bold border-2 border-black transition-all duration-100 ${filterType === 'split' ? 'bg-[#FF6B35] text-white shadow-brutal-sm' : 'bg-white text-gray-600 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[1px] active:translate-y-[1px] active:shadow-none'}`}>Splits</button>
    <span className="ml-auto text-sm text-gray-400">{filteredActivities.length} posts</span>
  </div>

  {/* Content */}
  <div className="p-4">
  <div className="max-w-md mx-auto space-y-4">
   {filteredActivities.length === 0 ? (
   <div className="text-center py-12">
    <div className="text-gray-400 mb-4">
    <Activity size={48} className="mx-auto" />
    </div>
    <p className="text-xl text-gray-600 mb-2 ">No matching activity</p>
    <p className="text-gray-500 text-xs ">
    {filterType || filterRating ? 
     'Try adjusting your filters to see more activity' : 
     'Your friends haven\'t posted any activity yet'
    }
    </p>
    {(filterType || filterRating) && (
    <button
     onClick={() => {
     setFilterType(null);
     setFilterRating(null);
     }}
     className="text-indigo-600 hover:text-indigo-800 text-sm font-medium "
    >
     Clear all filters
    </button>
    )}
   </div>
   ) : (
   filteredActivities.map((activity) => (
    <div key={activity.id} className="w-full">
    <div 
     onClick={() => {
     // If this is the current user's activity, navigate to the bill
     if (activity.isCurrentUser && activity.id.startsWith('user-')) {
      const billId = activity.id.replace('user-', '');
      onStartBill(billId);
     }
     }}
     className={`w-full bg-white border-2 border-black p-4 shadow-brutal-sm text-left transition-colors ${
     activity.isCurrentUser ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
     }`}
    >
    {/* User Header */}
    <div className="flex items-center gap-3 mb-3">
     <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center border-2 border-black flex-shrink-0">
     {activity.user.avatar ? (
      <img
      src={activity.user.avatar}
      alt={activity.user.name}
      className="w-full h-full object-cover"
      />
     ) : (
      <span className="text-sm font-semibold text-gray-600">
      {activity.user.initials}
      </span>
     )}
     </div>
     <div className="flex-grow">
     <div className="flex items-center gap-2">
      <p className="font-bold text-black ">{activity.user.name}</p>
      {activity.isCurrentUser && (
      <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 font-medium">
       You
      </span>
      )}
     </div>
     <p className="text-sm text-gray-500">{activity.timestamp}</p>
     </div>
     <div className="text-right">
     <p className="text-sm font-semibold">{formatCurrency(activity.total || activity.billTotal)}</p>
     <p className="text-xs text-gray-500">{Array.isArray(activity.participants) ? activity.participants.length : activity.participants} people</p>
     </div>
    </div>

    {/* Activity Content */}
    <div className="mb-3">
     <p className="text-gray-700 mb-2 ">
     {activity.type === 'split' ? 'Split a bill at' : 'Reviewed'} <span className="font-semibold">{activity.venue}</span>
     </p>
     
     {/* Rating - only show if rating exists */}
     {activity.rating && (
     <div className="flex items-center gap-2 mb-2">
      <StarDisplay rating={activity.rating} />
      <span className="text-sm text-gray-600">({activity.rating})</span>
      {activity.wouldReturn && (
      <div className="flex items-center gap-1 text-green-600">
       <ThumbsUp size={12} />
       <span className="text-xs ">Would return</span>
      </div>
      )}
     </div>
     )}
     
     {/* Would return without rating */}
     {!activity.rating && activity.wouldReturn && (
     <div className="flex items-center gap-1 text-green-600 mb-2">
      <ThumbsUp size={12} />
      <span className="text-xs ">Would return</span>
     </div>
     )}

     {/* Review Text */}
     {activity.review && (
     <div className="mb-2">
      <p className="text-sm text-gray-600 italic ">
      "{getReviewPreview(activity.review, expandedReviews.has(activity.id))}"
      </p>
      {activity.review.length > 80 && (
      <button
       onClick={() => toggleExpandedReview(activity.id)}
       className="text-xs text-indigo-600 hover:text-indigo-800 mt-1 "
      >
       {expandedReviews.has(activity.id) ? 'Show less' : 'Read more'}
      </button>
      )}
     </div>
     )}

     {/* Tags */}
     {activity.tags && activity.tags.length > 0 && (
     <div className="flex flex-wrap gap-1 mb-2">
      {activity.tags.map((tag, index) => (
      <span 
       key={index}
       className="inline-block bg-gray-100 text-gray-700 px-2 py-1 text-xs "
      >
       {tag}
      </span>
      ))}
     </div>
     )}

     <p className="text-xs text-gray-500 ">{activity.date}</p>
    </div>

    {/* Actions */}
    <div className="pt-2 border-t-2 border-black">
     <div className="flex gap-2 mb-3">
     <button 
      onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleLike(activity.id);
      }}
      className={`flex items-center gap-1 text-sm transition-colors ${
      likedPosts.has(activity.id) 
       ? 'text-red-600 hover:text-red-700' 
       : 'text-gray-700 hover:text-red-600'
      }`}
     >
      <Heart size={14} fill={likedPosts.has(activity.id) ? 'currentColor' : 'none'} />
      <span className="">Like</span>
     </button>
     <button 
      onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleComment(activity.id);
      }}
      className={`flex items-center gap-1 text-sm transition-colors ${
      commentingPosts.has(activity.id) 
       ? 'text-[#FF6B35] hover:text-[#FF5722]' 
       : 'text-gray-700 hover:text-[#FF6B35]'
      }`}
     >
      <MessageCircle size={14} />
      <span className="">Comment</span>
     </button>
     <button 
      onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleTfti(activity.id);
      }}
      className={`flex items-center gap-1 text-sm transition-colors ${
      tftiPosts.has(activity.id) 
       ? 'text-[#A8C09A] hover:text-[#9BB88F]' 
       : 'text-gray-700 hover:text-[#A8C09A]'
      }`}
     >
      <HeartCrack size={14} fill={tftiPosts.has(activity.id) ? 'currentColor' : 'none'} />
      <span className="">TFTI</span>
     </button>
     <button 
      onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSave(activity.id);
      }}
      className={`flex items-center gap-1 text-sm transition-colors ${
      savedPosts.has(activity.id) 
       ? 'text-[#FF8E9B] hover:text-[#FF7A89]' 
       : 'text-gray-700 hover:text-[#FF8E9B]'
      }`}
     >
      <Bookmark size={14} fill={savedPosts.has(activity.id) ? 'currentColor' : 'none'} />
      <span className="">Save</span>
     </button>
     </div>
     
     {/* Display existing comments - Inside the main card */}
     {comments[activity.id] && comments[activity.id].length > 0 && (
     <div className="mt-4 pt-4 border-t-2 border-black">
      {comments[activity.id].map((comment, index) => (
      <div key={comment.id} className={`py-3 ${index < comments[activity.id].length - 1 ? 'border-b border-gray-100' : ''}`}>
       <div className="flex items-center gap-3">
       {/* Profile Photo */}
       <div className="flex-shrink-0">
        {(() => {
        const avatarUrl = getCommentAuthorAvatar(comment, currentUser, friends, userProfile);
        return avatarUrl ? (
         <img
         src={avatarUrl}
         alt={comment.userName}
         className="w-8 h-8 rounded-full object-cover border-2 border-black"
         />
        ) : (
         <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-pink-400 flex items-center justify-center text-white text-sm font-bold border-2 border-black">
         {comment.userName?.charAt(0).toUpperCase() || '?'}
         </div>
        );
        })()}
       </div>
       {/* Comment Content */}
       <div className="flex-1">
        <div className="font-semibold text-black text-sm mb-1">{comment.userName}</div>
        <div className="text-black text-[15px]">{comment.text}</div>
       </div>
       </div>
      </div>
      ))}
     </div>
     )}
     
     {/* Comment Input - Inside the main card */}
     {commentingPosts.has(activity.id) && (
     <div className="mt-4 pt-4 border-t-2 border-black">
      <div className="flex gap-2">
      <input
       type="text"
       placeholder="Add a comment..."
       value={commentTexts[activity.id] || ''}
       autoFocus
       tabIndex={0}
       onChange={(e) => {
       const newTexts = { ...commentTexts };
       newTexts[activity.id] = e.target.value;
       setCommentTexts(newTexts);
       }}
       onKeyPress={(e) => {
       if (e.key === 'Enter' && commentTexts[activity.id]?.trim()) {
        handlePostComment(activity.id, commentTexts[activity.id]);
        const newTexts = { ...commentTexts };
        delete newTexts[activity.id];
        setCommentTexts(newTexts);
       }
       }}
       className="flex-grow px-3 py-2 text-sm border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
      />
      <button 
       className="btn-pill btn-pill-warning btn-pill-sm"
       onClick={(e) => {
       e.preventDefault();
       e.stopPropagation();
       if (commentTexts[activity.id]?.trim()) {
        handlePostComment(activity.id, commentTexts[activity.id]);
        const newTexts = { ...commentTexts };
        delete newTexts[activity.id];
        setCommentTexts(newTexts);
       }
       }}
      >
       Post
      </button>
      </div>
     </div>
     )}
     
    </div>
    </div>
    </div>
   ))
   )}

   {/* Load More */}
   {filteredActivities.length > 0 && (
   <div className="text-center py-4">
    <button className="btn-pill btn-pill-warning btn-pill-sm font-semibold ">
    Load More FOMO
    </button>
   </div>
   )}
  </div>
  </div>
  </div>
  
 </div>
 );
};

// Helper function to get comment author's profile picture
const getCommentAuthorAvatar = (comment, currentUser, friends = [], userProfile = null) => {
 // If there's already a stored avatar, use it
 if (comment.userAvatar) {
 return comment.userAvatar;
 }

 // If this is the current user's comment, try to get their profile picture
 if (currentUser && comment.userId &&
  (comment.userId === currentUser.id || comment.userId === currentUser.uid)) {
 if (userProfile?.profilePicture) {
  return userProfile.profilePicture;
 }
 }
 
 // For other users, check if we have their profile picture in friends data
 if (comment.userId && friends.length > 0) {
 const friend = friends.find(f => 
  f.userId === comment.userId || 
  f.username === comment.userName ||
  (f.displayName && f.displayName === comment.userName)
 );
 if (friend?.profilePicture) {
  return friend.profilePicture;
 }
 }
 
 // For now, return null to show generated avatar
 return null;
};

// Avatar component that handles profile pictures vs generated avatars 
const PersonAvatar = ({ person, currentUser, userProfile = null, className = "w-10 h-10 rounded-full", showBorder = true }) => {
 const [loaded, setLoaded] = useState(false);
 const [errored, setErrored] = useState(false);
 const isCurrentUser = currentUser && (person.id === currentUser.id || person.id === currentUser.uid);
 // Use profile picture from: current user's live profile, person data on the bill, or fallback to generated avatar
 const imgSrc = (isCurrentUser && userProfile?.profilePicture) ? userProfile.profilePicture : (person.profilePicture || person.avatar);
 const borderClass = showBorder ? 'border-2 border-black' : '';
 const initial = (person.name || '?')[0].toUpperCase();

 return (
 <div className={`${className} ${borderClass} relative overflow-hidden flex items-center justify-center bg-gray-200`}>
  {!loaded && !errored && <div className="absolute inset-0 animate-pulse bg-gray-300" />}
  {errored && <span className="text-sm font-bold text-gray-500">{initial}</span>}
  {!errored && (
  <img
   src={imgSrc}
   alt={person.name}
   className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
   onLoad={() => setLoaded(true)}
   onError={() => setErrored(true)}
  />
  )}
 </div>
 );
};

// Add Friend Modal Component
// Feed avatar with shimmer loading state
const FeedAvatar = ({ src, name, initials }) => {
 const [loaded, setLoaded] = useState(false);
 const [errored, setErrored] = useState(false);
 const fallback = initials || (name || '?')[0].toUpperCase();

 return (
 <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center relative border-2 border-black flex-shrink-0">
  {!loaded && !errored && src && <div className="absolute inset-0 animate-pulse bg-gray-300" />}
  {(!src || errored) && <span className="text-sm font-semibold text-gray-600">{fallback}</span>}
  {src && !errored && (
  <img
   src={src}
   alt={name}
   className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
   onLoad={() => setLoaded(true)}
   onError={() => setErrored(true)}
  />
  )}
 </div>
 );
};

const AddFriendModal = ({ isVisible, onClose, currentUser, db, setFriends, friends }) => {
 const [searchQuery, setSearchQuery] = useState('');
 const [searchResults, setSearchResults] = useState([]);
 const [isSearching, setIsSearching] = useState(false);
 const [searchType, setSearchType] = useState('username'); // 'username' or 'phone'

 const handleSearch = async () => {
 if (!searchQuery.trim() || !db) {
  console.log('Search cancelled: missing query or db', { searchQuery: searchQuery.trim(), db: !!db });
  return;
 }
 
 console.log('Starting search:', { searchQuery: searchQuery.trim(), searchType, db: !!db });
 setIsSearching(true);
 setSearchResults([]);
 
 try {
  let result = null;
  
  if (searchType === 'username') {
  console.log('Searching by username:', searchQuery.trim());
  result = await UserRegistry.findUserByUsername(searchQuery.trim(), db);
  console.log('Username search result:', result);
  } else {
  // Format phone number for search
  const cleanPhone = searchQuery.replace(/\D/g, '');
  const formattedPhone = cleanPhone.startsWith('1') ? `+${cleanPhone}` : `+1${cleanPhone}`;
  console.log('Searching by phone:', { original: searchQuery, clean: cleanPhone, formatted: formattedPhone });
  result = await UserRegistry.findUserByPhone(formattedPhone, db);
  console.log('Phone search result:', result);
  }
  
  if (result) {
  console.log('User found:', result);
  setSearchResults([result]);
  } else {
  console.log('No user found');
  setSearchResults([]);
  }
 } catch (error) {
  console.error('Error searching for user:', error);
  setSearchResults([]);
 } finally {
  setIsSearching(false);
 }
 };

 const handleAddFriend = async (friendData) => {
 try {
  const userId = currentUser?.uid || currentUser?.id;
  if (!userId) {
  console.error('No current user ID for adding friend');
  return;
  }

  // Check if already a friend
  const isAlreadyFriend = await FriendsManager.isFriend(userId, friendData.userId || friendData.username, db);
  if (isAlreadyFriend) {
  alert('This person is already in your friends list!');
  return;
  }

  // Add friend to Firestore
  await FriendsManager.addFriend(userId, friendData, db);
  
  // Refresh friends list
  const updatedFriends = await FriendsManager.getFriends(userId, db);
  setFriends(updatedFriends);
  
  // Show success message
  alert(`Added ${friendData.displayName || friendData.username} as a friend!`);
  
  // Close modal
  onClose();
 } catch (error) {
  console.error('Error adding friend:', error);
  alert('Failed to add friend. Please try again.');
 }
 };

 const handleRemoveFriend = async (friendData) => {
 try {
  const userId = currentUser?.uid || currentUser?.id;
  if (!userId) {
  console.error('No current user ID for removing friend');
  return;
  }

  // Remove friend from Firestore
  await FriendsManager.removeFriend(userId, friendData.userId || friendData.username, db);
  
  // Refresh friends list
  const updatedFriends = await FriendsManager.getFriends(userId, db);
  setFriends(updatedFriends);
  
  // Show success message
  alert(`Removed ${friendData.displayName || friendData.username} from friends`);
  
  // Close modal
  onClose();
 } catch (error) {
  console.error('Error removing friend:', error);
  alert('Failed to remove friend. Please try again.');
 }
 };

 // Check if a user is already a friend
 const isFriend = (userData) => {
 const userId = currentUser?.uid || currentUser?.id;
 if (!userId || !userData) return false;
 
 return friends.some(friend => 
  friend.userId === userData.userId || 
  friend.username === userData.username ||
  friend.id === (userData.userId || userData.username)
 );
 };

 if (!isVisible) return null;

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
  <div className="bg-white border-2 border-black p-6 w-full max-w-md shadow-brutal-lg">
  <div className="flex justify-between items-center mb-4">
   <h3 className="text-xl font-bold ">Add Friend</h3>
   <button onClick={onClose} className="text-gray-500 hover:text-black">
   <X size={24} />
   </button>
  </div>
  
  {/* Search Type Toggle */}
  <div className="flex gap-2 mb-4">
   <button
   onClick={() => setSearchType('username')}
   className={`flex-1 btn-pill ${
    searchType === 'username' 
    ? 'btn-pill-warning' 
    : 'btn-pill-secondary'
   }`}
   >
   Username
   </button>
   <button
   onClick={() => setSearchType('phone')}
   className={`flex-1 btn-pill ${
    searchType === 'phone' 
    ? 'btn-pill-warning' 
    : 'btn-pill-secondary'
   }`}
   >
   Phone
   </button>
  </div>
  
  {/* Search Input */}
  <div className="flex gap-2 mb-4">
   <input
   type="text"
   value={searchQuery}
   onChange={(e) => setSearchQuery(e.target.value)}
   placeholder={searchType === 'username' ? '@username' : 'Phone number'}
   className="flex-1 px-3 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35]"
   onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
   />
   <button
   onClick={handleSearch}
   disabled={!searchQuery.trim() || isSearching}
   className="btn-pill btn-pill-warning disabled:bg-gray-300 disabled:cursor-not-allowed"
   >
   {isSearching ? (
    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
   ) : (
    <Search size={16} />
   )}
   </button>
  </div>
  
  {/* Search Results */}
  <div className="space-y-2">
   {searchResults.length > 0 ? (
   searchResults.map((user, index) => (
    <div key={index} className="flex items-center justify-between p-3 border-2 border-black ">
    <div className="flex items-center gap-3">
     {user.profilePicture ? (
     <img 
      src={user.profilePicture} 
      alt={user.displayName} 
      className="w-10 h-10 rounded-full object-cover"
     />
     ) : (
     <div className="w-10 h-10 bg-indigo-700 rounded-full flex items-center justify-center text-white font-bold">
      {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
     </div>
     )}
     <div>
     <p className="font-medium ">{user.displayName || 'No name set'}</p>
     <p className="text-sm text-gray-600 ">@{user.username}</p>
     </div>
    </div>
    {isFriend(user) ? (
     <button
     onClick={() => handleRemoveFriend(user)}
     className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 border-2 border-black shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
     >
     Remove
     </button>
    ) : (
     <button
     onClick={() => handleAddFriend(user)}
     className="bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 border-2 border-black shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
     >
     Add
     </button>
    )}
    </div>
   ))
   ) : (
   searchQuery && !isSearching && (
    <p className="text-center text-gray-500 py-4 ">
    No user found with that {searchType}
    </p>
   )
   )}
  </div>
  
  {/* Help Text */}
  <div className="mt-4 text-sm text-gray-600 text-center ">
   {searchType === 'username' 
   ? 'Search by username (e.g., @john_doe)' 
   : 'Search by phone number (e.g., +1234567890)'
   }
  </div>
  </div>
 </div>
 );
};

// Search Modal Component
const SearchModal = ({ isVisible, onClose, currentUser, db }) => {
 const [searchQuery, setSearchQuery] = useState('');
 const [searchResults, setSearchResults] = useState([]);
 const [isSearching, setIsSearching] = useState(false);
 const [searchType, setSearchType] = useState('bills'); // 'bills', 'items', 'restaurants'

 const handleSearch = async () => {
 if (!searchQuery.trim()) {
  setSearchResults([]);
  return;
 }

 setIsSearching(true);
 setSearchResults([]);

 try {
  const query = searchQuery.toLowerCase().trim();
  let results = [];

  if (searchType === 'bills') {
  // Search through bill history
  const billHistory = await BillHistory.getHistoryWithSync(currentUser?.id, db);
  results = billHistory.filter(bill =>
   bill.venueName?.toLowerCase().includes(query) ||
   bill.billSnapshot?.items?.some(item => item.name.toLowerCase().includes(query)) ||
   bill.participants?.some(name => name.toLowerCase().includes(query))
  ).map(bill => ({
   type: 'bill',
   id: bill.id,
   title: bill.venueName || 'Unknown Venue',
   subtitle: `${bill.itemCount || bill.billSnapshot?.items?.length || 0} items • ${formatCurrency(bill.total)}`,
   date: bill.timestamp ? new Date(bill.timestamp).toLocaleDateString() : '',
   data: bill
  }));
  } else if (searchType === 'items') {
  // Search through all items in bill history
  const billHistory = await BillHistory.getHistoryWithSync(currentUser?.id, db);
  const itemResults = [];
  billHistory.forEach(bill => {
   const items = bill.billSnapshot?.items || [];
   items.forEach(item => {
   if (item.name.toLowerCase().includes(query)) {
    itemResults.push({
    type: 'item',
    id: `${bill.id}-${item.name}`,
    title: item.name,
    subtitle: `${formatCurrency(item.price)} at ${bill.venueName || 'Unknown Venue'}`,
    date: bill.timestamp ? new Date(bill.timestamp).toLocaleDateString() : '',
    data: { item, bill }
    });
   }
   });
  });
  results = itemResults;
  } else if (searchType === 'restaurants') {
  // Search through venues in bill history
  const billHistory = await BillHistory.getHistoryWithSync(currentUser?.id, db);
  const venueMap = new Map();
  billHistory.forEach(bill => {
   if (bill.venueName && bill.venueName.toLowerCase().includes(query)) {
   if (!venueMap.has(bill.venueName)) {
    venueMap.set(bill.venueName, {
    type: 'restaurant',
    id: bill.venueName,
    title: bill.venueName,
    subtitle: `Visited ${billHistory.filter(b => b.venueName === bill.venueName).length} times`,
    date: '',
    data: bill
    });
   }
   }
  });
  results = Array.from(venueMap.values());
  }

  setSearchResults(results);
 } catch (error) {
  console.error('Search error:', error);
  setSearchResults([]);
 } finally {
  setIsSearching(false);
 }
 };

 // Search as user types
 useEffect(() => {
 const delayedSearch = setTimeout(() => {
  handleSearch();
 }, 300);

 return () => clearTimeout(delayedSearch);
 }, [searchQuery, searchType]);

 const formatCurrency = (amount) => {
 if (typeof amount !== 'number') return '$0.00';
 return new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
 }).format(amount);
 };

 if (!isVisible) return null;

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
  <div className="bg-white w-full max-w-md h-[80vh] flex flex-col border-2 border-black shadow-brutal-lg">
  {/* Header */}
  <div className="flex items-center justify-between p-4 border-b-2 border-black">
   <h2 className="text-xl font-bold text-black">Search</h2>
   <button
   onClick={onClose}
   className="p-2 hover:bg-gray-100 transition-colors"
   >
   <X size={20} />
   </button>
  </div>
  
  {/* Search Type Toggle */}
  <div className="flex gap-1 p-4">
   {[
   { key: 'bills', label: 'Bills' },
   { key: 'items', label: 'Items' },
   { key: 'restaurants', label: 'Places' }
   ].map(({ key, label }) => (
   <button
    key={key}
    onClick={() => setSearchType(key)}
    className={`flex-1 py-2 px-3 text-sm font-bold border-2 border-black transition-all duration-100 ${
    searchType === key
     ? 'bg-[#FF6B35] text-white shadow-brutal-sm'
     : 'bg-white text-gray-600 hover:bg-gray-100 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm'
    }`}
   >
    {label}
   </button>
   ))}
  </div>
  
  {/* Search Input */}
  <div className="px-4 pb-4">
   <input
   type="text"
   value={searchQuery}
   onChange={(e) => setSearchQuery(e.target.value)}
   placeholder={`Search ${searchType}...`}
   className="w-full px-4 py-3 border-2 border-black focus:outline-none focus:shadow-brutal-sm transition-all"
   autoFocus
   />
  </div>
  
  {/* Search Results */}
  <div className="flex-1 overflow-y-auto px-4 pb-4">
   {isSearching ? (
   <div className="flex items-center justify-center py-8">
    <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
   </div>
   ) : searchResults.length > 0 ? (
   <div className="space-y-2">
    {searchResults.map((result) => (
    <div
     key={result.id}
     className="p-3 border-2 border-black shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
    >
     <div className="flex items-center gap-3">
     <div className="w-10 h-10 bg-[#FF6B35] flex items-center justify-center text-white font-bold">
      {result.type === 'bill' ? '📋' : result.type === 'item' ? '🍽️' : '🏪'}
     </div>
     <div className="flex-1">
      <h3 className="font-bold text-black truncate">{result.title}</h3>
      <p className="text-sm text-gray-600 truncate">{result.subtitle}</p>
      {result.date && (
      <p className="text-xs text-gray-400">{result.date}</p>
      )}
     </div>
     </div>
    </div>
    ))}
   </div>
   ) : searchQuery && !isSearching ? (
   <div className="text-center py-8">
    <p className="text-gray-500">No {searchType} found</p>
   </div>
   ) : (
   <div className="text-center py-8">
    <p className="text-gray-400">Start typing to search {searchType}</p>
   </div>
   )}
  </div>
  </div>
 </div>
 );
};

export default function App() {
 // State Management
 const [screen, setScreen] = useState('getStarted');
 const [billId, setBillId] = useState(null);
 const [billData, setBillData] = useState(null);
 const [calculationResultsFromHistory, setCalculationResultsFromHistory] = useState(null);
 const [friends, setFriends] = useState([]);
 const [currentUser, setCurrentUser] = useState(null);
 const [isLoading, setIsLoading] = useState(true);
 const [loadingState, setLoadingState] = useState({ message: null, subtitle: null });
 
 // Activity and social interaction states
 const [activityData, setActivityData] = useState([]);
 const [savedPosts, setSavedPosts] = useState(new Set());
 const [likedPosts, setLikedPosts] = useState(new Set());
 const [tftiPosts, setTftiPosts] = useState(new Set());
 const [commentingPosts, setCommentingPosts] = useState(new Set());
 const [comments, setComments] = useState({}); // Store comments by activity ID
 const [commentTexts, setCommentTexts] = useState({}); // Store draft comment texts by activity ID

 // Function to load saved posts from Firestore
 const loadSavedPosts = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 console.log('🔄 loadSavedPosts called:', { userId, hasDb: !!db });
 
 if (userId && db) {
  try {
  const userSavedRef = doc(db, `users/${userId}/profile/savedPlaces`);
  console.log('📂 Checking Firestore path:', `users/${userId}/profile/savedPlaces`);
  
  const savedDoc = await getDoc(userSavedRef);
  console.log('📄 Firestore document exists:', savedDoc.exists());
  
  if (savedDoc.exists()) {
   const savedData = savedDoc.data();
   console.log('📋 Raw saved data from Firestore:', savedData);
   
   const savedPlaceIds = savedData.savedPlaceIds || [];
   setSavedPosts(new Set(savedPlaceIds));
   console.log('✅ Loaded saved posts:', {
   count: savedPlaceIds.length,
   ids: savedPlaceIds
   });
  } else {
   console.log('📝 No saved places document found, setting empty set');
   setSavedPosts(new Set());
  }
  } catch (error) {
  console.error('❌ Error loading saved posts:', error);
  }
 } else {
  console.log('⚠️ Missing userId or db in loadSavedPosts');
 }
 };

 // Function to load liked posts from Firestore
 const loadLikedPosts = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 console.log('🔄 loadLikedPosts called:', { userId, hasDb: !!db });
 
 if (userId && db) {
  try {
  const userLikesRef = doc(db, `users/${userId}/profile/likedPlaces`);
  const likesDoc = await getDoc(userLikesRef);
  
  if (likesDoc.exists()) {
   const likesData = likesDoc.data();
   const likedPlaceIds = likesData.likedPlaceIds || [];
   setLikedPosts(new Set(likedPlaceIds));
   console.log('✅ Loaded liked posts:', { count: likedPlaceIds.length });
  } else {
   setLikedPosts(new Set());
  }
  } catch (error) {
  console.error('❌ Error loading liked posts:', error);
  }
 }
 };

 // Function to load TFTI posts from Firestore
 const loadTftiPosts = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 console.log('🔄 loadTftiPosts called:', { userId, hasDb: !!db });
 
 if (userId && db) {
  try {
  const userTftiRef = doc(db, `users/${userId}/profile/tftiPlaces`);
  const tftiDoc = await getDoc(userTftiRef);
  
  if (tftiDoc.exists()) {
   const tftiData = tftiDoc.data();
   const tftiPlaceIds = tftiData.tftiPlaceIds || [];
   setTftiPosts(new Set(tftiPlaceIds));
   console.log('✅ Loaded TFTI posts:', { count: tftiPlaceIds.length });
  } else {
   setTftiPosts(new Set());
  }
  } catch (error) {
  console.error('❌ Error loading TFTI posts:', error);
  }
 }
 };

 // Function to load comments from Firestore
 const loadComments = async () => {
 const userId = currentUser?.uid || currentUser?.id;
 console.log('🔄 loadComments called:', { userId, hasDb: !!db });
 
 if (userId && db) {
  try {
  const userCommentsRef = doc(db, `users/${userId}/profile/comments`);
  const commentsDoc = await getDoc(userCommentsRef);
  
  if (commentsDoc.exists()) {
   const commentsData = commentsDoc.data();
   setComments(commentsData.comments || {});
   console.log('✅ Loaded comments:', { count: Object.keys(commentsData.comments || {}).length });
  } else {
   setComments({});
  }
  } catch (error) {
  console.error('❌ Error loading comments:', error);
  }
 }
 };

 const handleSave = async (activityId) => {
 const newSaved = new Set(savedPosts);
 const wasSaved = newSaved.has(activityId);
 
 if (wasSaved) {
  newSaved.delete(activityId);
 } else {
  newSaved.add(activityId);
 }
 
 setSavedPosts(newSaved);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userSavedRef = doc(db, `users/${userId}/profile/savedPlaces`);
  console.log('💾 Saving to Firestore:', {
   userId,
   path: `users/${userId}/profile/savedPlaces`,
   activityId,
   action: wasSaved ? 'removed' : 'added'
  });
  await setDoc(userSavedRef, {
   savedPlaceIds: Array.from(newSaved),
   updatedAt: new Date().toISOString()
  });
  console.log(`✅ ${wasSaved ? 'Removed from' : 'Added to'} saved list:`, activityId);
  } catch (error) {
  console.error('Error saving to Firestore:', error);
  // Revert the local state if Firestore save failed
  setSavedPosts(savedPosts);
  }
 }
 };

 // Social interaction handlers
 const handleLike = async (activityId) => {
 const newLiked = new Set(likedPosts);
 const wasLiked = newLiked.has(activityId);
 
 if (wasLiked) {
  newLiked.delete(activityId);
 } else {
  newLiked.add(activityId);
 }
 
 setLikedPosts(newLiked);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userLikesRef = doc(db, `users/${userId}/profile/likedPlaces`);
  console.log('❤️ Saving likes to Firestore:', {
   userId,
   activityId,
   action: wasLiked ? 'unliked' : 'liked'
  });
  await setDoc(userLikesRef, {
   likedPlaceIds: Array.from(newLiked),
   updatedAt: new Date().toISOString()
  });
  console.log(`✅ ${wasLiked ? 'Removed like from' : 'Added like to'} activity:`, activityId);
  } catch (error) {
  console.error('Error saving like to Firestore:', error);
  setLikedPosts(likedPosts);
  }
 }
 };

 const handleTfti = async (activityId) => {
 const newTfti = new Set(tftiPosts);
 const wasTfti = newTfti.has(activityId);
 
 if (wasTfti) {
  newTfti.delete(activityId);
 } else {
  newTfti.add(activityId);
 }
 
 setTftiPosts(newTfti);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userTftiRef = doc(db, `users/${userId}/profile/tftiPlaces`);
  console.log('💔 Saving TFTI to Firestore:', {
   userId,
   activityId,
   action: wasTfti ? 'removed TFTI' : 'added TFTI'
  });
  await setDoc(userTftiRef, {
   tftiPlaceIds: Array.from(newTfti),
   updatedAt: new Date().toISOString()
  });
  console.log(`✅ ${wasTfti ? 'Removed TFTI from' : 'Added TFTI to'} activity:`, activityId);
  } catch (error) {
  console.error('Error saving TFTI to Firestore:', error);
  setTftiPosts(tftiPosts);
  }
 }
 };

 const handleComment = (activityId) => {
 const newCommenting = new Set(commentingPosts);
 if (newCommenting.has(activityId)) {
  newCommenting.delete(activityId);
 } else {
  newCommenting.add(activityId);
 }
 setCommentingPosts(newCommenting);
 };

 const handlePostComment = async (activityId, commentText) => {
 if (!commentText.trim()) return;
 
 const newComments = { ...comments };
 if (!newComments[activityId]) {
  newComments[activityId] = [];
 }
 
 const comment = {
  id: Date.now(),
  text: commentText.trim(),
  timestamp: new Date().toISOString(),
  userId: currentUser?.uid || currentUser?.id,
  userName: currentUser?.displayName || userProfile?.displayName || currentUser?.phoneNumber || 'Anonymous',
  userAvatar: userProfile?.profilePicture || null
 };
 
 newComments[activityId].push(comment);
 setComments(newComments);
 
 // Close the comment input
 const newCommenting = new Set(commentingPosts);
 newCommenting.delete(activityId);
 setCommentingPosts(newCommenting);
 
 // Persist to Firestore
 if (currentUser && db) {
  try {
  const userId = currentUser?.uid || currentUser?.id;
  const userCommentsRef = doc(db, `users/${userId}/profile/comments`);
  console.log('💬 Saving comment to Firestore:', {
   userId,
   activityId,
   comment: commentText.trim()
  });
  await setDoc(userCommentsRef, {
   comments: newComments,
   updatedAt: new Date().toISOString()
  });
  console.log('✅ Comment posted successfully');
  } catch (error) {
  console.error('Error saving comment to Firestore:', error);
  }
 }
 };

 // Load friends when user changes
 useEffect(() => {
 const loadFriends = async () => {
  const userId = currentUser?.uid || currentUser?.id;
  if (userId && db) {
  try {
   const userFriends = await FriendsManager.getFriends(userId, db);
   setFriends(userFriends);
   
   // Run migration to update existing friends with profile pictures
   FriendsManager.migrateFriendsWithProfilePictures(userId, db).then(async (migrationSuccess) => {
   if (migrationSuccess) {
    // Reload friends after migration to get updated profile pictures
    const updatedFriends = await FriendsManager.getFriends(userId, db);
    setFriends(updatedFriends);
   }
   });
  } catch (error) {
   console.error('Error loading friends:', error);
  }
  } else {
  setFriends([]);
  }
 };

 loadFriends();
 }, [currentUser, db]);

 // Load activity: own bills from history (fast) + friends from activities collection (async)
 const getUserActivityData = async () => {
 if (!currentUser || !db) return [];
 const userId = currentUser?.uid || currentUser?.id;

 try {
  // Load own bills from history — this is fast (cached/local)
  const history = await BillHistory.getHistoryWithSync(userId, db);
  const myActivities = history.slice(0, 20).map(bill => ({
  id: `user-${bill.id}`,
  type: 'split',
  user: {
   name: userProfile?.displayName || userProfile?.username || 'You',
   avatar: userProfile?.profilePicture || '/default-avatar.png',
   username: userProfile?.username || ''
  },
  venue: bill.venueName || 'Unknown Restaurant',
  total: bill.total || 0,
  participants: bill.participants || ['You'],
  timestamp: bill.createdAt ? new Date(bill.createdAt).toLocaleDateString() : bill.timestamp ? new Date(bill.timestamp).toLocaleDateString() : new Date().toLocaleDateString(),
  rawTimestamp: bill.timestamp || (bill.createdAt ? new Date(bill.createdAt).getTime() : 0),
  rating: bill.experience?.venueRating || null,
  wouldReturn: bill.experience?.wouldReturn || null,
  isCurrentUser: true,
  billData: bill
  }));

  return myActivities;
 } catch (error) {
  console.error('Error loading activity data:', error);
  return [];
 }
 };

 // Phone Auth State
 const [showPhoneAuth, setShowPhoneAuth] = useState(false);
 const [phoneNumber, setPhoneNumber] = useState('');
 const [verificationCode, setVerificationCode] = useState('');
 const [phoneAuthStep, setPhoneAuthStep] = useState('phone'); // 'phone' or 'verify'
 const [deliveryChannel, setDeliveryChannel] = useState('sms');
 const [isSendingCode, setIsSendingCode] = useState(false);
 const [isVerifyingCode, setIsVerifyingCode] = useState(false);
 
 // Mobile-friendly prompts state
 const [showNamePrompt, setShowNamePrompt] = useState(false);
 const [showVenuePrompt, setShowVenuePrompt] = useState(false);
 const [tempUserName, setTempUserName] = useState('');
 const [tempVenueName, setTempVenueName] = useState('');
 const [pendingBillCreation, setPendingBillCreation] = useState(false);
 const [isAddFriendModalVisible, setIsAddFriendModalVisible] = useState(false);
 const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
 
 const [errorMessage, setErrorMessage] = useState('');
 const [draggedItemId, setDraggedItemId] = useState(null);
 const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
 const [userProfile, setUserProfile] = useState(null);

 // Update activity data when userProfile or friends change
 useEffect(() => {
 const loadActivityData = async () => {
  const data = await getUserActivityData();
  setActivityData(data);
 };

 if (currentUser && userProfile) {
  loadActivityData();
  loadSavedPosts();
  loadLikedPosts();
  loadTftiPosts();
  loadComments();
 }
 }, [currentUser, userProfile, friends]);

 // Update page metadata for join links
 useEffect(() => {
 const urlParams = new URLSearchParams(window.location.search);
 const joinParam = urlParams.get('join');
 
 if (joinParam) {
  // Set a generic join title initially
  document.title = 'Join Bill - Split';
  
  // Update Open Graph meta tags for link previews
  const updateMetaTag = (property, content) => {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
   meta = document.createElement('meta');
   meta.setAttribute('property', property);
   document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
  };
  
  // Set generic join message initially
  updateMetaTag('og:title', 'Split the bill with me');
  updateMetaTag('og:description', 'Join me to split our bill together! 🧾');
  
  // Try to get more specific info from Firebase
  // This will run asynchronously and update if successful
  if (joinParam) {
  const billDocRef = doc(db, 'bills', joinParam.toUpperCase());
  getDoc(billDocRef).then((docSnap) => {
   if (docSnap.exists()) {
   const data = docSnap.data();
   const venueName = data.venueName || 'restaurant';
   document.title = `Split the ${venueName} bill - Split`;
   updateMetaTag('og:title', `Split the ${venueName} bill with me`);
   updateMetaTag('og:description', `Join me to split our ${venueName} bill! 🧾`);
   }
  }).catch((error) => {
   console.log('Could not fetch bill info for meta tags:', error);
  });
  }
 }
 }, []);
 const [isStartBillModalVisible, setIsStartBillModalVisible] = useState(false);
 const [scannedData, setScannedData] = useState(null);
 const [recentBills, setRecentBills] = useState([]);
 const [isReScanning, setIsReScanning] = useState(false);
 const [mergeHistory, setMergeHistory] = useState([]); // Track all merges for multi-step undo

 const AVATAR_COLORS = [
 { bg: 'E7A7A7', text: '333' }, { bg: 'A7E7A7', text: '333' }, { bg: 'A7A7E7', text: '333' },
 { bg: 'E7D4A7', text: '333' }, { bg: 'E7A7D1', text: '333' }, { bg: 'A7E7E7', text: '333' },
 ];

 // Helper function to get avatar URL (inline SVG - no network request)
 const getAvatarUrl = (userId, userName, colorIndex = 0) => {
 const color = AVATAR_COLORS[colorIndex % AVATAR_COLORS.length];
 const initial = (userName || '?').charAt(0).toUpperCase();
 const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="#${color.bg}"/><text x="50" y="50" dominant-baseline="central" text-anchor="middle" font-family="system-ui,sans-serif" font-weight="700" font-size="44" fill="#${color.text}">${initial}</text></svg>`;
 return `data:image/svg+xml,${encodeURIComponent(svg)}`;
 };



 // --- Authentication & Recent Bills Loading ---
useEffect(() => {
 console.log("=== STARTING AUTHENTICATION ===");
 setRecentBills(getRecentBills());

 // Timeout: if auth doesn't resolve in 10 seconds, show sign-in screen
 const authTimeout = setTimeout(() => {
 console.log("Authentication timeout - showing sign-in screen");
 setCurrentUser(null);
 setIsLoading(false);
 }, 10000);

 // Listen for auth state changes (session restore or new sign-in)
 auth.onAuthStateChanged(user => {
 console.log("Auth state changed:", user);
 clearTimeout(authTimeout);
 if (user && !user.isAnonymous) {
  console.log("User authenticated:", user.uid);

  const storedPhone = localStorage.getItem('authenticatedPhoneNumber') || localStorage.getItem('userPhoneNumber');

  setCurrentUser({
  id: user.uid,
  uid: user.uid,
  firebaseUid: user.uid,
  phoneNumber: user.phoneNumber || storedPhone,
  providerData: user.providerData
  });

  // Restore current bill from localStorage if exists
  const savedBillId = getCurrentBill();
  if (savedBillId && !billId) {
  console.log('🔄 Restoring bill from localStorage:', savedBillId);
  setBillId(savedBillId);
  setScreen('assignItems');
  }

  // One-time: migrate any localStorage profile data to Firestore
  setTimeout(async () => {
  try {
   await UserProfile.migrateFromLocalStorage(user.uid, db);
   const userProfile = await UserProfile.getProfile(user.uid, db);
   if (userProfile.username) {
   await UserRegistry.migrateExistingUser(user.uid, userProfile, db);
   }
  } catch (error) {
   console.error('Error during user migration:', error);
  }
  }, 1000);
 } else {
  // No user or anonymous user — show sign-in screen
  console.log("No authenticated user, showing sign-in screen");
  setCurrentUser(null);
 }
 setIsLoading(false);
 });
}, []);

 // --- Save billId to localStorage when it changes ---
 useEffect(() => {
 saveCurrentBill(billId);
 }, [billId]);

 // --- Firestore Real-time Listener ---
 useEffect(() => {
 if (!billId) return;
 
 // Skip Firestore listener for combined bills and settlements (they're local only)
 if (billId.startsWith('combined_') || billId.startsWith('settlement_')) {
  console.log("=== SKIPPING FIRESTORE LISTENER FOR LOCAL BILL ===", billId);
  return;
 }
 
 console.log("=== SETTING UP FIRESTORE LISTENER ===");
 const billDocRef = doc(db, `artifacts/${appId}/public/data/bills`, billId);
 const unsub = onSnapshot(billDocRef, (doc) => {
  if (doc.exists()) {
  console.log("=== BILL LOADED FROM FIRESTORE ===", doc.data());
  setBillData(doc.data());
  setIsLoading(false);
  } else {
  setErrorMessage("This bill no longer exists.");
  resetApp();
  }
 }, (error) => {
  console.error("Firestore listener error:", error);
  // Only reset if we don't have bill data already (don't nuke a working session)
  if (!billData) {
  setErrorMessage("Connection issue. Please try again.");
  resetApp();
  } else {
  console.warn("Firestore listener error, but billData exists locally - continuing");
  }
 });
 return () => unsub();
 }, [billId]);

 // Global error handler for debugging script errors
 useEffect(() => {
 const handleGlobalError = (error) => {
  console.error('Global error caught:', error);
  if (error.error) {
  console.error('Error object:', error.error);
  console.error('Error stack:', error.error.stack);
  }
 };
 
 const handleUnhandledRejection = (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  console.error('Promise rejection stack:', event.reason?.stack);
 };
 
 window.addEventListener('error', handleGlobalError);
 window.addEventListener('unhandledrejection', handleUnhandledRejection);
 
 return () => {
  window.removeEventListener('error', handleGlobalError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
 };
 }, []);

 // Load user profile when currentUser changes
 useEffect(() => {
 const loadProfile = async () => {
  const userId = currentUser?.uid || currentUser?.id;
  console.log('🔄 Loading profile for user:', userId, 'db connected:', !!db);

  if (userId) {
  try {
   // Use cloud sync method if available and database is connected
   const profile = await UserProfile.getProfile(userId, db);
   console.log('✅ Profile loaded with cloud sync:', profile);

   // Check if username is missing from profile
   if (!profile?.username && db) {
   console.log('⚠️ Username missing from profile, checking Firestore...');
   const existingUsername = await UserRegistry.getCurrentUserUsername(userId, db);

   if (existingUsername) {
    console.log('✅ Found existing username in Firestore:', existingUsername);
    // Save it back to the local profile
    profile.username = existingUsername;
    await UserProfile.saveProfile(userId, { username: existingUsername }, db);
   } else {
    console.log('⚠️ No username found in Firestore for this user');
   }
   }

   setUserProfile(profile);
  } catch (error) {
   console.error('❌ Profile load failed:', error);
   setUserProfile(null);
  }
  } else {
  console.log('❌ No userId, clearing profile');
  setUserProfile(null);
  }
 };

 if (currentUser) {
  loadProfile();
 }
 }, [currentUser, db]);

 
 // --- Core Functions ---
 const createNewBill = async () => {
 console.log("=== 🚀 CREATE NEW BILL FUNCTION CALLED ===");
 console.log("Current user in createNewBill:", currentUser);
 console.log("Current user id:", currentUser?.id);
 console.log("Current user type:", typeof currentUser);
 console.log("Scanned data exists:", !!scannedData);
 setIsLoading(true);
 setLoadingState({ 
  message: null, 
  subtitle: "Running the numbers and whatnot" 
 });
 
 // Check if user is authenticated first
 console.log('🔍 Authentication check - currentUser:', currentUser);
 console.log('🔍 Authentication check - currentUser.id:', currentUser?.id);
 console.log('🔍 Authentication check - !currentUser:', !currentUser);
 console.log('🔍 Authentication check - !currentUser.id:', !currentUser?.id);
 
 if (!currentUser || !currentUser.id) {
  console.error("❌ AUTHENTICATION FAILED");
  console.error("currentUser:", currentUser);
  console.error("currentUser.id:", currentUser?.id);
  setErrorMessage("Authentication issue. Please refresh the page and try again.");
  setIsLoading(false);
  return;
 }
 
 console.log('🚀 PASSED AUTHENTICATION CHECK, CONTINUING...');
 
 // Use mobile-friendly prompts instead of window.prompt
 setPendingBillCreation(true);
 setIsLoading(false); // Stop loading while waiting for user input
 
 // Set up venue name with AI suggestion
 const aiVenueName = scannedData.venueName || 'Unknown Restaurant';
 setTempVenueName(aiVenueName);
 
 // Check if user already has a display name saved
 const userId = currentUser?.uid || currentUser?.id;
 console.log('🔍 BILL CREATION - CHECKING FOR SAVED NAME. userId:', userId);
 const userProfile = userId ? await UserProfile.getProfile(userId, db) : null;
 console.log('🔍 BILL CREATION - userProfile:', userProfile);
 
 if (userProfile?.displayName?.trim()) {
  // User has a saved display name, use it automatically
  console.log('Using saved display name:', userProfile.displayName);
  setTempUserName(userProfile.displayName);
  setShowVenuePrompt(true);
 } else {
  // No saved name, prompt for it
  console.log('No saved display name found, showing name prompt');
  setShowNamePrompt(true);
 }
 return; // Exit here, will continue after user inputs
 };

 // Continue bill creation after user inputs name and venue
 const continueBillCreation = async (userName, venueName) => {
 console.log("Continuing bill creation with:", { userName, venueName });
 
 setIsLoading(true);
 setLoadingState({ 
  message: null, 
  subtitle: "Running the numbers and whatnot" 
 });
 
 if (!scannedData) {
  console.error("No scanned data available!");
  setErrorMessage("No receipt data found. Please scan again.");
  setIsLoading(false);
  return;
 }
 
 const ADJECTIVES = ['FUNNY', 'COOL', 'HAPPY', 'NICE', 'FAST', 'SLOW', 'BIG', 'SMALL'];
 const NOUNS = ['TACO', 'PIZZA', 'BURGER', 'LIME', 'LEMON', 'APPLE', 'ORANGE'];
 const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
 const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
 const num = Math.floor(Math.random() * 90) + 10;
 const newBillId = `${adj}-${noun}-${num}`;
 
 console.log("Generated bill ID:", newBillId);

 // Create a clean user object for Firestore
 const userWithAvatar = {
  id: currentUser.id,
  name: userName.trim(),
  avatar: getAvatarUrl(currentUser.id, userName.trim(), 2),
  profilePicture: userProfile?.profilePicture || null,
  paid: 0,
  phoneNumber: currentUser.phoneNumber || null
 };
 
 console.log("User object:", userWithAvatar);
 
 const newBill = {
  id: newBillId,
  venueName: venueName.trim(),
  items: scannedData.items || [],
  tax: scannedData.tax || 0,
  tip: scannedData.tip || 0,
  people: [userWithAvatar],
  participantIds: [currentUser.id],
  assignments: {},
  hostId: currentUser.id,
  receiptImage: scannedData.receiptImage || '',
  createdAt: new Date().toISOString(),
 };
 
 console.log("New bill object:", newBill);
 
 try {
  console.log("Attempting to save to Firebase...");
  console.log("Firebase config appId:", appId);
  console.log("Document path:", `artifacts/${appId}/public/data/bills/${newBillId}`);
  console.log("Current auth user:", auth.currentUser);
  
  // Save to Firestore with correct database
  console.log("=== SAVING TO FIRESTORE dev-database ===");
  await setDoc(doc(db, `artifacts/${appId}/public/data/bills`, newBillId), newBill);
  console.log("=== BILL SAVED TO FIRESTORE SUCCESSFULLY ===");

  // Set billData locally so AddPeopleScreen renders immediately
  // (don't wait for the Firestore listener round-trip)
  setBillData(newBill);
  setBillId(newBillId);
  const updatedBills = addRecentBill(newBillId);
  setRecentBills(updatedBills);
  setScreen('addPeople');
  console.log("=== NAVIGATING TO ADDPEOPLE SCREEN ===");
 } catch (error) {
  console.error("Error creating bill:", error);
  console.error("Error details:", error.message, error.code);
  console.error("Error stack:", error.stack);
  
  // More specific error messages
  if (error.code === 'permission-denied') {
   setErrorMessage("Permission denied. Please check Firestore rules.");
  } else if (error.code === 'unauthenticated') {
   setErrorMessage("Authentication issue. Please refresh and try again.");
  } else if (error.message?.includes('fetch')) {
   setErrorMessage("Network error. Please check your connection.");
  } else {
   setErrorMessage(`Could not create the bill: ${error.message}`);
  }
 } finally {
  setIsLoading(false);
  // Clear the pending state
  setPendingBillCreation(false);
 }
 };

 // Handlers for mobile-friendly prompts
 const handleNamePromptConfirm = (userName, venmoUsername) => {
 setTempUserName(userName);
 setShowNamePrompt(false);
 setShowVenuePrompt(true);

 // Save Venmo username to profile if provided
 if (venmoUsername) {
  const userId = currentUser?.uid || currentUser?.id;
  if (userId) {
  UserProfile.saveProfile(userId, { venmoUsername }, db);
  console.log('Venmo username saved from name prompt:', venmoUsername);
  }
 }
 };

 const handleNamePromptCancel = () => {
 setShowNamePrompt(false);
 setPendingBillCreation(false);
 setTempUserName('');
 setTempVenueName('');
 };

 const handleVenuePromptConfirm = (venueName) => {
 setTempVenueName(venueName);
 setShowVenuePrompt(false);
 // Continue with bill creation
 continueBillCreation(tempUserName, venueName);
 };

 const handleVenuePromptCancel = () => {
 setShowVenuePrompt(false);
 setPendingBillCreation(false);
 setTempUserName('');
 setTempVenueName('');
 };

 const joinBill = async (idToJoin, options = {}) => {
 try {
  setIsLoading(true);
  
  // Handle combined bills differently - they're passed as full objects
  if (options.isCombined && typeof idToJoin === 'object') {
  setLoadingState({ 
   message: null, 
   subtitle: "Setting up combined bill" 
  });
  
  // Format combined bill data for ScanAndVerifyScreen
  const scannedDataFormat = {
   items: idToJoin.items,
   tax: idToJoin.tax || 0,
   tip: idToJoin.tip || 0,
   receiptImage: idToJoin.receiptImage,
   venueName: idToJoin.venueName,
   // Store the combined bill metadata for later use
   isCombined: true,
   originalBills: idToJoin.originalBills
  };
  
  console.log('=== SETTING COMBINED BILL SCANNED DATA ===', scannedDataFormat);
  
  // Set the scanned data format for the scan screen
  setScannedData(scannedDataFormat);
  setBillId(idToJoin.id);
  // Also store the original combined bill data for reference
  setBillData(idToJoin);
  setScreen('scanAndVerify');
  setIsLoading(false);
  return;
  }
  
  setLoadingState({ 
  message: null, 
  subtitle: "Finding your friends' bill" 
  });
 const docRef = doc(db, `artifacts/${appId}/public/data/bills`, idToJoin.toUpperCase());
 console.log('Attempting to fetch bill from path:', `artifacts/${appId}/public/data/bills/${idToJoin.toUpperCase()}`);
 try {
  const doc = await getDoc(docRef);
  console.log('Bill document fetch result:', doc.exists() ? 'Found' : 'Not found');
  if (doc.exists()) {
  const bill = doc.data();
  console.log('Bill data loaded:', bill);
  const userExists = bill.people.some(p => p.id === currentUser.id);
  console.log('User already in bill:', userExists);
  
  if (!userExists) {
   // Try to get saved display name first
   const userId = currentUser?.uid || currentUser?.id;
   const userProfile = userId ? await UserProfile.getProfile(userId, db) : null;
   let userName = userProfile?.displayName?.trim();
   
   // If no saved name, prompt for it
   if (!userName) {
   userName = window.prompt("Enter your name:", "Guest");
   }
   
   if (userName) {
   const color = AVATAR_COLORS[(bill.people.length - 1) % AVATAR_COLORS.length];
   const joinerProfile = await UserProfile.getProfile(userId, db);
   const newUser = {
    id: currentUser.id,
    name: userName,
    avatar: getAvatarUrl(currentUser.id, userName, bill.people.length - 1),
    profilePicture: joinerProfile?.profilePicture || null,
    paid: 0,
    phoneNumber: currentUser.phoneNumber || null
   };
   console.log('Adding new user to bill:', newUser);
   await updateDoc(docRef, {
    people: arrayUnion(newUser),
    participantIds: arrayUnion(newUser.id)
   });
   // Update the bill data with the new user
   bill.people.push(newUser);
   bill.participantIds = Array.from(new Set([...(bill.participantIds || []), newUser.id]));
   }
  }
  
  // Set both billId and billData
  setBillId(idToJoin.toUpperCase());
  setBillData(bill);
  console.log('Setting screen to addPeople with billData:', bill);
  
  const updatedBills = addRecentBill(idToJoin.toUpperCase());
  setRecentBills(updatedBills);
  setIsLoading(false);
  setScreen('addPeople');
  } else {
  setErrorMessage("Bill not found. The link may be invalid or expired.");
  setIsLoading(false);
  }
 } catch (error) {
  console.error("Error joining bill:", error);
  setErrorMessage("Could not join the bill.");
  setIsLoading(false);
  setScreen('getStarted');
 }
 } catch (error) {
  console.error("joinBill function error:", error);
  setErrorMessage("Could not join the bill.");
  setIsLoading(false);
 setScreen('getStarted');
 }
};

const promptSignInForReceiptAction = (message = "Please sign in before scanning a receipt.") => {
 setIsStartBillModalVisible(false);
 setErrorMessage(message);
 setShowPhoneAuth(true);
 setPhoneAuthStep('phone');
};

const ensureSignedInForReceiptAction = (message) => {
 if (currentUser?.id || currentUser?.uid) {
  return true;
 }

 promptSignInForReceiptAction(message);
 return false;
};

// Parse a Toast receipt URL and load it into the verify screen
const handleToastImport = async (url) => {
 if (!ensureSignedInForReceiptAction("Please sign in before importing a receipt link.")) {
  return;
 }

 setIsLoading(true);
 setErrorMessage('');
 setIsStartBillModalVisible(false);
 try {
  const fetchToastReceiptFn = httpsCallable(functions, 'fetchToastReceipt');
  const result = await fetchToastReceiptFn({ url });
  const { venueName, items, tax, tip } = result.data;
  const itemsWithIds = items.map((item, i) => ({ quantity: 1, ...item, id: Date.now() + i }));
  setScannedData({ venueName, items: itemsWithIds, tax, tip, receiptImage: null, toastUrl: url });
  setScreen('scanAndVerify');
 } catch (err) {
  console.error('Toast import error:', err);
  if (err.code === 'functions/unauthenticated') {
   promptSignInForReceiptAction("Please sign in before importing a receipt link.");
  } else {
   setErrorMessage('Could not import that receipt. Make sure the Toast link is correct and try again.');
  }
  setScreen('getStarted');
 } finally {
  setIsLoading(false);
 }
};

// UPDATED analyzeReceipt function to use Firebase Function
const analyzeReceipt = async (base64ImageData) => {
 if (!ensureSignedInForReceiptAction("Please sign in before scanning a receipt.")) {
  return;
 }

 console.log("🔍 Starting receipt analysis...");
 setIsLoading(true);
 setErrorMessage('');

 try {
  // Compress image BEFORE sending to Gemini (shrinks ~8MB → ~1MB, much faster upload + inference)
  console.log('🖼️ Compressing image before analysis...');
  const compressedForAI = await compressImage(base64ImageData, 1200, 0.6);
  console.log('✅ Compressed for AI, size:', compressedForAI.length);

  // Also start the storage-quality compression in parallel
  const storageCompressionPromise = compressImage(base64ImageData, 800, 0.7);

  const analyzeReceiptFunction = httpsCallable(functions, 'analyzeReceipt');

  console.log('🚀 Calling Firebase Function...');
  const timeoutPromise = new Promise((_, reject) => {
   setTimeout(() => reject(new Error('Function call timed out after 60 seconds')), 60000);
  });

  const functionPromise = analyzeReceiptFunction({
   imageBase64: compressedForAI
  });

  const [result, compressedImage] = await Promise.all([
   Promise.race([functionPromise, timeoutPromise]),
   storageCompressionPromise
  ]);
  console.log('📥 Firebase Function response received:', result.data?.success);

  if (result.data.success) {
   console.log("✅ Receipt analysis successful!", result.data);
   const parsedData = result.data.data;
   const subtotal = parsedData.subtotal || parsedData.items.reduce((sum, item) => sum + item.price, 0);

   const scannedDataObject = {
    venueName: parsedData.venueName || 'Unknown Restaurant',
    items: parsedData.items.map((item, index) => ({ ...item, id: Date.now() + index })),
    tax: parsedData.tax || 0,
    tip: parseFloat((subtotal * 0.20).toFixed(2)),
    receiptImage: `data:image/jpeg;base64,${compressedImage}`
   };
   setScannedData(scannedDataObject);
   setScreen('scanAndVerify');
  } else {
   throw new Error(result.data.error || "Failed to analyze receipt");
  }
 } catch (error) {
  console.error("❌ Error analyzing receipt:", error);
  console.error("Error details:", {
   code: error.code,
   message: error.message,
   details: error.details
  });
  
  // Better error messages based on error codes
  if (error.code === 'functions/resource-exhausted') {
   setErrorMessage("Please wait a few seconds before scanning another receipt");
  } else if (error.code === 'functions/unauthenticated') {
   promptSignInForReceiptAction("Please sign in before scanning a receipt.");
  } else if (error.code === 'functions/internal') {
   if (error.message && error.message.includes("overloaded")) {
    setErrorMessage("AI service is busy right now. Please try again in a few minutes!");
   } else {
    setErrorMessage("Sorry, I couldn't read that receipt. Please try a clearer image.");
   }
  } else if (error.message && error.message.includes("imageBase64")) {
   setErrorMessage("Image format issue. Please try again.");
  } else if (error.message && error.message.includes("overloaded")) {
   setErrorMessage("AI service is busy right now. Please try again in a few minutes!");
  } else {
   setErrorMessage("Sorry, something went wrong. Please try again.");
  }
  setScreen('getStarted');
 } finally {
  setIsLoading(false);
 }
};

const handleTakePhotoStart = () => {
 if (!ensureSignedInForReceiptAction("Please sign in before scanning a receipt.")) {
  return;
 }

 setIsStartBillModalVisible(false);
 setScreen('camera');
};

const handleReScanRequest = () => {
 setIsReScanning(true);
 setIsStartBillModalVisible(true);
};

 const updateBillFromScan = async () => {
 setIsLoading(true);
 try {
  await updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), {
   items: scannedData.items,
   tax: scannedData.tax,
   tip: scannedData.tip,
    receiptImage: scannedData.receiptImage,
   assignments: {},
  });
  setIsReScanning(false);
  setScreen('assignItems');
 } catch (error) {
  console.error("Error updating bill from re-scan:", error);
  setErrorMessage("Could not update the bill. Please try again.");
 } finally {
  setIsLoading(false);
 }
};

const handleReceiptUpload = (imageSrc) => {
 if (!ensureSignedInForReceiptAction("Please sign in before scanning a receipt.")) {
  return;
 }

 console.log("📸 Photo captured, starting analysis...");
 const base64Data = imageSrc.split(',')[1];
 analyzeReceipt(base64Data);
};
 
const handleFileSelect = (event) => {
 const file = event.target.files[0];
 if (file && file.type.startsWith("image/")) {
  if (!ensureSignedInForReceiptAction("Please sign in before scanning a receipt.")) {
   event.target.value = '';
   return;
  }

  setIsStartBillModalVisible(false);
  const reader = new FileReader();
  reader.onload = (e) => handleReceiptUpload(e.target.result);
  reader.readAsDataURL(file);
 }
 };

 const resetApp = () => {
 setBillId(null);
 setBillData(null);
 setCalculationResultsFromHistory(null);
 setErrorMessage('');
 setScreen('getStarted');
 setIsReScanning(false);
 };

const saveBillToHistory = async (billData, results) => {
 console.log("=== SAVING BILL TO HISTORY ===", billData.id);
 const userId = currentUser?.uid || currentUser?.id;
 const history = await BillHistory.addToHistoryWithSync(userId, billData, results, db);
 console.log("=== BILL SAVED TO HISTORY, total items:", history.length);
 return history;
};

 const loadBillFromHistory = async (historyBill, options = {}) => {
 // If a bill ID string was passed, look up the full bill object first
 if (typeof historyBill === 'string') {
  const billId = historyBill;
  try {
  const userHistory = await BillHistory.getHistoryWithSync(currentUser?.id, db);
  const foundBill = userHistory.find(b => b.id === billId);
  if (foundBill) {
   historyBill = foundBill;
  } else {
   console.error('Bill not found in history:', billId);
   return;
  }
  } catch (error) {
  console.error('Failed to load bill from history:', error);
  return;
  }
 }

 // Handle combined settlements differently
 if (options.isCombinedSettlement || historyBill.isCombinedSettlement) {
  console.log('=== LOADING COMBINED SETTLEMENT ===', historyBill);
  
  // Set up combined settlement data for the final summary screen
  const combinedCalculationResults = {
  grandTotal: historyBill.total,
  breakdown: historyBill.combinedBreakdown.map(person => ({
   name: person.name,
   finalShare: person.totalOwed,
   paid: person.totalPaid,
   balance: person.finalBalance,
   items: [], // Not applicable for combined settlements
   venueBreakdown: person.venueBreakdown // Show breakdown by venue
  })),
  transactions: historyBill.summary?.transactions || [], // Use transactions from combined settlement
  isCombinedSettlement: true
  };
  
  // Create bill data structure for display
  const combinedBillData = {
  id: historyBill.id,
  venueName: historyBill.venueName,
  items: [], // Not applicable for combined settlements
  tax: 0,
  tip: 0,
  receiptImage: null,
  people: historyBill.participants.map(name => ({
   id: name.toLowerCase().replace(/\s+/g, ''),
   name: name,
   paid: 0 // Will be shown in breakdown
  })),
  assignments: {},
  isCombinedSettlement: true,
  originalBills: historyBill.originalBills
  };
  
  setBillData(combinedBillData);
  setBillId(historyBill.id);
  setCalculationResultsFromHistory(combinedCalculationResults);
  setScreen('finalSummary');
  return;
 }
 
 // Load bill data and navigate directly to final summary (all settled page)
 const reconstructedBillData = {
  id: historyBill.id,
  venueName: historyBill.venueName,
  items: historyBill.billSnapshot?.items || [],
  tax: historyBill.billSnapshot?.tax || 0,
  tip: historyBill.billSnapshot?.tip || 0,
  receiptImage: historyBill.billSnapshot?.receiptImage || null,
  people: historyBill.summary?.breakdown?.map(person => ({
  id: person.name.toLowerCase().replace(/\s+/g, ''),
  name: person.name,
  paid: person.paid || 0
  })) || [],
  assignments: {} // This might be reconstructed from the history if needed
 };

 // Reconstruct calculation results from stored summary
 const reconstructedCalculationResults = {
  grandTotal: historyBill.total,
  breakdown: (historyBill.summary?.breakdown || []).map(person => ({
  ...person,
  items: person.items || [],
  itemTotal: person.itemTotal || 0,
  taxAndTipShare: person.taxAndTipShare || 0,
  id: person.name.toLowerCase().replace(/\s+/g, '')
  })),
  transactions: historyBill.summary?.transactions || [],
  error: false
 };

 setBillData(reconstructedBillData);
 setBillId(historyBill.id);
 // Store calculation results separately for the final summary screen
 setCalculationResultsFromHistory(reconstructedCalculationResults);
 setScreen('finalSummary');
 setIsReScanning(false);
 };

 const viewBillBreakdown = async (billId) => {
 // If billId is provided, find that specific bill and show its breakdown
 if (billId) {
  try {
  const userHistory = await BillHistory.getHistoryWithSync(currentUser?.id, db);
  const bill = userHistory.find(b => b.id === billId);
  if (bill) {
  // Check if bill has items data
  if (!bill.items || bill.items.length === 0) {
   setScreen('history');
   return;
  }
  
  // Reconstruct the bill data from history to show in final summary
  const mockBillData = {
   items: bill.items || [],
   people: bill.participants.map((name, index) => ({ id: `history-${index}`, name, paid: bill.total / bill.participants.length })),
   venueName: bill.venueName,
   receiptImage: bill.receiptImage,
   assignments: bill.assignments || {},
   tax: bill.tax || 0,
   tip: bill.tip || 0
  };
  setBillData(mockBillData);
  setBillId(bill.id);
  setScreen('finalSummary');
  return;
  }
  } catch (error) {
  console.error('Error loading bill history:', error);
  setScreen('history');
  return;
  }
 }
 // Otherwise just go to history screen
 setScreen('history');
 };

const handleSignIn = () => {
setShowPhoneAuth(true);
setPhoneAuthStep('phone');
};

// Send verification code via Twilio Verify (Cloud Function)
const sendVerificationCode = async (channel = 'sms') => {
 try {
  setErrorMessage('');
  setIsSendingCode(true);

  const formattedPhone = `+1${phoneNumber}`;
  console.log("Sending verification code via", channel, "to:", formattedPhone);

  const sendCode = httpsCallable(functions, 'sendVerificationCode');
  const result = await sendCode({
  phoneNumber: formattedPhone,
  channel: channel
  });

  if (result.data.success) {
  setDeliveryChannel(channel);
  setPhoneAuthStep('verify');
  console.log("Verification code sent successfully via", channel);
  }
 } catch (error) {
  console.error("Error sending verification code:", error);

  if (error.code === 'functions/resource-exhausted') {
  setErrorMessage("Too many attempts. Please wait a few minutes before trying again.");
  } else if (error.code === 'functions/invalid-argument') {
  setErrorMessage("Invalid phone number. Please check and try again.");
  } else if (error.message && error.message.includes('recently')) {
  setErrorMessage("Too many verification requests for this number. Please wait a bit and try again.");
  } else {
  setErrorMessage("Failed to send verification code. Please try again.");
  }
 } finally {
  setIsSendingCode(false);
 }
 };

 // Verify code via Twilio Verify + sign in with Firebase Custom Token
 const verifyCode = async () => {
 if (!verificationCode || !verificationCode.trim()) {
  setErrorMessage("Please enter the verification code");
  return;
 }

 try {
  setIsVerifyingCode(true);
  setErrorMessage('');

  const formattedPhone = `+1${phoneNumber}`;
  console.log("Verifying code for:", formattedPhone);

  const checkCode = httpsCallable(functions, 'checkVerificationCode');
  const result = await checkCode({
  phoneNumber: formattedPhone,
  code: verificationCode.trim(),
  });

  if (result.data.success) {
  console.log("Twilio verification successful, signing in with custom token...");

  // Sign into Firebase with the custom token
  await signInWithCustomToken(auth, result.data.token);
  console.log("Firebase sign-in successful with UID:", result.data.uid);

  const userId = result.data.uid;
  const userPhoneNumber = result.data.phoneNumber;

  // STEP 1: Migrate data from old UID FIRST (before any profile lookups)
  if (result.data.hasMigration) {
   try {
   const migrationDoc = await getDoc(doc(db, 'user_migrations', userId));
   if (migrationDoc.exists()) {
    const { oldUid } = migrationDoc.data();
    console.log('Migrating user data from', oldUid, 'to', userId);
    await UserRegistry.migrateUserId(oldUid, userId, db);
   }
   } catch (migErr) {
   console.error('Migration failed:', migErr);
   }
  }

  // STEP 2: Now look up profile (will find migrated data if migration ran)
  try {
   const userProfile = await UserProfile.getProfile(userId, db);
   const displayName = userProfile?.displayName || '';
   const venmoUsername = userProfile?.venmoUsername || null;

   const existingUsername = await UserRegistry.getCurrentUserUsername(userId, db);

   if (existingUsername) {
   console.log("User already exists with username:", existingUsername);
   if (!userProfile?.username) {
    await UserProfile.saveProfile(userId, { username: existingUsername }, db);
   }
   } else {
   console.log("Creating new user record in Firestore...");
   const autoUsername = await UserRegistry.createUserRecord(userId, userPhoneNumber, displayName, db, venmoUsername);
   if (autoUsername) {
    await UserProfile.saveProfile(userId, { username: autoUsername }, db);
    console.log("User record created with username:", autoUsername);
   }
   }
  } catch (regError) {
   console.error("Error creating user record:", regError);
  }

  // Clean up modal state
  setShowPhoneAuth(false);
  setPhoneAuthStep('phone');
  setPhoneNumber('');
  setVerificationCode('');
  setDeliveryChannel('sms');

  // Store auth timestamps
  localStorage.setItem('lastSuccessfulAuth', Date.now().toString());
  localStorage.setItem('authenticatedPhoneNumber', result.data.phoneNumber);
  }
 } catch (error) {
  console.error("Error verifying code:", error);

  if (error.code === 'functions/unauthenticated') {
  setErrorMessage("Hmm that code didn't work :/ Want us to send another?");
  } else if (error.code === 'functions/not-found') {
  setErrorMessage("Code expired. Please request a new one.");
  } else if (error.code === 'functions/resource-exhausted') {
  setErrorMessage("Too many attempts. Please wait a few minutes.");
  } else {
  setErrorMessage(`Verification failed: ${error.message}`);
  }
 } finally {
  setIsVerifyingCode(false);
 }
 };

 const resendCode = async () => {
 setErrorMessage('');
 setVerificationCode('');
 setPhoneAuthStep('phone');
 };

 const cancelPhoneAuth = () => {
 // Only allow closing the modal if user is already authenticated
 // (e.g., re-authenticating from settings). Otherwise, phone auth is mandatory.
 if (currentUser) {
  setShowPhoneAuth(false);
 }
 setPhoneAuthStep('phone');
 setPhoneNumber('');
 setVerificationCode('');
 setDeliveryChannel('sms');
 setErrorMessage('');
 };

 const handleSignOut = async () => {
 console.log("Sign out button clicked!");
 console.log("Current auth user before sign out:", auth.currentUser);
 try {
  console.log("Attempting to sign out...");
  
  // Clear authentication-related localStorage data first
  localStorage.removeItem('lastSuccessfulAuth');
  localStorage.removeItem('authenticatedPhoneNumber');
  console.log("Authentication data cleared from localStorage");
  
  // Sign out from Firebase
  await signOut(auth);
  console.log("Firebase signOut completed");
  
  // Immediately clear current user to prevent fallback logic
  setCurrentUser(null);
  console.log("Current user cleared");
  
  // Navigate to get started screen 
  setScreen('getStarted');
  console.log("Redirected to getStarted screen");
  
 } catch (error) {
  console.error("Error during sign out process:", error);
  
  // Even if sign out fails, clear everything and redirect
  localStorage.removeItem('lastSuccessfulAuth');
  localStorage.removeItem('authenticatedPhoneNumber');
  setCurrentUser(null);
  setScreen('getStarted');
 }
 };
 
 const addItem = (name, price) => {
 if (!name || !price || !billId || !billData) return;
 const itemPrice = parseFloat(price);
 const newItem = { id: Date.now(), name: name.trim(), price: itemPrice, quantity: 1 };

 // Update the payer's amount to include the new item
 const payer = billData.people.find(p => p.paid > 0);
 const updatedPeople = payer
  ? billData.people.map(p => p.id === payer.id ? { ...p, paid: p.paid + itemPrice } : p)
  : billData.people;

 updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), {
  items: arrayUnion(newItem),
  people: updatedPeople
 });
 };

 const removeItem = (itemToRemove) => {
  if (!itemToRemove || !billId) return;
  const newAssignments = { ...billData.assignments };
  delete newAssignments[itemToRemove.id];
  updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), {
   items: arrayRemove(itemToRemove),
   assignments: newAssignments
  });
 };

 const addPerson = (name, friendData = null) => {
  const personId = friendData?.odId || Date.now().toString();
  const newPerson = {
  id: personId,
  name: name.trim(),
  avatar: friendData?.profilePicture || getAvatarUrl(personId, name.trim(), billData.people.length - 1),
  paid: 0,
  phoneNumber: friendData?.phoneNumber || null
  };
  updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), {
   people: arrayUnion(newPerson),
   participantIds: arrayUnion(personId)
  });
 };
 
 const removePerson = (personToRemove) => updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), {
  people: arrayRemove(personToRemove),
  participantIds: arrayRemove(personToRemove.id)
 });
 
 const setPaidAmount = (personId, amount) => {
  const newPeople = billData.people.map(p => p.id === personId ? { ...p, paid: amount } : p);
  updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), { people: newPeople });
 };
 
 // FIXED assignItem function
 const assignItem = (itemId, personId) => {
  if (!billData || !billId) return;
  
  const currentAssignees = billData.assignments[itemId] || [];
  const newAssignees = currentAssignees.includes(personId)
   ? currentAssignees.filter(id => id !== personId)
   : [...currentAssignees, personId];
  
  // Create a new assignments object with the updated value
  const newAssignments = {
   ...billData.assignments,
   [itemId]: newAssignees
  };
  
  // Update Firestore
  updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), { 
   assignments: newAssignments 
  }).catch(error => {
   console.error("Error updating assignments:", error);
   setErrorMessage("Could not update item assignment. Please try again.");
  });
 };
 
 const mergeItems = (droppedItemId, selectedItemId = null) => {
  // For mobile: selectedItemId is the first item, droppedItemId is the target
  // For desktop: draggedItemId is from state, droppedItemId is the target
  const sourceItemId = selectedItemId || draggedItemId;
  
  if (sourceItemId === null || sourceItemId === droppedItemId) return;
  
  const draggedItem = billData.items.find(i => i.id === sourceItemId);
  const droppedItem = billData.items.find(i => i.id === droppedItemId);
  if (!draggedItem || !droppedItem) return;

  const draggedAssignments = billData.assignments[sourceItemId] || [];
  const droppedAssignments = billData.assignments[droppedItemId] || [];
  const combinedAssignments = Array.from(new Set([...draggedAssignments, ...droppedAssignments]));
  
  const newItemId = Date.now();
  const newItems = billData.items
   .map(item => item.id === droppedItemId ? { id: newItemId, name: `${droppedItem.name} + ${draggedItem.name}`, price: parseFloat((droppedItem.price + draggedItem.price).toFixed(2)) } : item)
   .filter(item => item.id !== sourceItemId);

  const newAssignments = { ...billData.assignments };
  delete newAssignments[sourceItemId];
  delete newAssignments[droppedItemId];
  newAssignments[newItemId] = combinedAssignments;

  // Store merge in history for multi-step undo
  const mergeStep = {
  id: Date.now(),
  beforeItems: billData.items,
  beforeAssignments: billData.assignments,
  afterItems: newItems,
  afterAssignments: newAssignments,
  mergedItemName: `${droppedItem.name} + ${draggedItem.name}`
  };
  
  // Add to merge history (keep last 10 merges)
  setMergeHistory(prev => [mergeStep, ...prev.slice(0, 9)]);

  updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), { items: newItems, assignments: newAssignments });
  setDraggedItemId(null);
 };
 
 const undoMergeStep = () => {
 if (mergeHistory.length === 0 || !billData) return;
 
 // Get the most recent merge step
 const lastMergeStep = mergeHistory[0];
 
 // Restore to the state before this merge
 const restoredItems = lastMergeStep.beforeItems;
 const restoredAssignments = lastMergeStep.beforeAssignments;
 
 // Remove this step from history
 setMergeHistory(prev => prev.slice(1));
 
 updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), { 
  items: restoredItems, 
  assignments: restoredAssignments 
 });
 };

 const calculationResults = useMemo(() => {
 console.log("=== CALCULATION DEBUG START ===");
 console.log("billData:", billData);
 console.log("screen:", screen);
 
 if (!billData) {
 console.log("No billData, returning null");
 return null;
 }
 
 console.log("billData.items:", billData.items);
 console.log("billData.people:", billData.people);
 console.log("billData.assignments:", billData.assignments);
 
 const { items, tax, tip, people, assignments } = billData;
 const subtotal = items.reduce((sum, item) => sum + item.price, 0);
 const totalBill = subtotal + tax + tip;
 const totalPaid = people.reduce((sum, p) => sum + p.paid, 0);

 // No mismatch error — math adjusts naturally when items are added/removed

 let balances = {};
 people.forEach(person => {
 balances[person.id] = {
  id: person.id,
  name: person.name,
  paid: person.paid,
  balance: person.paid,
  items: [],
  itemTotal: 0,
  taxAndTipShare: 0,
  finalShare: 0,
 };
 });

 Object.keys(assignments).forEach(itemId => {
  const item = items.find(i => i.id.toString() === itemId.toString());
  const validAssignees = (assignments[itemId] || []).filter(personId => balances[personId]);
  
  if (item && validAssignees.length > 0) {
  const pricePerPerson = item.price / validAssignees.length;
  validAssignees.forEach(personId => {
   balances[personId].items.push({ ...item, price: pricePerPerson });
   balances[personId].itemTotal += pricePerPerson;
  });
  }
 });

 people.forEach(person => {
  const personItemTotal = balances[person.id].itemTotal;
  let finalShare = personItemTotal;
  let taxAndTipShare = 0;
  if (subtotal > 0) {
  const proportion = personItemTotal / subtotal;
  taxAndTipShare = (tax * proportion) + (tip * proportion);
  finalShare += taxAndTipShare;
  }
  balances[person.id].finalShare = finalShare;
  balances[person.id].taxAndTipShare = taxAndTipShare;
  balances[person.id].balance -= finalShare;
 });

 const tempBalances = JSON.parse(JSON.stringify(Object.values(balances)));
 const debtors = tempBalances.filter(p => p.balance < 0).sort((a, b) => a.balance - b.balance);
 const creditors = tempBalances.filter(p => p.balance > 0).sort((a, b) => b.balance - a.balance);
 const transactions = [];

 let i = 0, j = 0;
 while (i < debtors.length && j < creditors.length) {
  const debtor = debtors[i];
  const creditor = creditors[j];
  const amount = Math.min(-debtor.balance, creditor.balance);
  transactions.push({ from: debtor.name, to: creditor.name, amount });
  debtor.balance += amount;
  creditor.balance -= amount;
  if (Math.abs(debtor.balance) < 0.01) i++;
  if (Math.abs(creditor.balance) < 0.01) j++;
 }

 return { grandTotal: totalBill, breakdown: Object.values(balances), transactions };
 }, [billData, screen]);

 // Save to history and write activity when final results are calculated
 useEffect(() => {
 const saveToHistory = async () => {
  if (screen === 'finalSummary' && calculationResults && !calculationResults.error && billData && typeof saveBillToHistory === 'function') {
  // Skip saving combined settlements to history as they're temporary views
  if (!billData.isCombinedSettlement && !calculationResults.isCombinedSettlement) {
   await saveBillToHistory(billData, calculationResults);

   // Write to shared activities collection for social feed
   const userId = currentUser?.uid || currentUser?.id;
   if (userId && billData.id) {
   try {
    await setDoc(doc(db, `artifacts/${appId}/public/data/activities`, billData.id), {
    userId,
    userName: userProfile?.displayName || userProfile?.username || 'Unknown',
    userAvatar: userProfile?.profilePicture || null,
    type: 'split',
    venue: billData.venueName || 'Unknown Restaurant',
    total: calculationResults.grandTotal || 0,
    participants: billData.people?.map(p => p.name) || [],
    participantCount: billData.people?.length || 1,
    timestamp: Date.now(),
    rating: null,
    review: null,
    wouldReturn: null,
    tags: [],
    billId: billData.id,
    });
    console.log('Activity written to shared collection:', billData.id);
   } catch (err) {
    console.error('ACTIVITY WRITE ERROR:', err.message, err);
   }
   }
  }
  }
 };
 saveToHistory();
 }, [screen, calculationResults, billData]);

 // Screens that should have persistent navigation (only when truly signed in)
 const mainScreens = ['getStarted', 'activity', 'history', 'profile'];
 const isSignedIn = !!currentUser;
 const showBottomNav = mainScreens.includes(screen) && isSignedIn;

 const renderScreen = () => {
 if (isLoading) return <LoadingScreen message={loadingState.message} subtitle={loadingState.subtitle} />;

 const renderMainContent = () => {
  switch (screen) {
  case 'getStarted':
   return <ActivityScreen currentUser={currentUser} userProfile={userProfile} onBack={() => setScreen('getStarted')} onHome={() => setScreen('getStarted')} onStartBill={loadBillFromHistory} onSignIn={handleSignIn} setScreen={setScreen} setIsStartBillModalVisible={setIsStartBillModalVisible} setIsAddFriendModalVisible={setIsAddFriendModalVisible} friends={friends} setFriends={setFriends} db={db} setIsSearchModalVisible={setIsSearchModalVisible} joinBill={joinBill} />;
  case 'history':
   return (
   <Suspense fallback={<LazyScreenLoader />}>
    <HistoryScreen 
    onBack={() => setScreen('getStarted')} 
    onLoadBill={loadBillFromHistory}
    onJoinBill={joinBill}
    onHome={() => setScreen('getStarted')}
    currentUser={currentUser}
    db={db}
    />
   </Suspense>
   );
  case 'profile':
   return (
   <ProfileScreen 
    currentUser={currentUser}
    onBack={() => setScreen('getStarted')} 
    onSignOut={handleSignOut}
    onHome={() => setScreen('getStarted')}
    setScreen={setScreen}
    userProfile={userProfile}
    setUserProfile={setUserProfile}
    onStartBill={loadBillFromHistory}
   />
   );
  case 'saved':
   return (
   <SavedScreen 
    currentUser={currentUser}
    onBack={() => setScreen('getStarted')} 
    onHome={() => setScreen('getStarted')}
    onStartBill={loadBillFromHistory}
    savedPosts={savedPosts}
    userActivityData={activityData}
    loadSavedPosts={loadSavedPosts}
   />
   );
  case 'camera':
   return (
   <CameraScreen onCapture={handleReceiptUpload} onCancel={resetApp} onHome={resetApp} />
   );
  case 'scanAndVerify':
   return (
   <ScanAndVerifyScreen 
    scannedData={scannedData} 
    setScannedData={setScannedData} 
    onConfirm={isReScanning ? updateBillFromScan : createNewBill} 
    onCancel={() => {
     if (isReScanning) {
      setIsReScanning(false);
      setScreen('assignItems');
     } else {
      resetApp();
     }
    }}
    onRescan={analyzeReceipt}
   />
   );
  case 'addPeople':
   if (!billData) return <LoadingScreen />;
   return (
   <AddPeopleScreen people={billData.people} onAddPerson={addPerson} onRemovePerson={removePerson} onSetPaidAmount={setPaidAmount} onContinue={() => setScreen('assignItems')} onBack={() => setScreen('scanAndVerify')} onCancel={resetApp} billTotal={calculationResults.grandTotal} billId={billId} currentUser={currentUser} userProfile={userProfile} venueName={billData.venueName} receiptImage={billData.receiptImage} friends={friends} db={db} />
   );
  case 'assignItems':
   if (!billData) return <LoadingScreen />;
   return (
   <AssignItemsScreen items={billData.items} people={billData.people} assignments={billData.assignments} onAssign={assignItem} onAssignToMe={(itemId) => assignItem(itemId, currentUser.id)} onSplitEvenly={(itemId) => {
    // Update to use the new assignments object approach
    const newAssignments = {
     ...billData.assignments,
     [itemId]: billData.people.map(p => p.id)
    };
    updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billId), { 
     assignments: newAssignments 
    });
   }} onCalculate={() => setScreen('finalSummary')} onBack={() => setScreen('addPeople')} onCancel={resetApp} draggedItemId={draggedItemId} setDraggedItemId={setDraggedItemId} onMergeItems={mergeItems} billId={billId} billData={billData} onReScan={handleReScanRequest} onAddItem={addItem} onRemoveItem={removeItem} mergeHistory={mergeHistory} onUndo={undoMergeStep} currentUser={currentUser} userProfile={userProfile} />
  );
  case 'finalSummary':
  console.log('Rendering finalSummary screen, billData:', billData ? 'EXISTS' : 'NULL');
  console.log('calculationResults:', calculationResults ? 'EXISTS' : 'NULL');
  if (!billData) {
   console.log('No billData, showing loading screen');
   return <LoadingScreen />;
  }
  // Use calculation results from history if available (for loaded bills), otherwise use computed results
  const resultsToUse = calculationResultsFromHistory || calculationResults;
  return (
   <FinalSummaryScreen results={resultsToUse} onStartNew={resetApp} onEdit={() => setScreen('assignItems')} currentUser={currentUser} billData={billData} />
  );
  default:
   // Redirect unknown screens to home (activity feed)
   setScreen('getStarted');
   return null;
  }
 };

 // For main screens, show content with persistent bottom nav
 if (showBottomNav) {
  return (
  <div className="relative flex flex-col min-h-screen">
   {/* Main Content */}
   <div className="flex-1 pb-20 md:pb-0">
   {renderMainContent()}
   </div>
   
   {/* Persistent Bottom Navigation */}
   <div className="fixed md:hidden bottom-0 left-0 right-0 z-50 border-t-2 border-black bg-white" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
    <div className="flex items-end justify-around px-2 pt-1" style={{
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
    }}>
    {/* Home */}
    <button
     className="flex flex-col items-center gap-0.5 py-2 px-4 active:scale-95 transition-all duration-100"
     onClick={() => {
     if (isStartBillModalVisible) setIsStartBillModalVisible(false);
     if (screen === 'getStarted') window.scrollTo({ top: 0, behavior: 'smooth' });
     else setScreen('getStarted');
     }}
     style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none', outline: 'none' }}
    >
     <div className={`w-10 h-10 flex items-center justify-center border-2 transition-all duration-100 ${
     screen === 'getStarted' && !isStartBillModalVisible
      ? 'border-black bg-[#FF6B35] text-white shadow-brutal-sm'
      : 'border-transparent text-[#6B7280]'
     }`}>
     <Home size={20} strokeWidth={2.5} />
     </div>
     <span className={`text-[10px] font-bold uppercase tracking-wide ${
     screen === 'getStarted' && !isStartBillModalVisible ? 'text-black' : 'text-[#6B7280]'
     }`}>Home</span>
    </button>

    {/* Scan - Primary Action */}
    <button
     className="flex flex-col items-center -mt-4 active:scale-95 transition-all duration-100"
     onClick={() => setIsStartBillModalVisible(true)}
     style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none', outline: 'none' }}
    >
     <div className={`w-14 h-14 flex items-center justify-center border-2 border-black transition-all duration-100 ${
     isStartBillModalVisible || screen === 'camera' || screen === 'scanAndVerify'
      ? 'bg-[#FF6B35] text-white shadow-brutal-sm'
      : 'bg-[#FFD700] text-black shadow-brutal hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-lg active:translate-x-[2px] active:translate-y-[2px] active:shadow-none'
     }`}>
     <Camera size={24} strokeWidth={2.5} />
     </div>
     <span className={`text-[10px] font-bold uppercase tracking-wide mt-0.5 ${
     isStartBillModalVisible || screen === 'camera' || screen === 'scanAndVerify' ? 'text-black' : 'text-[#6B7280]'
     }`}>Scan</span>
    </button>

    {/* History */}
    <button
     className="flex flex-col items-center gap-0.5 py-2 px-4 active:scale-95 transition-all duration-100"
     onClick={() => {
     if (isStartBillModalVisible) setIsStartBillModalVisible(false);
     setScreen('history');
     }}
     style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none', outline: 'none' }}
    >
     <div className={`w-10 h-10 flex items-center justify-center border-2 transition-all duration-100 ${
     screen === 'history' && !isStartBillModalVisible
      ? 'border-black bg-[#FF6B35] text-white shadow-brutal-sm'
      : 'border-transparent text-[#6B7280]'
     }`}>
     <Clock size={20} strokeWidth={2.5} />
     </div>
     <span className={`text-[10px] font-bold uppercase tracking-wide ${
     screen === 'history' && !isStartBillModalVisible ? 'text-black' : 'text-[#6B7280]'
     }`}>History</span>
    </button>

    {/* Profile */}
    <button
     className="flex flex-col items-center gap-0.5 py-2 px-4 active:scale-95 transition-all duration-100"
     onClick={() => {
     if (isStartBillModalVisible) setIsStartBillModalVisible(false);
     setScreen('profile');
     }}
     style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none', outline: 'none' }}
    >
     <div className={`w-10 h-10 flex items-center justify-center border-2 transition-all duration-100 ${
     screen === 'profile' && !isStartBillModalVisible
      ? 'border-black bg-[#FF6B35] text-white shadow-brutal-sm'
      : 'border-transparent text-[#6B7280]'
     }`}>
     {userProfile?.profilePicture ? (
      <div className="w-6 h-6 rounded-full overflow-hidden border-2 border-black">
      <img src={userProfile.profilePicture} alt="Profile" className="w-full h-full object-cover" />
      </div>
     ) : (
      <UserIcon size={20} strokeWidth={2.5} />
     )}
     </div>
     <span className={`text-[10px] font-bold uppercase tracking-wide ${
     screen === 'profile' && !isStartBillModalVisible ? 'text-black' : 'text-[#6B7280]'
     }`}>Profile</span>
    </button>
    </div>
   </div>
  </div>
  );
 }

 // For other screens (camera, bill flow, etc.), show without bottom nav
 return renderMainContent();
 };
 
 return (
 <>
  <style>{`
  .font-receipt { font-family: 'JetBrains Mono', monospace; }
  
  /* Disable text selection on draggable elements */
  [data-item-id] {
   -webkit-user-select: none;
   -moz-user-select: none;
   -ms-user-select: none;
   user-select: none;
   -webkit-touch-callout: none;
   -webkit-tap-highlight-color: transparent;
  }
  
  /* Allow text selection on input fields and expanded content */
  [data-item-id] input,
  [data-item-id] textarea,
  [data-item-id] .expanded-content {
   -webkit-user-select: text;
   -moz-user-select: text;
   -ms-user-select: text;
   user-select: text;
  }
  `}</style>
  <div className="min-h-screen bg-[#fdfaef] p-0 safe-area-top">
  <div className="w-full min-h-screen bg-[#fdfaef] flex flex-col font-receipt text-lg
      sm:text-lg md:text-lg lg:text-xl xl:text-xl">
   {isCancelModalVisible && <CancelModal onConfirm={resetApp} onDismiss={() => setIsCancelModalVisible(false)} />}
  {isStartBillModalVisible && <StartBillModal onTakePhoto={handleTakePhotoStart} onCancel={() => { console.log('Modal cancel clicked'); setIsStartBillModalVisible(false); }} onFileSelect={handleFileSelect} onToastImport={handleToastImport} />}
   {showPhoneAuth && (
   <PhoneAuthModal
    isVisible={showPhoneAuth}
    step={phoneAuthStep}
    phoneNumber={phoneNumber}
    setPhoneNumber={setPhoneNumber}
    verificationCode={verificationCode}
    setVerificationCode={setVerificationCode}
    onSendCode={() => sendVerificationCode('sms')}
    onVerifyCode={verifyCode}
    onCancel={cancelPhoneAuth}
    errorMessage={errorMessage}
    onResendCode={resendCode}
    isSendingCode={isSendingCode}
    isVerifyingCode={isVerifyingCode}
    onTryWhatsApp={() => sendVerificationCode('whatsapp')}
    deliveryChannel={deliveryChannel}
   />
   )}
   
   {/* Mobile-friendly prompt modals */}
   <NamePromptModal
   isVisible={showNamePrompt}
   onConfirm={handleNamePromptConfirm}
   onCancel={handleNamePromptCancel}
   initialValue={userProfile?.displayName || ''}
   initialVenmo={userProfile?.venmoUsername || ''}
   />
   <VenuePromptModal
   isVisible={showVenuePrompt}
   onConfirm={handleVenuePromptConfirm}
   onCancel={handleVenuePromptCancel}
   initialValue={tempVenueName}
   />
   <AddFriendModal
   isVisible={isAddFriendModalVisible}
   onClose={() => setIsAddFriendModalVisible(false)}
   currentUser={currentUser}
   db={db}
   setFriends={setFriends}
   friends={friends}
   />
   <SearchModal
   isVisible={isSearchModalVisible}
   onClose={() => setIsSearchModalVisible(false)}
   currentUser={currentUser}
   db={db}
   />
   
   {/* Desktop Navigation Header */}
   {showBottomNav && (
   <div className="hidden md:block bg-gradient-to-r from-[#FF6B35] to-[#F7931E] via-[#FF8E9B] px-8 py-4 shadow-brutal">
    <div className="flex justify-between items-center max-w-6xl mx-auto">
    <div className="flex items-center space-x-8">
     <button 
     onClick={() => setScreen('home')}
     className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
     >
     <img src="/banana-8bit.png" alt="Banana" className="w-8 h-8" />
     <h1 className="text-xl font-bold text-white">SPLIT</h1>
     </button>
    </div>
    <div className="flex items-center gap-4">
     <nav className="flex items-center gap-3">
     <button 
      className={`flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 ${
      screen === 'getStarted' && !isStartBillModalVisible
       ? 'text-white bg-white/20 border-2 border-black' 
       : 'text-white/90 hover:text-white hover:bg-white/10 border-2 border-transparent hover:border-black'
      }`}
      onClick={() => {
      if (isStartBillModalVisible) {
       setIsStartBillModalVisible(false);
      }
      if (screen === 'getStarted') {
       window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
       setScreen('getStarted');
      }
      }}
     >
      <Home size={18} />
      <span>Home</span>
     </button>
     <button 
      className={`flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 ${
      screen === 'history' && !isStartBillModalVisible
       ? 'text-white bg-white/20 border-2 border-black'
       : 'text-white/90 hover:text-white hover:bg-white/10 border-2 border-transparent hover:border-black'
      }`}
      onClick={() => {
      if (isStartBillModalVisible) {
       setIsStartBillModalVisible(false);
      }
      setScreen('history');
      }}
     >
      <Calendar size={16} />
      <span>History</span>
     </button>
     <button 
      className={`flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 ${
      screen === 'profile' && !isStartBillModalVisible
       ? 'text-white bg-white/20 border-2 border-black'
       : 'text-white/90 hover:text-white hover:bg-white/10 border-2 border-transparent hover:border-black'
      }`}
      onClick={() => {
      if (isStartBillModalVisible) {
       setIsStartBillModalVisible(false);
      }
      setScreen('profile');
      }}
     >
      <UserIcon size={16} />
      <span>Profile</span>
     </button>
     <button
      className={`flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 ${
      isStartBillModalVisible || screen === 'camera' || screen === 'scanAndVerify'
       ? 'text-white bg-white/20 border-2 border-black'
       : 'text-white/90 hover:text-white hover:bg-white/10 border-2 border-transparent hover:border-black'
      }`}
      onClick={() => setIsStartBillModalVisible(true)}
     >
      <Receipt size={16} />
      <span>Create</span>
     </button>
     </nav>
    </div>
    </div>
   </div>
   )}
   
   {renderScreen()}
  </div>


  </div>
 </>
 );
}

// --- Screen Components (THESE ARE ALL THE SAME AS YOUR ORIGINAL) ---
const LoadingScreen = ({ message, subtitle }) => {
 const roasts = useMemo(() => [
  "LET'S SEE THE DAMAGE...", 
  "I RLY HOPE YOU'RE NOT SAVING FOR A MORTGAGE...", "PROB SHOULD HAVE COOKED THAT CHICKEN.",
  "ENTERING OUR RECESSION ERA...", "WHO NEEDS A SAVINGS ACCOUNT ANYWAYS?",
  "FORGET A 401K! LET'S DO FIREBALL SHOTS!",
  "ARE WORK HAPPY HOURS A WRITE-OFF?", "TBH, I DIDN'T AGREE TO THAT APP",
 ], []);
 const [roast, setRoast] = useState(roasts[0]);

 useEffect(() => {
  const interval = setInterval(() => {
   setRoast(roasts[Math.floor(Math.random() * roasts.length)]);
  }, 2000);
  return () => clearInterval(interval);
 }, [roasts]);

 return (
  <div className="flex flex-col h-full bg-[#fdfaef] text-black p-6 justify-center items-center gap-6 text-center safe-area-top">
   {/* Enhanced loading spinner */}
   <div className="relative">
    <Loader2 className="animate-spin" size={72} style={{
     animation: 'spin 2s linear infinite, colorCycle 3s ease-in-out infinite',
     color: '#10b981'
    }} />
    <div className="absolute inset-0 rounded-full opacity-20 animate-pulse" style={{
     animation: 'pulse 2s ease-in-out infinite, bgColorCycle 3s ease-in-out infinite',
     background: 'radial-gradient(circle, #10b981 0%, transparent 70%)'
    }}></div>
   </div>
   
   <style jsx>{`
    @keyframes colorCycle {
     0% { color: #10b981; } /* green */
     33% { color: #8b5cf6; } /* purple */
     66% { color: #3b82f6; } /* bright blue */
     100% { color: #10b981; } /* back to green */
    }
    @keyframes bgColorCycle {
     0% { background: radial-gradient(circle, #10b981 0%, transparent 70%); }
     33% { background: radial-gradient(circle, #8b5cf6 0%, transparent 70%); }
     66% { background: radial-gradient(circle, #3b82f6 0%, transparent 70%); }
     100% { background: radial-gradient(circle, #10b981 0%, transparent 70%); }
    }
   `}</style>
   
   {/* Primary message with better hierarchy */}
   <div className="space-y-3 max-w-md">
    <h2 className="text-3xl md:text-4xl font-bold text-black min-h-[3rem] flex items-center justify-center font-clash">
     {message || roast}
    </h2>
    {subtitle && (
     <p className="text-lg text-gray-600 leading-relaxed">{subtitle}</p>
    )}
   </div>
   
   {/* Progress dots animation */}
   <div className="flex gap-2">
    {[0, 1, 2].map((i) => (
     <div 
      key={i}
      className="w-2 h-2 bg-green-400 rounded-full animate-pulse"
      style={{ animationDelay: `${i * 0.2}s` }}
     ></div>
    ))}
   </div>
  </div>
 );
};

const CancelModal = ({ onConfirm, onDismiss }) => (
 <div className="absolute inset-0 bg-black bg-opacity-60 z-30 flex items-center justify-center p-4 font-receipt">
  <div className="bg-white p-6 shadow-brutal-lg text-center max-w-sm mx-auto border-2 border-black">
   <h3 className="text-2xl font-medium text-black mt-4 font-clash">CANCEL SPLIT?</h3>
   <div className="mt-2"><p className="text-lg text-gray-700">Are you sure? All progress will be lost.</p></div>
   <div className="mt-5 sm:mt-6 flex flex-col gap-3">
    <button onClick={onConfirm} type="button" className="w-full btn-pill btn-pill-danger btn-pill-lg font-medium">YES, CANCEL</button>
    <button onClick={onDismiss} type="button" className="w-full btn-pill btn-pill-secondary btn-pill-lg font-medium">NO, CONTINUE</button>
   </div>
  </div>
 </div>
);

const StartBillModal = ({ onTakePhoto, onCancel, onFileSelect, onToastImport }) => {
 const fileInputRef = useRef(null);
 const [showToastInput, setShowToastInput] = useState(false);
 const [toastUrl, setToastUrl] = useState('');

 const handleUploadClick = () => {
  triggerHaptic('light');
  fileInputRef.current.click();
 };

 const handleToastSubmit = () => {
  if (!toastUrl.includes('toasttab.com')) return;
  triggerHaptic('medium');
  onToastImport(toastUrl.trim());
 };

 return (
  <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
   <div className="bg-white p-6 shadow-brutal-lg text-center max-w-sm w-full mx-auto border-2 border-black">
    <h3 className="text-2xl font-medium text-black mt-4 font-clash">Create a new bill</h3>
    <div className="mt-5 sm:mt-6 flex flex-col gap-3">
     <button onClick={onTakePhoto} className="w-full btn-pill btn-pill-warning btn-pill-xl md:hidden"><Camera size={24} /> Take photo</button>
     <input type="file" ref={fileInputRef} onChange={onFileSelect} className="hidden" accept="image/*" />
     <button onClick={handleUploadClick} className="w-full btn-pill btn-pill-dark btn-pill-xl"><Upload size={20} /> Upload photo</button>

     {/* Toast divider */}
     <div className="flex items-center gap-3 my-1">
      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">or</span>
      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
     </div>

     {showToastInput ? (
      <div className="flex flex-col gap-2">
       <div className="flex gap-2">
        <input
         type="url"
         value={toastUrl}
         onChange={e => setToastUrl(e.target.value)}
         onKeyDown={e => e.key === 'Enter' && handleToastSubmit()}
         placeholder="toasttab.com/receipts/..."
         className="flex-1 px-3 py-2 border-2 border-black text-sm outline-none focus:shadow-brutal-sm transition-all"
         autoFocus
        />
        <button
         onClick={handleToastSubmit}
         disabled={!toastUrl.includes('toasttab.com')}
         className="btn-pill btn-pill-warning px-4 disabled:opacity-40"
        >
         <ArrowRight size={18} />
        </button>
       </div>
       <button onClick={() => { setShowToastInput(false); setToastUrl(''); }} className="text-xs text-gray-400 hover:text-gray-600">
        Cancel
       </button>
      </div>
     ) : (
      <button
       onClick={() => setShowToastInput(true)}
       className="w-full btn-pill btn-pill-secondary btn-pill-xl border-2 border-black"
      >
       Import from Toast
      </button>
     )}

     <button onClick={onCancel} onTouchStart={() => {}} type="button" className="mt-2 w-full btn-pill btn-pill-ghost btn-pill-lg touch-manipulation">Cancel</button>
    </div>
   </div>
  </div>
 );
};

const GetStartedScreen = ({ onStartBill, onJoin, errorMessage, recentBills, onViewHistory, currentUser, onSignIn, onSignOut, setScreen, userProfile }) => {
 
 // Check if user is signed in (has a persistent account, not just anonymous)
 const isSignedIn = !!currentUser;

 // Debug logging
 console.log("Current user:", currentUser);
 console.log("Is signed in:", isSignedIn);
 
 // Get user's bill history with cloud sync - only show enhanced experience if signed in
 const [userHistory, setUserHistory] = useState([]);
 const [showEnhancedExperience, setShowEnhancedExperience] = useState(false);
 
 useEffect(() => {
  const loadUserHistory = async () => {
  if (isSignedIn && currentUser?.id) {
   const history = await BillHistory.getHistoryWithSync(currentUser.id, db);
   setUserHistory(history);
   setShowEnhancedExperience(history.length > 0);
  } else {
   setUserHistory([]);
   setShowEnhancedExperience(false);
  }
  };
  loadUserHistory();
 }, [isSignedIn, currentUser?.id]);
 
 console.log("User history length:", userHistory.length);
 console.log("Show enhanced experience:", showEnhancedExperience);
 
 // Get recent dining partners from history
 const getFrequentPartners = () => {
  const partnerCounts = {};
  userHistory.forEach(bill => {
   bill.participants.forEach(name => {
    partnerCounts[name] = (partnerCounts[name] || 0) + 1;
   });
  });
  return Object.entries(partnerCounts)
   .sort(([,a], [,b]) => b - a)
   .slice(0, 3)
   .map(([name]) => name);
 };
 
 const frequentPartners = getFrequentPartners();

 // Check for URL parameters on component mount
 useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const joinParam = urlParams.get('join');
  if (joinParam && onJoin) {
   // Auto-trigger join with the bill ID from URL
   setTimeout(() => {
    onJoin(joinParam.toUpperCase());
   }, 500); // Small delay for user to see the page
  }
 }, [onJoin]);

 // Enhanced layout for signed-in users with history
 if (showEnhancedExperience) {
  return (
   <div className="flex flex-col min-h-full bg-[#fdfaef] text-[#2D5A5A] overflow-y-auto pb-20 md:pb-0">
    {/* Top Navigation Bar - Desktop Only */}
    <div className="hidden md:block bg-gradient-to-r from-[#FF6B35] to-[#F7931E] via-[#FF8E9B] px-8 py-4 shadow-brutal">
     <div className="flex justify-between items-center max-w-7xl mx-auto">
      {/* Left side - Logo */}
      <div className="flex items-center gap-4">
       <img 
        src="/banana-8bit.png" 
        alt="Split Logo" 
        className="w-8 h-8"
       />
       <h1 className="text-xl font-bold text-white">SPLIT</h1>
      </div>
      
      {/* Right side - Navigation and Actions */}
      <div className="flex items-center gap-4">
       <nav className="flex items-center gap-3">
        <button 
         className="text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 text-base font-medium px-4 py-2 border-2 border-transparent hover:border-black "
         onClick={() => setScreen('getStarted')}
        >
         Home
        </button>
        <button 
         className="text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 text-base font-medium px-4 py-2 border-2 border-transparent hover:border-black "
         onClick={onViewHistory}
        >
         History
        </button>
       </nav>
       
       {/* Action buttons */}
       <div className="flex items-center gap-3">
        <button 
         className="bg-white text-orange-600 hover:bg-orange-50 px-4 py-2 font-medium transition-all duration-200 flex items-center gap-2 shadow-brutal-sm"
         onClick={onStartBill}
        >
         <Camera size={16} />
         Create
        </button>
        <button 
         className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 overflow-hidden border-2 border-black hover:border-black "
         onClick={() => setScreen('profile')}
        >
         {userProfile?.profilePicture ? (
          <img 
           src={userProfile.profilePicture} 
           alt="Profile" 
           className="w-full h-full object-cover"
          />
         ) : (
          <UserIcon size={18} className="text-[#2D5A5A]" />
         )}
        </button>
       </div>
      </div>
     </div>
    </div>
    <div className="p-6 md:p-8 lg:p-12 flex-grow">
     {/* Header */}
     <div className="text-center mt-6 md:mt-8 mb-8 md:mb-10">
      <h1 className="text-3xl md:text-4xl lg:text-5xl mb-3 md:mb-4 font-bold text-[#2D5A5A] font-clash">Welcome back! 👋</h1>
      <p className="text-[#6B7280] text-lg md:text-lg lg:text-xl ">Ready to split another bill?</p>
     </div>
     
     {errorMessage && <div className="bg-red-900 border-2 border-red-500 text-red-300 p-3 text-center text-lg mb-4">{errorMessage}</div>}
     
     <div className="flex-grow space-y-6 md:space-y-8 max-w-2xl md:max-w-4xl lg:max-w-5xl mx-auto w-full">
     {/* Recent Bills */}
     {userHistory.length > 0 && (
      <div>
       <h3 className="text-xl md:text-xl lg:text-2xl font-bold mb-5 md:mb-6 flex items-center gap-3">
        <Calendar size={22} className="text-[#FF8E9B]" /> 
        <span className="text-[#2D5A5A] ">Recent Bills</span>
       </h3>
       <div className="space-y-3">
        {userHistory.slice(0, 3).map(bill => (
         <div key={bill.id} className="bg-white p-5 border-2 border-black transition-all duration-200 hover:shadow-brutal">
          <div className="flex justify-between items-center">
           <div className="flex-1 min-w-0">
            <p className="text-[#2D5A5A] font-bold text-lg md:text-lg lg:text-xl truncate ">{bill.venueName}</p>
            <p className="text-[#6B7280] text-base md:text-base lg:text-lg font-medium ">{bill.date} • {formatCurrency(bill.total)}</p>
            <p className="text-[#9CA3AF] text-sm ">with {bill.participants.slice(0, 2).join(', ')}{bill.participants.length > 2 ? ` +${bill.participants.length - 2} more` : ''}</p>
           </div>
           <button 
            onClick={() => onViewHistory(bill.id)}
            className="bg-[#A8C09A] hover:bg-[#9BB88F] active:bg-[#8FA583] active:scale-95 text-white px-3 py-1 text-xs md:text-sm border-2 border-black transition-all duration-150 ml-2 flex-shrink-0 "
           >
            VIEW BILL
           </button>
          </div>
         </div>
        ))}
       </div>
       {userHistory.length > 3 && (
        <button onClick={onViewHistory} className="w-full mt-3 text-[#FF8E9B] hover:text-[#FF7A89] text-center py-2 ">
         View all {userHistory.length} bills →
        </button>
       )}
      </div>
     )}
     
     {/* Frequent Partners */}
     {frequentPartners.length > 0 && (
      <div>
       <h3 className="text-xl md:text-xl lg:text-2xl font-bold mb-5 md:mb-6 flex items-center gap-3">
        <Users size={22} className="text-[#A8C09A]" /> 
        <span className="text-[#2D5A5A] ">Quick Split</span>
       </h3>
       <div className="bg-white p-5 border-2 border-black hover:border-black transition-all duration-200">
        <p className="text-[#6B7280] text-base md:text-sm mb-4 ">Start a bill with your usual crew:</p>
        <div className="flex flex-wrap gap-3">
         {frequentPartners.map(partner => (
          <span key={partner} className="bg-[#FF8E9B] hover:bg-[#FF7A89] text-white px-3 py-2 text-sm md:text-sm font-bold shadow-brutal transition-all duration-200">
           {partner}
          </span>
         ))}
         <button onClick={onStartBill} className="btn-pill btn-pill-warning btn-pill-sm font-medium">
          + Split Together
         </button>
        </div>
       </div>
      </div>
     )}
    </div>
    
    </div>
   </div>
  );
 }
 
 // Layout for guests (non-signed-in users) or signed-in users without history
 return (
  <div className="flex flex-col min-h-full bg-[#fdfaef] text-[#2D5A5A] p-6 md:p-12 justify-between overflow-y-auto pb-20 md:pb-8">
   <div className="text-center mt-12">
    <div className="flex items-center justify-center gap-6 mb-6">
     <img 
      src="/logo192.png" 
      alt="Split Logo" 
      className="w-24 h-24 pixelated"
      style={{ imageRendering: 'pixelated' }}
     />
    </div>
    <h1 className="text-7xl font-bold text-[#2D5A5A]">SPLIT</h1>
    <p className="text-[#6B7280] mt-2 text-xl ">THE FASTEST WAY TO SPLIT THE BILL.</p>
   </div>
   {errorMessage && <div className="bg-red-900 border-2 border-red-500 text-red-300 p-3 text-center text-lg">{errorMessage}</div>}
   <div className="flex flex-col items-center gap-4">
    {/* Show sign in option only for non-signed-in users */}
    {!isSignedIn && onSignIn && (
     <>
      <button onClick={onSignIn} className="w-full btn-pill btn-pill-warning btn-pill-xl ">
       SIGN IN WITH PHONE
      </button>
      <div className="w-full text-center text-[#6B7280] text-base font-medium ">Save bills to your account</div>
      <div className="w-full text-center text-[#6B7280] text-sm font-medium tracking-wider ">OR</div>
     </>
    )}
   </div>
   <div className="text-center mb-4 hidden md:block">
    {isSignedIn ? (
     <div className="flex items-center justify-between">
      <p className="text-gray-500 text-sm">Signed in • Bills save to your account</p>
      <button 
       onClick={onSignOut}
       className="text-blue-600 hover:text-blue-800 text-sm underline"
      >
       Sign Out
      </button>
     </div>
    ) : (
     <p className="text-gray-500 text-sm">Split bills effortlessly • No account required</p>
    )}
   </div>
  </div>
 );
};

const CameraScreen = ({ onCapture, onCancel, onHome }) => {
 // Define haptic locally within component scope
 const localTriggerHaptic = (style = 'light') => {
  if (window.navigator && window.navigator.vibrate) {
   const patterns = { 
    light: [10], 
    medium: [20], 
    heavy: [30], 
    success: [10, 10, 10] 
   };
   window.navigator.vibrate(patterns[style] || patterns.light);
  }
  console.log(`Haptic triggered: ${style}`);
 };
 const videoRef = useRef(null);
 const canvasRef = useRef(null);
 const [hasFlash, setHasFlash] = useState(false);
 const [currentStream, setCurrentStream] = useState(null);

 const handleCancelClick = () => {
  onCancel();
 };

 useEffect(() => {
  let stream = null;
  const startCamera = async () => {
   try {
    // Request camera with flash capability
    stream = await navigator.mediaDevices.getUserMedia({ 
     video: { 
      facingMode: 'environment',
      torch: true // Request torch/flash capability
     } 
    });
    
    if (videoRef.current) {
     videoRef.current.srcObject = stream;
     setCurrentStream(stream);
     
     // Check if flash is available
     const track = stream.getVideoTracks()[0];
     if (track && typeof track.getCapabilities === 'function') {
      const capabilities = track.getCapabilities();
      
      if (capabilities && capabilities.torch) {
       setHasFlash(true);
      }
     }
    }
   } catch (err) {
    console.error("Error accessing camera:", err);
    // Fallback: try without torch requirement
    try {
     stream = await navigator.mediaDevices.getUserMedia({ 
      video: { facingMode: 'environment' } 
     });
     if (videoRef.current) {
      videoRef.current.srcObject = stream;
      setCurrentStream(stream);
     }
    } catch (fallbackErr) {
     console.error("Fallback camera access failed:", fallbackErr);
     alert("Cannot access camera. Please check permissions.");
     onCancel();
    }
   }
  };
  startCamera();
  return () => {
   if (stream && stream.getTracks) {
    stream.getTracks().forEach(track => {
     if (track && typeof track.stop === 'function') {
      track.stop();
     }
    });
   }
  };
 }, [onCancel]);


 const handleCapture = async () => {
  localTriggerHaptic('heavy');
  
  // Enable flash if available
  if (hasFlash && currentStream) {
   try {
    const track = currentStream.getVideoTracks()[0];
    await track.applyConstraints({
     advanced: [{ torch: true }]
    });
    console.log("Flash enabled for capture");
    
    // Small delay to let flash stabilize
    await new Promise(resolve => setTimeout(resolve, 200));
   } catch (err) {
    console.error("Could not enable flash:", err);
    // Continue without flash
   }
  }
  
  if (videoRef.current && canvasRef.current) {
   const video = videoRef.current;
   const canvas = canvasRef.current;
   canvas.width = video.videoWidth;
   canvas.height = video.videoHeight;
   const context = canvas.getContext('2d');
   context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
   const dataUrl = canvas.toDataURL('image/png');
   
   // Disable flash after capture
   if (hasFlash && currentStream) {
    try {
     const track = currentStream.getVideoTracks()[0];
     await track.applyConstraints({
      advanced: [{ torch: false }]
     });
     console.log("Flash disabled after capture");
    } catch (err) {
     console.error("Could not disable flash:", err);
    }
   }
   
   onCapture(dataUrl);
  }
 };


 return (
  <div className="fixed inset-0 flex flex-col bg-black z-50">
   <video ref={videoRef} autoPlay playsInline className="w-full flex-1 object-cover"></video>
   <canvas ref={canvasRef} className="hidden"></canvas>
   <div className="flex items-center justify-between px-6 py-5 bg-black">
    <button onClick={handleCancelClick} className="text-white font-bold text-base tracking-wide hover:text-gray-300 transition-colors uppercase">Cancel</button>
    {hasFlash && (
     <div className="text-white text-sm font-bold bg-[#2D5A5A] px-3 py-1 border-2 border-white uppercase tracking-wide">
      ⚡ Flash On
     </div>
    )}
    <button onClick={handleCapture} className="w-18 h-18 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-[0_0_0_3px_rgba(0,0,0,1)]" style={{ width: 72, height: 72 }}>
     <div className="w-14 h-14 rounded-full border-[3px] border-black"></div>
    </button>
    {onHome ? (
     <button onClick={onHome} className="text-white hover:text-gray-300 transition-colors">
      <Home size={24} />
     </button>
    ) : <div className="w-6" />}
   </div>
  </div>
 );
};

const ScanAndVerifyScreen = ({ scannedData, setScannedData, onConfirm, onCancel, onRescan }) => {
 const [newItemName, setNewItemName] = useState('');
 const [newItemPrice, setNewItemPrice] = useState('');
 const [newItemQuantity, setNewItemQuantity] = useState('1');
 const [isReceiptEnlarged, setIsReceiptEnlarged] = useState(false);
 
 const subtotal = useMemo(() => {
  if (!scannedData || !scannedData.items) return 0;
  return scannedData.items.reduce((sum, item) => sum + item.price, 0);
 }, [scannedData]);

 if (!scannedData) return <LoadingScreen />;

 const { items, tax, tip, receiptImage, toastUrl } = scannedData;

 const onItemChange = (id, field, value) => {
  const newItems = items.map(item => item.id === id ? { ...item, [field]: value } : item);
  setScannedData({ ...scannedData, items: newItems });
 };

 const handleAddItem = (e) => {
  e.preventDefault();
  if (newItemName && newItemPrice) {
   const newItem = {
    id: Date.now(),
    name: newItemName,
    price: parseFloat(newItemPrice) || 0,
    quantity: parseInt(newItemQuantity) || 1,
   };
   setScannedData({ ...scannedData, items: [...items, newItem] });
   setNewItemName('');
   setNewItemPrice('');
   setNewItemQuantity('1');
  }
 };
 
 const handleRemoveItem = (id) => {
  const newItems = items.filter(item => item.id !== id);
  setScannedData({ ...scannedData, items: newItems });
 };

 const setTaxValue = (newTax) => setScannedData({ ...scannedData, tax: newTax });
 const setTipValue = (newTip) => setScannedData({ ...scannedData, tip: newTip });

 return (
  <div className="flex flex-col h-full text-black bg-receipt-beige safe-area-top">
  <BillProgressStepper currentStep="verify" />
  {/* Enlarged receipt modal */}
  {isReceiptEnlarged && (
   <div className="absolute inset-0 bg-black bg-opacity-75 z-30 flex flex-col items-center justify-center p-4">
    <img 
     src={receiptImage} 
     alt="Receipt" 
     className="max-w-full max-h-full object-contain mb-4 shadow-brutal-lg" 
    />
    <div className="flex gap-4">
     <button 
      onClick={() => {
       setIsReceiptEnlarged(false);
       onRescan();
      }} 
      className="btn-pill btn-pill-primary font-receipt text-lg"
     >
      RETAKE PHOTO
     </button>
     <button 
      onClick={() => setIsReceiptEnlarged(false)} 
      className="btn-pill btn-pill-secondary font-receipt text-lg"
     >
      CLOSE
     </button>
    </div>
   </div>
  )}
  
  <div className="relative p-4 bg-receipt-beige border-b-2 border-dashed border-gray-400 text-center receipt-container">
   <h2 className="receipt-header">VERIFY RECEIPT</h2>
   <p className="caption text-gray-600 uppercase tracking-wide">Add, Edit, or Remove Items</p>
   <div className="receipt-dotted-line my-2"></div>
   <button onClick={() => {
    // Trigger file upload for rescan
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
     const file = e.target.files[0];
     if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
       const base64Data = event.target.result.split(',')[1];
       onRescan(base64Data);
      };
      reader.readAsDataURL(file);
     }
    };
    input.click();
   }} className="absolute top-1/2 right-16 -translate-y-1/2 text-gray-600 hover:text-black">
    <RotateCcw size={24}/>
   </button>
   <button onClick={onCancel} className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-600 hover:text-black"><X size={28}/></button>
  </div>
  <div className="flex-grow overflow-x-hidden overflow-y-auto px-4 py-4 space-y-3 sm:px-5">
   {receiptImage ? (
   <div
    className="w-full h-40 bg-gray-200 mb-4 flex items-center justify-center border-2 border-dashed border-gray-400 cursor-pointer hover:border-indigo-500 transition-colors"
    onClick={() => setIsReceiptEnlarged(true)}
    title="Click to enlarge receipt"
   >
    <img src={receiptImage} alt="Receipt" className="object-contain h-full w-full" />
   </div>
   ) : toastUrl ? (
   <a
    href={toastUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="w-full mb-4 flex items-center gap-3 p-4 bg-white border-2 border-black shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
   >
    <img src="/toast-8bit.svg" alt="Toast" className="w-8 h-8" />
    <div className="flex-1 min-w-0 text-left">
     <div className="font-bold text-sm">Imported from Toast</div>
     <div className="text-xs text-gray-500 truncate max-w-full">{toastUrl}</div>
    </div>
    <span className="text-gray-400 text-sm">View &rarr;</span>
   </a>
   ) : null}
   {items.map(item => (
   <div key={item.id} className="group border-2 border-transparent px-3 py-2 transition-all duration-200 hover:border-gray-300 hover:bg-white/20 hover:shadow-brutal-sm">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 items-start gap-2 sm:items-center sm:flex-1 sm:mr-2">
    <input
     type="number"
     value={item.quantity || 1}
     onChange={(e) => onItemChange(item.id, 'quantity', parseInt(e.target.value) || 1)}
     className="w-10 flex-shrink-0 bg-transparent p-1 text-center text-sm text-gray-700 outline-none transition-all duration-200 hover:bg-white/40 focus:bg-white/60 focus:shadow-brutal-sm sm:w-12"
     min="1"
     title="Quantity"
    />
    <span className="pt-2 text-gray-500 sm:pt-0">×</span>
    <input
     type="text"
     value={item.name}
     onChange={(e) => onItemChange(item.id, 'name', e.target.value)}
     className="min-w-0 flex-1 bg-transparent p-2 text-[13px] outline-none transition-all duration-200 hover:bg-white/40 focus:bg-white/60 focus:shadow-brutal-sm receipt-item sm:text-[15px]"
    />
    </div>
    <div className="flex items-center justify-end gap-2 sm:flex-shrink-0">
    <input
     type="number"
     value={item.price}
     onChange={(e) => onItemChange(item.id, 'price', parseFloat(e.target.value) || 0)}
     className="w-16 bg-transparent p-1 text-right text-[13px] outline-none transition-all duration-200 hover:bg-white/40 focus:bg-white/60 focus:shadow-brutal-sm price sm:w-20 sm:text-[15px]"
     step="0.01"
    />
    <button
     onClick={() => handleRemoveItem(item.id)}
     className="flex-shrink-0 p-1 text-red-400 transition-all duration-100 hover:text-red-600 active:scale-95"
     title="Remove item"
    >
     <Trash2 size={16}/>
    </button>
    </div>
    </div>
   </div>
   ))}
   <form onSubmit={handleAddItem} className="mt-4 pt-4 border-t border-dashed border-gray-400">
   <div className="px-3 py-1 transition-colors hover:bg-white/10">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 items-start gap-2 sm:items-center sm:flex-1 sm:mr-2">
    <input
     type="number"
     value={newItemQuantity}
     onChange={(e) => setNewItemQuantity(e.target.value)}
     placeholder="1"
     className="w-10 bg-transparent p-1 text-center text-sm outline-none placeholder-gray-400 hover:bg-white/30 focus:bg-white/40 sm:w-12"
     min="1"
    />
    <span className="pt-2 text-gray-500 sm:pt-0">×</span>
    <input
     type="text"
     value={newItemName}
     onChange={(e) => setNewItemName(e.target.value)}
     placeholder="+ Add new item..."
     className="min-w-0 flex-1 bg-transparent p-1 text-[13px] outline-none placeholder-gray-500 hover:bg-white/30 focus:bg-white/40 receipt-item sm:text-[15px]"
    />
    </div>
    <div className="flex items-center justify-end gap-2 sm:flex-shrink-0">
    <input
     type="number"
     value={newItemPrice}
     onChange={(e) => setNewItemPrice(e.target.value)}
     placeholder="0.00"
     className="w-16 bg-transparent p-1 text-right text-[13px] outline-none placeholder-gray-500 hover:bg-white/30 focus:bg-white/40 price sm:w-20 sm:text-[15px]"
     step="0.01"
    />
    <button type="submit" className="p-1 text-green-600 hover:text-green-800" title="Add item">
     <PlusCircle size={16}/>
    </button>
    </div>
    </div>
   </div>
   </form>
   
   <div className="mt-6 pt-4 border-t border-dashed border-gray-500 space-y-3">
    <div className="receipt-dotted-line my-2"></div>
    
    <div className="flex justify-between items-center">
    <span className="body-small text-gray-700">Subtotal</span>
    <span className="price">${subtotal.toFixed(2)}</span>
    </div>
    
    <div className="flex justify-between items-center">
    <label htmlFor="tax" className="body-small text-gray-700">Tax</label>
    <TaxInput tax={tax} onTaxChange={setTaxValue} />
    </div>
    
    <div className="flex justify-between items-center">
    <label htmlFor="tip" className="body-small text-gray-700">Tip</label>
    <TipInput tip={tip} onTipChange={setTipValue} />
    </div>
    
    
    <div className="flex justify-around pt-2 gap-2">
    {[18, 20, 22].map(p => (
     <button 
     key={p} 
     onClick={() => { 
      triggerHaptic('light'); 
      setTipValue(parseFloat((subtotal * (p / 100)).toFixed(2))); 
     }} 
     className="micro bg-white/30 text-black px-2 py-1 border-2 border-black hover:bg-white/50"
     >
     {p}%
     </button>
    ))}
    </div>
    
    <div className="receipt-dotted-line my-3"></div>
    
    <div className="receipt-total flex justify-between items-center">
    <span className="body font-bold">TOTAL</span>
    <span className="price-large">${(subtotal + tax + tip).toFixed(2)}</span>
    </div>
   </div>
  </div>
  <div className="p-4 border-t-2 border-dashed border-gray-400">
   <div 
   onClick={() => {
    console.log("🎯 CONFIRM div clicked!");
    console.log("onConfirm function:", typeof onConfirm, onConfirm?.name);
    console.log("About to call onConfirm...");
    onConfirm();
    console.log("onConfirm call completed");
   }}
   onTouchStart={(e) => {
    console.log("🎯 CONFIRM div touchstart!");
   }}
   onTouchEnd={(e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("🎯 CONFIRM div touched!");
    console.log("onConfirm function:", typeof onConfirm, onConfirm?.name);
    console.log("About to call onConfirm...");
    onConfirm();
    console.log("onConfirm call completed");
   }}
   role="button"
   tabIndex={0}
   className="w-full btn-pill btn-pill-primary btn-pill-xl select-none"
   style={{ 
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    WebkitUserSelect: 'none',
    userSelect: 'none'
   }}
   >
   CONFIRM & CREATE BILL <ChevronRight size={24} />
   </div>
  </div>
  </div>
 );
};

const AddPeopleScreen = ({ people, onAddPerson, onRemovePerson, onSetPaidAmount, onContinue, onBack, onCancel, billTotal, billId, currentUser, userProfile, venueName, receiptImage, friends, db }) => {
 const [newName, setNewName] = useState('');
 const [editingPerson, setEditingPerson] = useState(null);
 const [paidAmount, setPaidAmount] = useState(0);
 const [showSuggestions, setShowSuggestions] = useState(false);
 const [filteredFriends, setFilteredFriends] = useState([]);
 const [venmoInput, setVenmoInput] = useState('');

 // Check if current user already has Venmo saved
 const currentUserHasVenmo = useMemo(() => {
  return !!userProfile?.venmoUsername;
 }, [userProfile]);

 const totalPaid = useMemo(() => people.reduce((sum, p) => sum + p.paid, 0), [people]);
 const remainingAmount = billTotal - totalPaid;

 // Filter friends based on input and exclude already added people
 useEffect(() => {
  if (newName.trim() && friends && friends.length > 0) {
  const alreadyAddedNames = people.map(p => p.name.toLowerCase());
  const filtered = friends
   .filter(friend => {
   const friendName = (friend.displayName || friend.username).toLowerCase();
   return friendName.includes(newName.toLowerCase()) && 
     !alreadyAddedNames.includes(friendName);
   })
   .slice(0, 3); // Limit to 3 suggestions
  setFilteredFriends(filtered);
  setShowSuggestions(filtered.length > 0);
  } else {
  setFilteredFriends([]);
  setShowSuggestions(false);
  }
 }, [newName, friends, people]);

 const handleFriendSelect = (friend) => {
  const friendName = friend.displayName || friend.username;
  onAddPerson(friendName, friend);
  setNewName('');
  setShowSuggestions(false);
 };

 const handleAdd = (e) => {
  e.preventDefault();
  if(newName.trim()) {
   onAddPerson(newName);
   setNewName('');
  }
 };
 
 const handleOpenPaidModal = (person) => {
  triggerHaptic('light');
  setEditingPerson(person);
  
  // Smart auto-population logic
  if (person.paid === 0) {
   // If this person hasn't paid anything yet, auto-populate with remaining amount
   const remainingToPay = billTotal - totalPaid;
   if (remainingToPay > 0) {
    setPaidAmount(remainingToPay.toFixed(2));
   } else {
    // If everything is accounted for, suggest the full amount
    setPaidAmount(billTotal.toFixed(2));
   }
  } else {
   // If they already have an amount, show their current amount
   setPaidAmount(person.paid.toFixed(2));
  }
 };

 const handleConfirmPaidAmount = () => {
  triggerHaptic('medium');
  onSetPaidAmount(editingPerson.id, parseFloat(paidAmount) || 0);

  // Save Venmo if user entered it in the inline prompt
  if (venmoInput.trim()) {
   const userId = currentUser?.uid || currentUser?.id;
   if (userId) {
   const cleanVenmo = venmoInput.replace('@', '').trim();
   UserProfile.saveProfile(userId, { venmoUsername: cleanVenmo }, db);
   console.log('Venmo username saved from paid amount modal:', cleanVenmo);
   }
   setVenmoInput('');
  }

  setEditingPerson(null);
 };

 return (
  <div className="flex flex-col h-full safe-area-top">
   {editingPerson && (
    <div className="absolute inset-0 bg-black bg-opacity-60 z-30 flex items-center justify-center p-4 font-receipt">
     <div className="bg-white p-6 shadow-brutal-lg w-full max-w-sm mx-auto border-2 border-black">
      <h3 className="text-xl">HOW MUCH DID {editingPerson.name.toUpperCase()} PAY?</h3>
      <p className="text-sm text-gray-600 mt-1">Bill total: {formatCurrency(billTotal)} | Remaining: {formatCurrency(billTotal - totalPaid)}</p>
      <div className="mt-4 flex items-center gap-2">
       <span className="text-2xl">$</span>
       <input
        type="number"
        step="0.01"
        value={paidAmount}
        onChange={e => setPaidAmount(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmPaidAmount(); }}
        className="flex-grow p-3 text-xl bg-gray-100 border-2 border-black outline-none text-center"
        placeholder="0.00"
        autoFocus
       />
      </div>
      <div className="mt-3 flex gap-2">
       <button
       onClick={() => setPaidAmount(billTotal.toFixed(2))}
       className="flex-1 py-2 px-3 text-sm bg-gray-100 hover:bg-gray-200 active:scale-95 border-2 border-black transition-all"
       >
       Full bill<br/><span className="font-bold">{formatCurrency(billTotal)}</span>
       </button>
       <button
       onClick={() => setPaidAmount((billTotal - totalPaid + (editingPerson?.paid || 0)).toFixed(2))}
       className="flex-1 py-2 px-3 text-sm bg-gray-100 hover:bg-gray-200 active:scale-95 border-2 border-black transition-all"
       >
       Remaining<br/><span className="font-bold">{formatCurrency(billTotal - totalPaid + (editingPerson?.paid || 0))}</span>
       </button>
       <button
       onClick={() => {
        const splitAmount = billTotal / Math.max(people.length, 1);
        setPaidAmount(splitAmount.toFixed(2));
       }}
       className="flex-1 py-2 px-3 text-sm bg-gray-100 hover:bg-gray-200 active:scale-95 border-2 border-black transition-all"
       >
       Split evenly<br/><span className="font-bold">{formatCurrency(billTotal / Math.max(people.length, 1))}</span>
       </button>
      </div>
      {/* Inline Venmo prompt - only for current user who hasn't set Venmo yet */}
      {editingPerson && !currentUserHasVenmo &&
      (editingPerson.id === currentUser?.id || editingPerson.id === currentUser?.uid) && (
       <div className="mt-3 bg-blue-50 border-2 border-blue-600 p-3">
       <p className="text-xs text-blue-700 mb-2">Add your Venmo to get paid back:</p>
       <div className="flex items-center bg-white border-2 border-blue-600 px-3 py-2">
        <span className="text-gray-400 mr-1 text-sm">@</span>
        <input
        type="text"
        value={venmoInput}
        onChange={(e) => setVenmoInput(e.target.value)}
        placeholder="venmo-username"
        className="flex-1 bg-transparent outline-none text-sm"
        onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmPaidAmount(); }}
        />
       </div>
       </div>
      )}
      <div className="mt-4 flex gap-3">
       <button onClick={() => setEditingPerson(null)} className="w-full btn-pill btn-pill-secondary btn-pill-lg">CANCEL</button>
       <button onClick={handleConfirmPaidAmount} className="w-full btn-pill btn-pill-primary btn-pill-lg">CONFIRM</button>
      </div>
     </div>
    </div>
   )}
   <BillProgressStepper currentStep="people" />
   <div className="relative p-6 border-b-2 border-dashed border-gray-400 text-center">
    <button
     onClick={onBack}
     className="absolute left-4 top-4 text-gray-600 hover:text-black transition-colors md:hidden p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 transition-transform"
     title="Back to edit items"
    >
     <ArrowLeft size={24} />
    </button>
    <button
     onClick={onCancel}
     className="absolute right-4 top-4 text-gray-600 hover:text-black transition-colors p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 transition-transform"
     title="Save and go home"
    >
     <X size={24} />
    </button>
    <h2 className="text-xl px-16">WHO'S HERE & WHO GOT THE BILL?</h2>
    <div className="mt-3 space-y-2">
     {/* Primary Share Button */}
     <button 
      onClick={async (e) => {
       triggerHaptic('medium');
       // Use production URL for sharing even in development
       const baseUrl = window.location.hostname === 'localhost' 
        ? 'https://avocadoisextrabananasplit.web.app' 
        : window.location.origin;
       const shareableLink = `${baseUrl}?join=${billId}`;
       const venue = venueName || 'restaurant';
       
       // Try to use Web Share API first (mobile)
       if (navigator.share) {
        try {
         await navigator.share({
          title: `Split the ${venue} bill`,
          text: `Split the ${venue} bill with me! 🧾`,
          url: shareableLink,
         });
         return; // Success, exit early
        } catch (error) {
         console.log('Share failed:', error);
        }
       }
       
       // Desktop or fallback - SMS with text and link
       const message = encodeURIComponent(`Split the ${venue} bill with me! ${shareableLink} 🧾`);
       const iMessageURL = `sms:&body=${message}`;
       
       if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(shareableLink).then(() => {
         // Show brief success feedback
         const button = e.target.closest('button');
         const originalBg = button.style.background;
         button.style.background = '#059669';
         setTimeout(() => {
          button.style.background = originalBg;
         }, 1000);
        }).catch(() => {
         // Fallback to SMS
         window.location.href = iMessageURL;
        });
       } else {
        // No clipboard support, go straight to SMS
        window.location.href = iMessageURL;
       }
      }}
      className="w-full btn-pill btn-pill-success btn-pill-xl"
     >
      <Share2 size={24} />
      SHARE LINK TO JOIN
     </button>
     
    </div>
   </div>
   <div className="flex-grow overflow-y-auto p-4">
    <div className="relative mb-4">
     <form onSubmit={handleAdd} className="flex gap-2">
      <input 
      type="text" 
      value={newName} 
      onChange={(e) => setNewName(e.target.value)} 
      placeholder="ADD FRIEND'S NAME" 
      className="flex-grow p-2 rounded-md bg-white border-2 border-black outline-none" 
      onFocus={() => newName.trim() && setShowSuggestions(filteredFriends.length > 0)}
      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} // Delay to allow clicking suggestions
      />
      <button type="submit" className="btn-pill btn-pill-primary btn-pill-lg">+</button>
     </form>
     
     {/* Friend Suggestions Dropdown */}
     {showSuggestions && (
     <div className="absolute top-full left-0 right-12 bg-white border-2 border-black mt-1 shadow-brutal z-10">
      {filteredFriends.map((friend) => (
      <button
       key={friend.id}
       onClick={() => handleFriendSelect(friend)}
       className="w-full flex items-center gap-3 p-3 hover:bg-gray-100  border-b-2 border-black last:border-b-0 text-left"
      >
       <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
       {friend.profilePicture ? (
        <img 
        src={friend.profilePicture} 
        alt={friend.displayName || friend.username}
        className="w-full h-full object-cover"
        />
       ) : (
        <span className="text-xs font-semibold text-gray-600">
        {(friend.displayName || friend.username)[0].toUpperCase()}
        </span>
       )}
       </div>
       <div>
       <div className="font-medium text-sm">{friend.displayName || friend.username}</div>
       {friend.username && friend.displayName && (
        <div className="text-xs text-gray-500">@{friend.username}</div>
       )}
       </div>
      </button>
      ))}
     </div>
     )}
    </div>
    <div className="space-y-2">
     {people.map(person => (
      <div key={person.id} onClick={() => handleOpenPaidModal(person)} className="flex items-center justify-between p-3 bg-white border-2 border-black shadow-brutal-sm cursor-pointer hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100">
       <div className="flex items-center gap-3">
        <PersonAvatar person={person} currentUser={currentUser} userProfile={userProfile} className="w-10 h-10 rounded-full" />
        <span className="text-xl font-bold">{person.name.toUpperCase()}</span>
       </div>
       <div className="flex items-center gap-3">
        {person.paid > 0 && <span className="text-base bg-green-200 text-green-800 px-2 py-1 border-2 border-black font-bold">{formatCurrency(person.paid)}</span>}
        {person.id !== currentUser.id && <button onClick={(e) => { e.stopPropagation(); onRemovePerson(person); }} className="text-gray-600 hover:text-red-500 z-10"><X size={24} /></button>}
       </div>
      </div>
     ))}
    </div>
   </div>
   <div className="p-4 border-t-2 border-dashed border-gray-400">
    <div data-unaccounted-box className={`p-3 mb-3 text-center border-2 border-black shadow-brutal-sm font-bold transition-all ${Math.abs(remainingAmount) > 0.01 ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
     <p className="text-xl">{formatCurrency(remainingAmount)} {Math.abs(remainingAmount) > 0.01 ? 'UNACCOUNTED' : 'ACCOUNTED'}</p>
     <p className="text-base font-medium">PAID: {formatCurrency(totalPaid)} / {formatCurrency(billTotal)}</p>
    </div>
    <button
     onClick={() => {
      if (Math.abs(remainingAmount) > 0.01) {
       const box = document.querySelector('[data-unaccounted-box]');
       if (box) {
        box.classList.add('animate-shake');
        setTimeout(() => box.classList.remove('animate-shake'), 500);
       }
       triggerHaptic('heavy');
       return;
      }
      onContinue();
     }}
     className={`w-full py-3 px-4 flex items-center justify-center gap-2 border-2 border-black text-xl font-bold transition-all duration-100 ${
      Math.abs(remainingAmount) > 0.01
       ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
       : 'bg-[#FF6B35] text-white shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none'
     }`}
    >
     ASSIGN ITEMS <ChevronRight size={24} />
    </button>
   </div>
  </div>
 );
};

const AssignItemsScreen = ({ items, people, assignments, onAssign, onAssignToMe, onSplitEvenly, onCalculate, onBack, onCancel, draggedItemId, setDraggedItemId, onMergeItems, billId, billData, onReScan, onAddItem, onRemoveItem, mergeHistory, onUndo, currentUser, userProfile }) => {
 const [expandedItemId, setExpandedItemId] = useState(null);
 const [isReceiptVisible, setIsReceiptVisible] = useState(false);
 
 const [isAddingItem, setIsAddingItem] = useState(false);
 const [newItemName, setNewItemName] = useState('');
 const [newItemPrice, setNewItemPrice] = useState('');

 // Add safety checks
 if (!items || !Array.isArray(items)) {
  console.error("Items is not an array:", items);
  return <div className="p-4 text-center">Error: No items found</div>;
 }

 if (!people || !Array.isArray(people)) {
  console.error("People is not an array:", people);
  return <div className="p-4 text-center">Error: No people found</div>;
 }

 if (!assignments) {
  console.error("Assignments is undefined");
  assignments = {}; // Provide default empty object
 }

 const unassignedItemsCount = items.filter(item => !assignments[item.id] || assignments[item.id].length === 0).length;

 const handleConfirmAddItem = () => {
  if (newItemName && newItemPrice) {
   onAddItem(newItemName, newItemPrice);
   setNewItemName('');
   setNewItemPrice('');
   setIsAddingItem(false);
  }
 };
 
 const handleRemoveItem = (item) => {
  if(window.confirm(`Are you sure you want to delete "${item.name}"? This cannot be undone.`)){
   onRemoveItem(item);
  }
 };

 const getAssigneeSummary = (item) => {
  const assignees = assignments[item.id] || [];
  const assigneeNames = assignees.map(id => people.find(p => p.id === id)?.name).filter(Boolean).join(', ');
  if (!assigneeNames) return <span className="text-red-600">UNASSIGNED</span>;
  if (assignees.length === people.length) return <span className="text-gray-700">SPLIT EVENLY</span>;
  return <span className="text-gray-700 truncate">TO: {assigneeNames.toUpperCase()}</span>;
 };

 return (
  <div className="flex flex-col h-full safe-area-top">
   <BillProgressStepper currentStep="assign" />
   {isReceiptVisible && (
    <div className="absolute inset-0 bg-black bg-opacity-75 z-20 flex flex-col items-center justify-center p-4">
     <img src={billData.receiptImage} alt="Receipt" className="max-w-full max-h-full object-contain mb-4" />
     <div className="flex gap-4">
      <button onClick={() => {
       setIsReceiptVisible(false);
       onReScan();
      }} className="btn-pill btn-pill-primary font-receipt text-lg">RETAKE</button>
      <button onClick={() => setIsReceiptVisible(false)} className="btn-pill btn-pill-secondary font-receipt text-lg">CLOSE</button>
     </div>
    </div>
   )}
   <div className="relative p-6 border-b-2 border-dashed border-gray-400 text-center flex justify-between items-center">
    <button onClick={onBack} className="text-gray-600 hover:text-black p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 transition-transform"><ArrowLeft size={24} /></button>
    <div className="text-center flex-1 px-4"><h2 className="text-xl">ASSIGN & MERGE</h2><p className="text-sm text-gray-600">{('ontouchstart' in window) ? 'TAP TO COMBINE ITEMS.' : 'DRAG TO COMBINE ITEMS.'}</p></div>
    <div className="flex items-center gap-2">
     <button 
      onClick={() => {
       if (mergeHistory.length > 0) {
        triggerHaptic('medium');
        onUndo(); // Undo one step back
       }
      }} 
      disabled={mergeHistory.length === 0}
      className={`${mergeHistory.length > 0 ? 'text-gray-600 hover:text-black' : 'text-gray-300'} relative`}
      title={mergeHistory.length > 0 ? `Undo last merge (${mergeHistory.length} steps available)` : 'No merges to undo'}
     >
      <RotateCcw size={24} />
      {mergeHistory.length > 0 && (
       <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
        {mergeHistory.length}
       </span>
      )}
     </button>
     <button onClick={() => setIsReceiptVisible(true)} className="text-gray-600 hover:text-black p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 transition-transform"><Receipt size={28} /></button>
     <button onClick={onCancel} className="text-gray-600 hover:text-black p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 transition-transform"><X size={28}/></button>
    </div>
   </div>
   <div className="flex-grow overflow-y-auto p-4 space-y-2">
    {items.map(item => {
     const isExpanded = expandedItemId === item.id;
     const itemAssignees = assignments[item.id] || [];
     const splitCount = itemAssignees.filter(id => people.find(p => p.id === id)).length;
     return (
      <div 
       key={item.id}
       data-item-id={item.id}
       draggable={!('ontouchstart' in window)} // Enable draggable for desktop only
       onDragStart={(e) => {
        // Desktop drag start code (same as original)
        if (!('ontouchstart' in window)) {
         setDraggedItemId(item.id);
         e.dataTransfer.effectAllowed = 'move';
         
         const element = e.currentTarget;
         element.style.transform = 'scale(1.15) rotate(3deg)';
         element.style.zIndex = '100';
         element.style.boxShadow = '0 25px 50px rgba(0,0,0,0.4)';
         element.style.opacity = '0.9';
         element.style.background = 'linear-gradient(45deg, #dbeafe, #bfdbfe)';
         element.style.borderColor = '#3b82f6';
         element.style.borderWidth = '3px';
        }
       }}
       onDragOver={(e) => {
        // Desktop drag over code (same as original)
        if (!('ontouchstart' in window) && draggedItemId && draggedItemId !== item.id) {
         e.preventDefault();
         e.dataTransfer.dropEffect = 'move';
         
         const element = e.currentTarget;
         element.style.background = 'linear-gradient(45deg, #dcfce7, #bbf7d0)';
         element.style.transform = 'scale(1.15) translateY(-8px)';
         element.style.borderColor = '#22c55e';
         element.style.borderWidth = '5px';
         element.style.borderStyle = 'solid';
         element.style.boxShadow = '0 25px 50px rgba(34, 197, 94, 0.6)';
         element.style.transition = 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)';
        }
       }}
       onDragLeave={(e) => {
        // Clear highlight when leaving
        if (!('ontouchstart' in window) && draggedItemId && draggedItemId !== item.id) {
         const element = e.currentTarget;
         element.style.background = '';
         element.style.transform = '';
         element.style.borderColor = '';
         element.style.borderWidth = '';
         element.style.borderStyle = '';
         element.style.boxShadow = '';
        }
       }}
       onDrop={(e) => {
        // Desktop drop
        if (!('ontouchstart' in window) && draggedItemId && draggedItemId !== item.id) {
         e.preventDefault();
         onMergeItems(item.id);
        }
       }}
       onDragEnd={() => {
        // Desktop drag end cleanup
        if (!('ontouchstart' in window)) {
         document.querySelectorAll('[data-item-id]').forEach(el => {
          el.style.transform = '';
          el.style.zIndex = '';
          el.style.boxShadow = '';
          el.style.opacity = '';
          el.style.background = '';
          el.style.borderColor = '';
          el.style.borderWidth = '';
          el.style.borderStyle = '';
          el.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
         });
         setDraggedItemId(null);
        }
       }}
       className="bg-white border-2 border-black shadow-brutal-sm transition-all duration-300 touch-manipulation"
       style={{
        transformOrigin: 'center'
       }}
       onTouchStart={(e) => {
        // Mobile touch handling (same as original)
        const target = e.target;
        const isInteractiveElement = target.tagName === 'BUTTON' || 
               target.tagName === 'IMG' || 
               target.closest('button') ||
               target.classList.contains('interactive');
        
        if (isInteractiveElement) {
         return;
        }
        
        e.preventDefault();
        
        const element = e.currentTarget;
        
        triggerHaptic('light');
        element.style.transform = 'scale(0.98)';
        element.style.transition = 'transform 0.1s ease-out';
        
        element.longPressTimer = setTimeout(() => {
         MobileSelectionHandler.selectItem(item.id);
        }, 500);
        
        element.touchStartTime = Date.now();
       }}
       onTouchEnd={(e) => {
        // Mobile touch end (same as original)
        const target = e.target;
        const isInteractiveElement = target.tagName === 'BUTTON' || 
               target.tagName === 'IMG' || 
               target.closest('button') ||
               target.classList.contains('interactive');
        
        if (isInteractiveElement) {
         return;
        }
        
        const element = e.currentTarget;
        clearTimeout(element.longPressTimer);
        
        element.style.transform = '';
        element.style.transition = 'transform 0.2s ease-out';
        
        const touchDuration = Date.now() - element.touchStartTime;
        
        if (touchDuration < 300) {
         if (MobileSelectionHandler.selectedItemId && 
          MobileSelectionHandler.selectedItemId !== item.id) {
          const merged = MobileSelectionHandler.attemptMerge(item.id, onMergeItems);
          if (!merged) {
           setExpandedItemId(isExpanded ? null : item.id);
          }
         } else {
          setExpandedItemId(isExpanded ? null : item.id);
         }
        }
       }}
       onTouchMove={(e) => {
        const element = e.currentTarget;
        clearTimeout(element.longPressTimer);
        element.style.transform = '';
       }}
       onClick={(e) => {
        if (!('ontouchstart' in window)) {
         setExpandedItemId(isExpanded ? null : item.id);
        }
       }}
      >
       <div className="p-3 cursor-pointer" onClick={() => setExpandedItemId(isExpanded ? null : item.id)}>
        <div className="flex justify-between items-start gap-2">
         <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
           {item.quantity && item.quantity > 1 && (
            <span className="text-base font-bold text-gray-700 flex-shrink-0">{item.quantity}×</span>
           )}
           <p className="text-xl truncate">{item.name.toUpperCase()}</p>
           {items.length > 1 && (
            <div className="flex flex-col gap-1 flex-shrink-0">
             <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
             <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
             <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            </div>
           )}
          </div>
          <div className="text-base mt-1 truncate">{getAssigneeSummary(item)}</div>
         </div>
         <div className="text-right text-xl flex-shrink-0">
          <p className="font-bold">{formatCurrency(item.price)}</p>
          {splitCount > 1 && <p className="text-base text-gray-700">{formatCurrency(item.price / splitCount)} EA</p>}
         </div>
        </div>
       </div>
       {isExpanded && (
        <div className="p-4 border-t-2 border-dashed border-gray-400 bg-gray-100">
         <div className="flex items-center justify-between mb-3">
          <p className="text-base font-bold">ASSIGN TO:</p>
          <button onClick={() => handleRemoveItem(item)} className="p-2 text-red-400 hover:text-red-600 active:scale-95 transition-all duration-100" title="Delete item"><Trash2 size={18}/></button>
         </div>
         <div className="flex items-center gap-2 flex-wrap">
          {people.map(person => <button key={person.id} onClick={() => {
           if (item && item.id && person && person.id) {
            onAssign(item.id, person.id);
           } else {
            console.error("Invalid item or person:", { item, person });
           }
          }} title={person.name} className={`w-10 h-10 rounded-full transition-all duration-200 border-2 border-black interactive ${itemAssignees.includes(person.id) ? 'ring-4 ring-indigo-400' : ''}`}><PersonAvatar person={person} currentUser={currentUser} userProfile={userProfile} className="w-full h-full rounded-full" showBorder={false} /></button>)}
          <div className="flex-grow" />
          <button onClick={() => onAssignToMe(item.id)} className="text-base bg-gray-200 text-black px-3 py-1 border-2 border-black shadow-brutal-sm font-bold hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100">ME</button>
          <button onClick={() => onSplitEvenly(item.id)} className="text-base bg-gray-200 text-black px-3 py-1 border-2 border-black shadow-brutal-sm font-bold hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100">ALL</button>
         </div>
        </div>
       )}
      </div>
     );
    })}
    {isAddingItem ? (
     <div className="p-3 bg-white border-2 border-black shadow-brutal-sm mt-4">
      <div className="flex items-center gap-2">
       <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} placeholder="Item Name" className="flex-grow p-2 rounded-md bg-white border-2 border-black outline-none" />
       <input type="number" value={newItemPrice} onChange={e => setNewItemPrice(e.target.value)} placeholder="Price" className="w-24 p-2 rounded-md bg-white border-2 border-black outline-none" />
      </div>
      <div className="flex gap-2 mt-2">
       <button onClick={() => setIsAddingItem(false)} className="w-full btn-pill btn-pill-secondary btn-pill-sm">CANCEL</button>
       <button onClick={handleConfirmAddItem} className="w-full btn-pill btn-pill-primary btn-pill-sm">ADD</button>
      </div>
     </div>
    ) : (
     <button onClick={() => setIsAddingItem(true)} className="w-full mt-2 btn-pill btn-pill-ghost">
      <PlusCircle size={20} /> ADD ITEM MANUALLY
     </button>
    )}
   </div>
   <div className="p-4 border-t-2 border-dashed border-gray-400"><button onClick={onCalculate} disabled={unassignedItemsCount > 0} className="w-full btn-pill btn-pill-success btn-pill-xl disabled:bg-gray-400 text-xl">{unassignedItemsCount > 0 ? `${unassignedItemsCount} UNASSIGNED` : 'CALCULATE TOTALS'}{unassignedItemsCount === 0 && <CheckCircle size={24} />}</button></div>
  </div>
 );
};

// Toast notification component
const Toast = ({ message, isVisible, onHide }) => {
 useEffect(() => {
 if (isVisible) {
  const timer = setTimeout(() => {
  onHide();
  }, 2500);
  return () => clearTimeout(timer);
 }
 }, [isVisible, onHide]);

 if (!isVisible) return null;

 return (
 <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
  <div className="bg-green-600 text-white px-4 py-2 shadow-brutal flex items-center gap-2 border-2 border-black">
  <Check size={16} />
  <span className="font-medium">{message}</span>
  </div>
 </div>
 );
};

const FinalSummaryScreen = ({ results, onStartNew, onEdit, currentUser, billData }) => {
 const [showVenmoOptions, setShowVenmoOptions] = useState(false);
 const [showRatings, setShowRatings] = useState(false);
 const [showExportOptions, setShowExportOptions] = useState(false);
 const [toast, setToast] = useState({ message: '', isVisible: false });
 const [experience, setExperience] = useState({
  venueRating: null,
  venueNotes: '',
  overallRating: null,
  wouldReturn: null,
  dishRatings: {},
  tags: []
 });

 // Auto-populate Venmo username from profile for the current user
 useEffect(() => {
  const userId = currentUser?.uid || currentUser?.id;
  if (userId && billData) {
  const autoPopulate = async () => {
   try {
   const userProfile = await UserProfile.getProfile(userId, db);
   const currentUserInBill = billData.people.find(person => person.id === currentUser.id);

   if (userProfile?.venmoUsername && currentUserInBill) {
    const existingVenmo = billData?.venmoUsernames || {};

    // Auto-populate if not already set
    if (!existingVenmo[currentUserInBill.name]) {
    console.log('Auto-populating Venmo username for:', currentUserInBill.name, 'with:', userProfile.venmoUsername);
    updateVenmoUsername(currentUserInBill.name, userProfile.venmoUsername);
    }
   }
   } catch (err) {
   console.log('Could not auto-populate Venmo:', err);
   }
  };
  autoPopulate();
  }
 }, [billData, currentUser]);

 // Load existing experience data when component mounts or when ratings section is opened
 useEffect(() => {
  if (showRatings && billData?.id) {
  const existingBill = BillHistory.getBill(billData.id);
  if (existingBill?.experience) {
   setExperience(existingBill.experience);
  }
  }
 }, [showRatings, billData?.id]);

 // Show toast notification
 const showToast = (message) => {
  setToast({ message, isVisible: true });
 };

 const hideToast = () => {
  setToast(prev => ({ ...prev, isVisible: false }));
 };

 // Save experience data
 const saveExperience = async () => {
  BillHistory.updateExperience(billData.id, experience);
  // Also sync to shared activities collection for the social feed
  if (billData?.id) {
  try {
   await updateDoc(doc(db, `artifacts/${appId}/public/data/activities`, billData.id), {
   rating: experience.venueRating || null,
   review: experience.venueNotes || null,
   wouldReturn: experience.wouldReturn,
   tags: experience.tags || [],
   });
  } catch (err) {
   console.warn('Could not sync experience to activity:', err.message);
  }
  }
  setShowRatings(false);
  showToast("Notes saved!");
 };

 // Common tags for quick selection
 const availableTags = [
  'Date Night', 'Family Friendly', 'Romantic', 'Loud', 'Quiet', 'Great Service',
  'Slow Service', 'Good Value', 'Expensive', 'Casual', 'Fancy', 'Good for Groups',
  'Outdoor Seating', 'Happy Hour', 'Brunch', 'Takeout Friendly'
 ];

 // Star rating component
 const StarRating = ({ rating, onRate, size = 24 }) => (
  <div className="flex gap-1">
  {[1,2,3,4,5].map(star => {
   const isActive = star <= (rating || 0);
   return (
   <button
    key={star}
    onClick={() => {
    // If clicking the current rating, clear it. Otherwise set new rating.
    onRate(rating === star ? null : star);
    }}
    onTouchEnd={(e) => {
    e.preventDefault();
    // If touching the current rating, clear it. Otherwise set new rating.
    onRate(rating === star ? null : star);
    }}
    className={`p-1 transition-colors ${
    isActive ? 'text-yellow-500' : 'text-gray-300'
    } hover:text-yellow-400 active:text-yellow-600`}
   >
    <Star 
    size={size} 
    fill={isActive ? '#eab308' : 'none'} 
    stroke={isActive ? '#eab308' : 'currentColor'}
    className="transition-all duration-200"
    />
   </button>
   );
  })}
  </div>
 );

 if (!results) return <div className="p-4 flex items-center justify-center h-full">CALCULATING...</div>;
 if (results.error) return <div className="p-4 flex flex-col items-center justify-center h-full text-center"><AlertTriangle className="text-red-500 h-12 w-12 mb-4" /><h3 className="text-2xl">ERROR</h3><p className="text-red-600">{results.error}</p><button onClick={onStartNew} className="mt-4 btn-pill btn-pill-primary">START OVER</button></div>;

 // Find current user's data to check if they paid anything
 const currentUserData = billData?.people.find(person => person.id === currentUser?.id);
 const currentUserPaid = currentUserData?.paid > 0.01;

 // Get Venmo usernames from Firebase (they're stored in billData.venmoUsernames)
 const venmoUsernames = billData?.venmoUsernames || {};

 const handleVenmoPayment = (transaction) => {
  const recipientUsername = venmoUsernames[transaction.to];
  if (!recipientUsername) {
   alert(`${transaction.to} needs to add their Venmo username first`);
   return;
  }
  
  // Clean the username - remove @ symbol if present
  const cleanUsername = recipientUsername.replace('@', '');
  const amount = transaction.amount.toFixed(2);
  // FIXED: Changed the note to avoid triggering Venmo's bill splitting feature
  const note = encodeURIComponent(`Payment via SplitApp`);
  
  // Construct the Venmo URL properly
  const venmoAppURL = `venmo://paycharge?txn=pay&recipients=${cleanUsername}&amount=${amount}&note=${note}`;
  
  // For mobile - try to open the app directly
  if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
   // On mobile, use window.location to open the app
   window.location.href = venmoAppURL;
  } else {
   // For desktop - create a temporary link and click it
   const tempLink = document.createElement('a');
   tempLink.href = venmoAppURL;
   tempLink.style.display = 'none';
   document.body.appendChild(tempLink);
   tempLink.click();
   document.body.removeChild(tempLink);
   
   // Fallback message for desktop users
   setTimeout(() => {
    if (window.confirm('Venmo app not found. Would you like to open Venmo in your browser?')) {
     const venmoWebURL = `https://venmo.com/paycharge?txn=pay&recipients=${cleanUsername}&amount=${amount}&note=${note}`;
     window.open(venmoWebURL, '_blank');
    }
   }, 1000);
  }
  
  triggerHaptic('medium');
 };

 const updateVenmoUsername = async (personName, username) => {
  try {
   // Update the Venmo usernames in Firebase so all devices see the change
   const cleanUsername = username.replace('@', ''); // Remove @ if user includes it
   const updatedUsernames = {
    ...venmoUsernames,
    [personName]: cleanUsername
   };

   // Update Firebase document
   await updateDoc(doc(db, `artifacts/${appId}/public/data/bills`, billData.id), {
    venmoUsernames: updatedUsernames
   });

   // Also save to current user's profile if this is the current user
   const currentUserName = currentUserData?.name;
   if (personName === currentUserName && cleanUsername) {
    const userId = currentUser?.uid || currentUser?.id;
    if (userId) {
    UserProfile.saveProfile(userId, { venmoUsername: cleanUsername }, db);
    console.log('Venmo also saved to profile for user:', userId);
    }
   }
  } catch (error) {
   console.error("Error updating Venmo username:", error);
  }
 };

 const summaryText = `--- BILL SUMMARY ---
TOTAL: ${formatCurrency(results.grandTotal)}

--- WHO OWES WHO ---
${results.transactions.length > 0 
 ? results.transactions.map(t => `${t.from.toUpperCase()} owes ${t.to.toUpperCase()} ${formatCurrency(t.amount)}`).join('\n')
 : 'EVERYONE IS SETTLED UP!'
}

--- BREAKDOWN ---

${results.breakdown.map(person => 
 `${person.name.toUpperCase()}:
 - Items: ${formatCurrency(person.itemTotal)}
 - Tax/Tip: ${formatCurrency(person.taxAndTipShare)}
 - TOTAL SHARE: ${formatCurrency(person.finalShare)}
 - PAID: ${formatCurrency(person.paid)}
 - OWES/GETS: ${formatCurrency(Math.abs(person.balance))}`
).join('\n\n')}`;

 return (
  <>
   <div className="flex flex-col h-full bg-[#fdfaef] safe-area-top">
    <div className="relative p-4 text-center border-b-2 border-dashed border-gray-400">
    <button
     onClick={async () => {
      triggerHaptic('medium');
      const baseUrl = window.location.hostname === 'localhost'
       ? 'https://avocadoisextrabananasplit.web.app'
       : window.location.origin;
      const shareableLink = `${baseUrl}?join=${billData?.id}`;
      const venue = billData?.venueName || 'restaurant';
      if (navigator.share) {
       try {
        await navigator.share({
         title: `Split the ${venue} bill`,
         text: `Split the ${venue} bill with me! 🧾`,
         url: shareableLink,
        });
        return;
       } catch (e) { /* user cancelled */ }
      }
      if (navigator.clipboard && window.isSecureContext) {
       await navigator.clipboard.writeText(shareableLink);
       showToast("Link copied!");
      } else {
       const message = encodeURIComponent(`Split the ${venue} bill with me! ${shareableLink} 🧾`);
       window.location.href = `sms:&body=${message}`;
      }
     }}
     className="absolute top-4 left-4 p-2 bg-white border-2 border-black shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
     title="Share Bill Link"
    >
     <Share2 size={24} className="text-black" />
    </button>
    <button
     onClick={onStartNew}
     className="absolute top-4 right-4 p-2 bg-white border-2 border-black shadow-brutal-sm hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-100"
     title="Start New Bill"
    >
     <Home size={24} className="text-black" />
    </button>
    {results.isCombinedSettlement ? (
     <>
      <h2 className="text-3xl text-black font-clash">COMBINED TOTAL</h2>
      <p className="text-base text-gray-600">SETTLED ACROSS MULTIPLE VENUES</p>
      <div className="text-sm text-gray-600 mt-1">
       {billData.originalBills?.map(bill => bill.venueName).join(' + ')}
      </div>
      <p className="text-4xl mt-2">{formatCurrency(results.grandTotal)}</p>
     </>
    ) : (
     <>
      {results.transactions && results.transactions.length > 0 ? (
       <>
        <h2 className="text-3xl text-black font-clash">WHO OWES WHO</h2>
        <p className="text-4xl mt-4">{formatCurrency(results.grandTotal)}</p>
       </>
      ) : (
       <>
        <h2 className="text-3xl text-black font-clash">ALL SETTLED!</h2>
        <p className="text-base text-gray-600">EVERYONE IS BALANCED.</p>
        <p className="text-4xl mt-2">{formatCurrency(results.grandTotal)}</p>
       </>
      )}
     </>
    )}
   </div>
   <div className="flex-grow overflow-y-auto p-4 space-y-3">
    <h3 className="text-2xl text-black">SETTLE UP</h3>

    {/* Show Venmo input for current user if they paid but don't have Venmo set */}
    {results.transactions.length > 0 && currentUserPaid && !venmoUsernames[currentUserData?.name] && (
     <div className="bg-blue-50 border-2 border-blue-200 p-3">
      <p className="text-sm text-blue-800 mb-2">Add your Venmo so people can pay you back:</p>
      <div className="flex items-center gap-2">
       <div className="flex items-center flex-grow bg-white border-2 border-blue-300 px-3 py-2">
        <span className="text-gray-400 mr-1">@</span>
        <input
         type="text"
         placeholder="your-venmo-username"
         onChange={(e) => {
         const val = e.target.value.replace('@', '').trim();
         if (val) updateVenmoUsername(currentUserData?.name, val);
         }}
         onBlur={(e) => {
         const val = e.target.value.replace('@', '').trim();
         if (val) updateVenmoUsername(currentUserData?.name, val);
         }}
         className="flex-grow bg-transparent outline-none text-sm"
        />
       </div>
      </div>
     </div>
    )}

    {/* Show confirmation if Venmo is set */}
    {results.transactions.length > 0 && currentUserPaid && venmoUsernames[currentUserData?.name] && (
     <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border-2 border-green-600 px-3 py-2">
      <span>✓ Your Venmo: @{venmoUsernames[currentUserData?.name]}</span>
      <button
      onClick={() => updateVenmoUsername(currentUserData?.name, '')}
      className="ml-auto text-xs text-gray-400 hover:text-gray-600"
      >
      edit
      </button>
     </div>
    )}


    {results.transactions.length > 0 ? results.transactions.map((t, index) => (
     <div key={index} className="p-4 bg-white border-2 border-black shadow-brutal-sm">
      <div className="flex items-center justify-between mb-2">
       <div className="flex items-center gap-2 text-xl font-bold">
        <span>{t.from.toUpperCase()}</span>
        <ArrowRight size={20} className="text-gray-600"/>
        <span>{t.to.toUpperCase()}</span>
       </div>
       <span className="text-2xl font-bold">{formatCurrency(t.amount)}</span>
      </div>

      {/* Show Venmo pay button if recipient has Venmo set */}
      {venmoUsernames[t.to] ? (
       <button
        onClick={() => handleVenmoPayment(t)}
        className="w-full mt-2 bg-[#008CFF] hover:bg-[#0077E6] text-white py-2 px-3 border-2 border-black text-base flex items-center justify-center gap-2 active:scale-95 transition-transform"
       >
        PAY @{venmoUsernames[t.to].toUpperCase()} ON VENMO
       </button>
      ) : (
       <p className="text-xs text-gray-400 mt-2 text-center">
        {t.to} hasn't added their Venmo yet
       </p>
      )}
     </div>
    )) : (
     <div className="text-center p-4 bg-white border-2 border-black shadow-brutal-sm"><h3 className="text-xl text-gray-800 font-bold">EVERYTHING IS BALANCED!</h3><p className="text-base text-gray-600">NO PAYMENTS ARE NEEDED.</p></div>
    )}

    <div className="pt-4 mt-4 border-t-2 border-dashed border-gray-400">
     <h3 className="text-2xl text-black mb-2">DETAILED BREAKDOWN</h3>
     {results.breakdown.map((person) => (
      <details key={person.id} open className="bg-white border-2 border-black shadow-brutal-sm mb-2">
       <summary className="p-3 cursor-pointer flex justify-between items-center text-xl font-bold">
        <span>{person.name.toUpperCase()}</span>
        <span className="text-black">
         {formatCurrency(person.finalShare)}
        </span>
       </summary>
       <div className="p-3 border-t-2 border-dashed border-gray-400 bg-gray-100 text-lg space-y-1">
        {/* Combined Settlement Venue Breakdown */}
        {results.isCombinedSettlement && person.venueBreakdown ? (
         person.venueBreakdown.map((venueData, index) => (
          <div key={`${person.name}-venue-${index}`} className="mb-3 pb-2 border-b-2 border-black">
           <div className="font-bold text-gray-800 mb-1">{venueData.venue.toUpperCase()}</div>
           <div className="flex justify-between pl-2">
            <span className="text-gray-700">Share</span>
            <span>{formatCurrency(venueData.amount)}</span>
           </div>
           <div className="flex justify-between pl-2">
            <span className="text-gray-700">Paid</span>
            <span>{formatCurrency(venueData.paid)}</span>
           </div>
           <div className="flex justify-between pl-2 text-sm">
            <span className="text-gray-600">
             {venueData.balance >= 0 ? 'Gets back' : 'Owes'}
            </span>
            <span className={venueData.balance >= 0 ? 'text-green-600' : 'text-red-600'}>
             {formatCurrency(Math.abs(venueData.balance))}
            </span>
           </div>
          </div>
         ))
        ) : (
         /* Regular Bill Items */
         <>
          {person.items && person.items.length > 0 && person.items.map((item, index) => (
           <div key={index} className="flex justify-between">
            <span className="text-gray-700">{item.name.toUpperCase()}</span>
            <span>{formatCurrency(item.price)}</span>
           </div>
          ))}
          <div className="flex justify-between">
           <span className="text-gray-700">TAX & TIP SHARE</span>
           <span>{formatCurrency(person.taxAndTipShare || 0)}</span>
          </div>
         </>
        )}
        <div className="flex justify-between border-t-2 border-dashed border-gray-400 mt-2 pt-2">
         <span>TOTAL SHARE</span>
         <span>{formatCurrency(person.finalShare)}</span>
        </div>
        <div className="flex justify-between">
         <span>AMOUNT PAID</span>
         <span>{formatCurrency(person.paid)}</span>
        </div>
        <div className={`flex justify-between text-xl mt-2 pt-2 border-t-2 border-dashed border-gray-400 ${person.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
         <span>FINAL BALANCE</span>
         <span>{formatCurrency(person.balance)}</span>
        </div>
       </div>
      </details>
     ))}
    </div>

    {/* Notes and Experience Section */}
    <div className="pt-4 mt-4 border-t-2 border-dashed border-gray-400">
     <div className="mb-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
       <div>
        <h3 className="text-2xl text-black flex items-center gap-2">
         C'MON, SPILL THE TEA!
         <Coffee size={28} className="text-amber-600" />
        </h3>
        <p className="text-sm text-gray-600 mt-1">Rate the vibes, dishes, and add notes for next time</p>
       </div>
       <button 
        onClick={() => setShowRatings(!showRatings)}
        className={`px-4 py-2 border-2 border-black shadow-brutal-sm flex items-center justify-center gap-2 transition-all duration-100 w-full sm:w-auto hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${
         showRatings ? 'bg-blue-600 text-white' : 'bg-white text-black'
        }`}
       >
        <MessageSquare size={20} />
        {showRatings ? 'DONE' : 'ADD NOTES'}
       </button>
      </div>
     </div>
     
     {showRatings && (
      <div className="bg-white border-2 border-black shadow-brutal-sm p-4 space-y-4">
       {/* Venue Rating */}
       <div>
        <label className="block text-lg font-bold mb-2">
         HOW WAS {billData.venueName?.toUpperCase() || 'THIS PLACE'}?
        </label>
        <StarRating 
         rating={experience.venueRating} 
         onRate={(rating) => setExperience(prev => ({...prev, venueRating: rating}))}
         size={32}
        />
       </div>

       {/* Would Return */}
       <div>
        <label className="block text-lg font-bold mb-2">WOULD YOU RETURN?</label>
        <div className="flex gap-2">
         <button
          onClick={() => setExperience(prev => ({...prev, wouldReturn: true}))}
          className={`px-4 py-2 border-2 border-black flex items-center gap-2 font-bold transition-all duration-100 ${
           experience.wouldReturn === true ? 'bg-green-600 text-white shadow-brutal-sm' : 'bg-white text-black hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[2px] active:translate-y-[2px] active:shadow-none'
          }`}
         >
          <ThumbsUp size={20} /> YES
         </button>
         <button
          onClick={() => setExperience(prev => ({...prev, wouldReturn: false}))}
          className={`px-4 py-2 border-2 border-black flex items-center gap-2 font-bold transition-all duration-100 ${
           experience.wouldReturn === false ? 'bg-red-600 text-white shadow-brutal-sm' : 'bg-white text-black hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[2px] active:translate-y-[2px] active:shadow-none'
          }`}
         >
          <ThumbsDown size={20} /> NO
         </button>
        </div>
       </div>

       {/* Notes */}
       <div>
        <label className="block text-lg font-bold mb-2">NOTES</label>
        <textarea
         value={experience.venueNotes}
         onChange={(e) => setExperience(prev => ({...prev, venueNotes: e.target.value}))}
         placeholder="Great service, loud music, perfect for date night..."
         className="w-full p-3 border-2 border-black shadow-brutal-sm resize-none h-24 focus:shadow-brutal focus:outline-none transition-shadow"
        />
       </div>

       {/* Tags */}
       <div>
        <label className="block text-lg font-bold mb-2">TAGS</label>
        <div className="flex flex-wrap gap-2">
         {availableTags.map(tag => (
          <button
           key={tag}
           onClick={() => {
            setExperience(prev => ({
             ...prev,
             tags: prev.tags.includes(tag) 
              ? prev.tags.filter(t => t !== tag)
              : [...prev.tags, tag]
            }));
           }}
           className={`px-3 py-1 text-sm font-bold border-2 border-black transition-all duration-100 ${
            experience.tags.includes(tag)
             ? 'bg-black text-white shadow-brutal-sm'
             : 'bg-white text-black hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[1px] active:translate-y-[1px] active:shadow-none'
           }`}
          >
           {tag}
          </button>
         ))}
        </div>
       </div>

       {/* Dish Ratings */}
       <div>
        <label className="block text-lg font-bold mb-2">RATE THE DISHES</label>
        <div className="space-y-3">
         {billData.items.map(item => (
          <div key={item.id} className="bg-white p-3 border-2 border-black shadow-brutal-sm">
           <div className="flex justify-between items-start mb-2">
            <div>
             <p className="font-medium">{item.name.toUpperCase()}</p>
             <p className="text-sm text-gray-600">{formatCurrency(item.price)}</p>
            </div>
            <button
             onClick={() => {
              const currentDish = experience.dishRatings[item.id] || {};
              setExperience(prev => ({
               ...prev,
               dishRatings: {
                ...prev.dishRatings,
                [item.id]: {
                 ...currentDish,
                 recommended: !currentDish.recommended
                }
               }
              }));
             }}
             className={`px-2 py-1 text-xs font-bold border-2 border-black flex items-center gap-1 transition-all duration-100 ${
              experience.dishRatings[item.id]?.recommended
               ? 'bg-green-600 text-white shadow-brutal-sm'
               : 'bg-white text-black hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal-sm active:translate-x-[1px] active:translate-y-[1px] active:shadow-none'
             }`}
            >
             <ThumbsUp size={14} /> 
             {experience.dishRatings[item.id]?.recommended ? 'RECOMMENDED' : 'RECOMMEND?'}
            </button>
           </div>
           
           <div className="mb-2">
            <StarRating 
             rating={experience.dishRatings[item.id]?.rating} 
             onRate={(rating) => {
              setExperience(prev => ({
               ...prev,
               dishRatings: {
                ...prev.dishRatings,
                [item.id]: {
                 ...prev.dishRatings[item.id],
                 rating
                }
               }
              }));
             }}
             size={20}
            />
           </div>
           
           <textarea
            value={experience.dishRatings[item.id]?.notes || ''}
            onChange={(e) => {
             setExperience(prev => ({
              ...prev,
              dishRatings: {
               ...prev.dishRatings,
               [item.id]: {
                ...prev.dishRatings[item.id],
                notes: e.target.value
               }
              }
             }));
            }}
            placeholder="Notes about this dish..."
            className="w-full p-2 text-sm border-2 border-black shadow-brutal-sm resize-none h-16 focus:shadow-brutal focus:outline-none transition-shadow"
           />
          </div>
         ))}
        </div>
       </div>

       {/* Save Button */}
       <div className="pt-4 border-t-2 border-dashed border-gray-400">
        <button
         onClick={saveExperience}
         onTouchEnd={(e) => {
          e.preventDefault();
          saveExperience();
         }}
         className="w-full bg-green-600 text-white py-3 px-4 flex items-center justify-center gap-2 border-2 border-black shadow-brutal-sm text-xl font-bold transition-all duration-100 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
        >
         <MessageSquare size={24} /> SAVE EXPERIENCE
        </button>
       </div>
      </div>
     )}
    </div>
   </div>
   
   {/* Export options */}
   <div className="p-4 border-t-2 border-dashed border-gray-400 bg-gray-50">
    <button 
     onClick={() => setShowExportOptions(!showExportOptions)}
     className="w-full bg-yellow-400 text-black py-3 px-4 border-2 border-black shadow-brutal-sm text-xl font-bold flex items-center justify-center gap-2 transition-all duration-100 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
    >
     <span className="font-bold">I'VE GOT RECEIPTS...</span>
     <ChevronRight 
      size={24} 
      className={`transform transition-transform ${showExportOptions ? 'rotate-90' : ''}`}
     />
    </button>
    
    {showExportOptions && (
     <div className="p-4 pt-3 space-y-3 bg-gray-100">
      <button 
       onClick={() => { 
        triggerHaptic('light'); 
        const textarea = document.createElement('textarea'); 
        textarea.value = summaryText; 
        document.body.appendChild(textarea); 
        textarea.select(); 
        document.execCommand('copy'); 
        document.body.removeChild(textarea); 
        showToast("Text copied!");
       }} 
       className="w-full bg-gray-700 text-white py-3 px-4 flex items-center justify-center gap-2 border-2 border-black shadow-brutal-sm text-xl font-bold transition-all duration-100 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
      >
       <Copy size={24} /> COPY TEXT SUMMARY
      </button>
      <button 
       onClick={() => { 
        triggerHaptic('light'); 
        generateImageSummary(results); 
        showToast("Image copied!");
       }} 
       className="w-full bg-purple-600 text-white py-3 px-4 flex items-center justify-center gap-2 border-2 border-black shadow-brutal-sm text-xl font-bold transition-all duration-100 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
      >
       <Receipt size={24} /> COPY IMAGE SUMMARY
      </button>
     </div>
    )}
   </div>
   
   {/* Main Actions */}
   <div className="p-4 border-t-2 border-dashed border-gray-400 bg-gray-100">
    <button 
     onClick={() => { triggerHaptic('light'); onEdit(); }} 
     className="w-full bg-gray-300 text-black py-3 px-4 flex items-center justify-center gap-2 border-2 border-black shadow-brutal-sm text-xl font-bold transition-all duration-100 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-brutal active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
    >
     EDIT SPLIT
    </button>
   </div>
  </div>
  
  <Toast 
   message={toast.message} 
   isVisible={toast.isVisible} 
   onHide={hideToast} 
  />
 </>
 );
};
